import { z } from 'zod'
import { ISODate, ISOTimestamp, MoneyOut, Uuid, blankAsAbsent } from './primitives.ts'

/**
 * ══ HÌNH DẠNG DỮ LIỆU RA — nguồn sự thật DUY NHẤT cho cả hai đầu (KIEN-TRUC §7.1, §12.3) ═══════
 *
 * Trước đợt N1, mỗi hình dạng dưới đây tồn tại HAI lần: một bản `interface` trong `packages/
 * modules/<miền>` (máy chủ trả về) và một bản `interface ...Row` CHÉP TAY trong `apps/web` (màn
 * hình đọc vào). Không có gì buộc hai bản khớp nhau. Thêm một cột ở máy chủ mà quên sửa bên web
 * thì `tsc` xanh, màn hình lặng lẽ không hiển thị cột mới; ĐỔI TÊN một trường thì `tsc` cũng xanh
 * và màn hình hiện `undefined`.
 *
 * Nay: lược đồ ở đây là bản DUY NHẤT. `apps/web` lấy kiểu bằng `z.infer`, còn máy chủ có cổng
 * `dto-parity.test.ts` phân giải phản hồi THẬT qua chính lược đồ này — nên lệch là ĐỎ, không phải
 * lặng lẽ.
 *
 * ⚠ Đây là lược đồ ĐẦU RA: tiền dùng `MoneyOut` (chỉ khẳng định hình dạng, không chuẩn hoá lại —
 *   dữ liệu đã chuẩn từ CSDL) chứ không phải `MoneyIn`.
 */

/**
 * ⚠ Lược đồ này liệt kê ĐÚNG những gì máy chủ ĐANG trả về, kể cả trường mà màn hình chưa dùng —
 *   cổng `dto-parity.test.ts` chạy `.strict()` nên thừa cũng đỏ như thiếu. Bản đầu chỉ chép lại
 *   `interface CustomerRow` cũ của web và LẬP TỨC đỏ với 15 trường không khai: `entityId`,
 *   `docEmail`, `lastVatRate`, `updatedAt`… Đó chính là bằng chứng cho việc hai bản chép tay đã
 *   trôi khỏi nhau từ lúc nào không ai biết.
 *
 * ⚠ Lược đồ này KHÔNG lọc bớt đầu ra. Cắt trường (VD `entityId` — cột nội bộ) là ĐỔI HÀNH VI của
 *   một API đang chạy, thuộc §11.3 và phải làm có chủ đích, không lồng vào một lần dọn hợp đồng.
 */
export const CustomerDto = z.object({
  id: Uuid,
  entityId: Uuid,
  code: z.string(),
  name: z.string(),
  invoiceName: z.string().nullable(),
  tax: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  docEmail: z.string().nullable(),
  repPosition: z.string().nullable(),
  contactName: z.string().nullable(),
  contactPerson: z.string().nullable(),
  bankAccount: z.string().nullable(),
  bankName: z.string().nullable(),
  acctName: z.string().nullable(),
  acctPhone: z.string().nullable(),
  acctEmail: z.string().nullable(),
  lastVatRate: z.number().nullable(),
  creditDays: z.number(),
  creditAmount: MoneyOut.nullable(),
  salesNote: z.string().nullable(),
  customerNote: z.string().nullable(),
  creditReviewNote: z.string().nullable(),
  creditAdjBy: z.string().nullable(),
  creditAdjNote: z.string().nullable(),
  creditAdjAt: ISOTimestamp.nullable(),
  fromVendorId: Uuid.nullable(),
  salesMainId: Uuid.nullable(),
  salesSubId: Uuid.nullable(),
  splitMain: z.number(),
  splitSub: z.number(),
  splitStatus: z.enum(['pending', 'approved', 'rejected']).nullable(),
  splitApproverId: Uuid.nullable(),
  splitBy: z.string().nullable(),
  splitAt: ISOTimestamp.nullable(),
  splitNote: z.string().nullable(),
  salesEffectiveFrom: ISODate.nullable(),
  splitPendingFrom: ISODate.nullable(),
  splitHistory: z.array(z.object({
    at: z.string(), byName: z.string(), label: z.string(),
  })).nullable(),
  salesSplitPeriods: z.array(z.object({
    effectiveFrom: ISODate,
    salesMainName: z.string().nullable(),
    salesSubName: z.string().nullable(),
    splitMain: z.number(),
    splitSub: z.number(),
    by: z.string(),
  })).nullable(),
  salesMainName: z.string().nullable(),
  salesSubName: z.string().nullable(),
  isSupplier: z.boolean(),
  active: z.boolean(),
  version: z.number(),
  createdAt: ISOTimestamp,
  updatedAt: ISOTimestamp,
})

/** Kết quả tra MST — xem `TaxLookupQuery` về việc vì sao "không tìm thấy" KHÔNG phải lỗi HTTP. */
export const TaxLookupDto = z.object({
  found: z.boolean(),
  name: z.string(),
  address: z.string(),
})

export const VendorDto = z.object({
  id: Uuid,
  entityId: Uuid,
  code: z.string().nullable(),
  name: z.string(),
  tax: z.string().nullable(),
  paymentTermDays: z.number().nullable(),
  phone: z.string().nullable(),
  note: z.string().nullable(),
  address: z.string().nullable(),
  email: z.string().nullable(),
  docEmail: z.string().nullable(),
  contactName: z.string().nullable(),
  contactPerson: z.string().nullable(),
  repPosition: z.string().nullable(),
  bankAccount: z.string().nullable(),
  bankName: z.string().nullable(),
  bankHolder: z.string().nullable(),
  acctName: z.string().nullable(),
  acctPhone: z.string().nullable(),
  acctEmail: z.string().nullable(),
  fromCustomerId: Uuid.nullable(),
  isCustomer: z.boolean(),
  isFuelVendor: z.boolean(),
  portalEnabled: z.boolean(),
  portalEmail: z.string().nullable(),
  active: z.boolean(),
  version: z.number(),
  createdAt: ISOTimestamp,
  updatedAt: ISOTimestamp,
})

/**
 * ══ BA CỘT GIỮA CỦA MÀN DANH MỤC — tóm tắt hợp đồng + công nợ của MỘT đối tác ═════════════════
 *
 * Ba cột đó là ba câu hỏi khác nhau, và người giữ danh mục hỏi cả ba mỗi ngày:
 *   · *Hợp đồng* — đã có giấy chưa, bản gốc về kế toán chưa (căn cứ CHI HOA HỒNG cho sale).
 *   · *Hiệu lực HĐ đến* — ngày XA NHẤT trong MỌI hợp đồng, để biết ai sắp phải gia hạn.
 *   · *Tín dụng & công nợ* — hạn mức, số ngày nợ, và DƯ NỢ THẬT đang treo.
 *
 * Trước đây màn tự trả lời bằng cách tải CẢ bảng hợp đồng và CẢ sổ công nợ về trình duyệt rồi gom
 * tay. Nay máy chủ trả lời sẵn cho đúng những dòng đang hiện trên trang.
 *
 * ⚠ `owedTotal = null` nghĩa là NGƯỜI GỌI KHÔNG ĐƯỢC XEM SỔ, không phải "không nợ đồng nào" (§4:
 *   dữ liệu không có quyền xem thì KHÔNG được tuần tự hoá, và hiện 0 cho họ là một con số SAI chứ
 *   không phải một ô rỗng). Màn giấu hẳn dòng dư nợ khi gặp `null`.
 * ⚠ KHÔNG trả về `kind` của huy hiệu "đã nhận bản gốc": máy chủ trả SỐ ĐẾM, màn suy ra huy hiệu
 *   bằng `handoverState()` — luật đó đã có đúng một chỗ và có ca kiểm riêng.
 */
export const PartnerBoardFactsDto = z.object({
  /** Số hợp đồng của tờ có hiệu lực XA NHẤT. `null` = chưa có hợp đồng, hoặc tờ đó chưa có số. */
  contractNo: z.string().nullable(),
  /** Ngày hiệu lực XA NHẤT, ĐÃ tính kỳ gia hạn. `null` = chưa tờ nào có ngày. */
  contractEnd: ISODate.nullable(),
  contractCount: z.number().int(),
  contractValidCount: z.number().int(),
  contractReceivedCount: z.number().int(),
  /** Có tờ CÒN hiệu lực bị gắn cờ "chờ bổ sung" — nhãn bàn giao "Chờ bổ sung hợp đồng" (bản cũ `azHandoverState`). */
  contractSupplement: z.boolean(),
  /** Tờ hiệu lực XA NHẤT đã từng nhận bản gốc — khi MỌI tờ hết hạn, nhãn là "Chờ bổ sung HĐ (kỳ trước đã nhận)". */
  contractLatestReceived: z.boolean(),
  owedTotal: z.number().nullable(),
  /** Ngày đơn SỚM NHẤT (bỏ đơn huỷ) — chỉ phía khách; NCC luôn `null`. */
  firstOrderDate: ISODate.nullable(),
})

/** Một dòng của bảng Danh mục ▸ Khách hàng = hồ sơ khách + ba cột giữa. */
export const CatalogCustomerDto = CustomerDto.merge(PartnerBoardFactsDto)
/** Một dòng của bảng Danh mục ▸ Nhà cung cấp — cùng ba cột giữa, khác CHIỀU công nợ (phải trả). */
export const CatalogVendorDto = VendorDto.merge(PartnerBoardFactsDto)

/**
 * Dải KPI + con số trên huy hiệu đếm của màn Danh mục.
 *
 * ⚠ Đếm trên tập ĐANG LỌC, không phải trên cả bảng — bấm ô KPI rồi gõ tìm kiếm thì con số phải đi
 *   theo, nếu không người dùng thấy "12" mà bảng hiện 3 dòng.
 * ⚠ `debtReadable = false` ⇒ ô "Quá hạn công nợ" ẨN HẲN. Hiện số 0 cho người không được xem sổ là
 *   một con số sai (§4), và `overdue` khi đó luôn là 0 chứ không phải số thật bị che.
 */
export const PartnerBoardSummaryDto = z.object({
  /** SỐ DÒNG của tập đang lọc — tên khớp `PagerState.totalRows`, và không đụng luật lint tiền. */
  totalRows: z.number().int(),
  noContract: z.number().int(),
  pending: z.number().int(),
  expiring: z.number().int(),
  overdue: z.number().int(),
  debtReadable: z.boolean(),
})

/**
 * ══ ĐẦU MÀN SỔ CHỨNG TỪ BÁN — ba ô tiền · hai hộp chọn · danh sách chưa nạp MISA ══════════════
 *
 * Tất cả tính trên TOÀN BỘ tập đang lọc, nên đây là một cửa RIÊNG: nó chỉ chạy lại khi bộ lọc đổi,
 * còn lật trang thì không đụng tới.
 *
 * ⚠ `unsynced` là DANH SÁCH chứ không phải con số — nút "đẩy tất cả" cần đúng những tờ đó, và câu
 *   xác nhận còn liệt kê 15 tờ đầu kèm số tiền.
 * ⚠ `customers`/`salesNames` là nội dung hai HỘP CHỌN. Chúng lấy DISTINCT ở SQL trên cả sổ, không
 *   gom từ trang đang xem — hộp chọn thiếu mất khách ở trang 7 là một bộ lọc nói dối.
 */
export const ArRegisterSummaryDto = z.object({
  totalRows: z.number().int(),
  totalVnd: z.number(),
  paidVnd: z.number(),
  remainVnd: z.number(),
  unsynced: z.array(z.object({
    id: Uuid, docNo: z.string(), invName: z.string(),
    invoiceNo: z.string().nullable(), total: MoneyOut,
  })),
  customers: z.array(z.object({ id: Uuid, name: z.string() })),
  salesNames: z.array(z.string()),
})

/**
 * MẪU IN (F11). `body` đi kèm trong danh sách — màn in cần nội dung mẫu ngay để dựng bản in, và
 * số mẫu của một pháp nhân đếm bằng đơn vị chục, không phải nghìn.
 */
export const DocTemplateDto = z.object({
  id: Uuid,
  kind: z.enum(['contract', 'appendix', 'quote', 'waybill', 'handover', 'bill']),
  partyKind: z.enum(['customer', 'vendor']),
  name: z.string(),
  body: z.string(),
  /** Link Google Doc bản gốc — `null` là chưa có. Sale mở nó để giữ ĐÚNG bố cục bản công ty. */
  driveUrl: z.string().nullable(),
  isDefault: z.boolean(),
  sortOrder: z.number(),
  active: z.boolean(),
  version: z.number(),
  /** Lần sửa cuối — đủ 5 mẫu thì "＋ Thêm mẫu" thay mẫu CŨ NHẤT theo mốc này (T25, bản cũ `AzContract.tsx:1932-1941`). */
  updatedAt: z.string(),
})

/**
 * Cấu hình công ty (`/settings`) — ba ô hiển thị + SÁU Ô ĐẦU THƯ in trên hợp đồng / báo giá (F11).
 * Bảng có đúng một dòng mỗi pháp nhân; chưa ai lưu lần nào thì mọi ô là `null`.
 */
export const CompanySettingsDto = z.object({
  divisionName: z.string().nullable(),
  displayName: z.string().nullable(),
  bctcTemplateUrl: z.string().nullable(),
  printCompanyName: z.string().nullable(),
  printCompanyAddr: z.string().nullable(),
  printCompanyTax: z.string().nullable(),
  printCompanyPhone: z.string().nullable(),
  printCompanyEmail: z.string().nullable(),
  printCompanyWeb: z.string().nullable(),
  /** Tài khoản nhận tiền CỦA MÌNH + giám đốc ký (`0069`) — in trên ĐNTT và bảng kê bán. */
  payAcctName: z.string().nullable(),
  payAcctNo: z.string().nullable(),
  payBankName: z.string().nullable(),
  payBankAddr: z.string().nullable(),
  directorName: z.string().nullable(),
  /** Địa chỉ nhận báo cáo dòng tiền cho CEO (`0071`). */
  ceoReportEmail: z.string().nullable(),
  ceoReportCc: z.string().nullable(),
  /** Tờ in bảng giá công bố đường sắt (`0072`). */
  railDocTitle: z.string().nullable(),
  railDocSourceNote: z.string().nullable(),
  railDocEffectiveFrom: ISODate.nullable(),
})

/**
 * ĐƠN HÀNG RT (F10).
 *
 * ⚠ `costPriceAt` là `.optional()` chứ không phải `.nullable()` suông: với người không được xem
 *   giá vốn, khoá này KHÔNG XUẤT HIỆN trong phản hồi (E16 — `maskCost` xoá hẳn khoá). `null` là
 *   một giá trị THẬT ("đơn chưa ghi giá vốn"), khác hẳn "bạn không được xem".
 */
export const OrderDto = z.object({
  id: Uuid,
  ref: z.string(),
  quoteId: Uuid.nullable(),
  customerId: Uuid,
  ownerUserId: Uuid.nullable(),
  kind: z.enum(['outbound', 'inbound', 'internal']),
  pickupAddr: z.string(),
  dropAddr: z.string(),
  pickups: z.array(z.unknown()).nullable(),
  drops: z.array(z.unknown()).nullable(),
  cargo: z.string().nullable(),
  weightTon: z.number().nullable(),
  contNo: z.string().nullable(),
  contSize: z.enum(['20', '40', '45']).nullable(),
  contType: z.string().nullable(),
  packId: Uuid.nullable(),
  routeName: z.string().nullable(),
  planDate: ISODate,
  dueAt: ISODate.nullable(),
  poNo: z.string().nullable(),
  bookingNo: z.string().nullable(),
  billNo: z.string().nullable(),
  sealNo: z.string().nullable(),
  trainNo: z.string().nullable(),
  currentLocation: z.string().nullable(),
  needInvoice: z.boolean().nullable(),
  revenues: z.array(z.unknown()).nullable(),
  sellPrice: MoneyOut,
  listPriceId: Uuid.nullable(),
  listPriceAt: MoneyOut.nullable(),
  costPriceAt: MoneyOut.nullable().optional(),
  revenueMode: z.enum(['contract', 'negotiated']).nullable(),
  contractPriceId: Uuid.nullable(),
  estOtherCostVat: MoneyOut.nullable(),
  estOtherCostNoVat: MoneyOut.nullable(),
  estOffBook: MoneyOut.nullable(),
  estDepreciation: MoneyOut.nullable(),
  /**
   * Số chứng từ NVK đã ghi sổ cho Phân bổ dầu / khấu hao vỏ lạnh (xác nhận ở Vận hành vỏ lạnh) — Chi tiết đơn hiện link
   * "🧾 đã ghi sổ" như bản cũ (`estOffBookNvkDocNo` · `estDepreciationNvkDocNo`, `Az.tsx:8809-8823`; rà 16/10 L6).
   */
  fuelAllocDocNo: z.string().nullable().optional(),
  deprAllocDocNo: z.string().nullable().optional(),
  fuelUsedL: z.number().nullable(),
  jobFees: z.array(z.object({
    label: z.string(),
    amount: z.union([z.number(), z.string()]),
    payeeKind: z.enum(['customer', 'vendor', 'staff']).optional(),
    payeeId: z.string().optional(),
    payeeName: z.string().optional(),
    note: z.string().optional(),
  })).nullable(),
  plannedVendorId: Uuid.nullable(),
  plannedVendorConfirmed: z.boolean(),
  plannedVendorConfirmedAt: ISOTimestamp.nullable(),
  orderStatus: z.enum(['pending', 'confirmed', 'picking', 'awaiting_transport',
    'transiting', 'awaiting_delivery', 'delivered', 'cancelled']),
  // ── Luồng hóa đơn (F15). Đơn hàng chỉ ĐỌC; miền bán hàng ghi. ──
  billMode: z.enum(['immediate', 'monthly']).nullable(),
  billStatus: z.enum(['none', 'requested', 'issued']),
  arInvoiceId: Uuid.nullable(),
  arDocNo: z.string().nullable(),
  arInvoiceId2: Uuid.nullable(),
  arDocNo2: z.string().nullable(),
  invoiceNo: z.string().nullable(),
  draft: z.boolean(),
  /** Đơn hệ RATRACO cũ (0151). Vắng ở bản cũ của hợp đồng ⇒ coi như `false`. */
  legacy: z.boolean().optional(),
  salesNote: z.string().nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  createdAt: ISOTimestamp,
  version: z.number(),
})

/* ══ BẢNG ĐƠN HÀNG LỌC Ở MÁY CHỦ (đợt Đ3) — `POST /az/orders/bang*` · `GET /az/orders/khach/:id/gan-nhat` ═════════ */

/**
 * Một dòng bảng Đơn hàng = đơn (đã cắt giá vốn theo quyền, có `legacy`) + trạng thái TIỀN của nó.
 * ⚠ `payState` là `null` khi người xem KHÔNG đọc được sổ công nợ (`ar:read`) hoặc đơn chưa có hoá đơn. Đơn hệ cũ
 *   luôn `paid` với người đọc được sổ (anh chốt: đơn cũ = đã xuất HĐ + đã thu tiền).
 */
export const OrderBoardRowDto = OrderDto.extend({
  payState: z.enum(['paid', 'partial', 'unpaid']).nullable(),
  /**
   * Tên · mã · MST của KHÁCH trên chính dòng (đợt Đ4, §3.4b): danh mục khách màn tải bị siết `own` (sale chính/phụ
   * của khách), nhưng đơn của sale có thể thuộc khách mà sale đó không phụ trách (24,5 % đơn hệ cũ) ⇒ màn hiện `—`.
   * Chỉ là khách của CHÍNH đơn người xem thấy — không lộ khách nào khác. `null` khi khách không còn trong danh mục.
   */
  customerName: z.string().nullable(),
  customerCode: z.string().nullable(),
  customerTax: z.string().nullable(),
})

export const OrderBoardPageDto = z.object({
  rows: z.array(OrderBoardRowDto),
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  /** Chỉ có khi nhảy trang CUỐI (cần đếm để cắt đúng trang); còn lại `null` — tổng số đọc ở `…/tom-tat`. */
  total: z.number().int().nullable(),
  /**
   * CHỈ ở chế độ `ids` (đọc lại đúng các đơn — chi tiết đơn): cơ cấu doanh thu từng đơn, cùng hàm `revenueBreakdown`
   * và cùng khuôn `OrderDetailDto.totals`. Đơn hệ cũ không cửa `chi-tiet` nào thấy được ⇒ hộp chi tiết đọc tiền ở đây.
   */
  totals: z.array(z.object({
    jobId: Uuid,
    freightVnd: z.number(), surchargeVnd: z.number(), otherVnd: z.number(), revenueVnd: z.number(),
    vatRatePct: z.number(), vatVnd: z.number(), onBehalfVnd: z.number(), grandTotalVnd: z.number(),
    vatGroups: z.array(z.object({ vatRate: z.number(), baseVnd: z.number(), vatVnd: z.number(), labels: z.array(z.string()) })),
  })).optional(),
})

/**
 * Đầu màn Đơn hàng — tính khi BỘ LỌC đổi, không tính khi lật trang.
 * ⚠ `counts` đếm trên TOÀN BỘ đơn trong phạm vi (không theo bộ lọc) — "bấm chip sẽ thấy bao nhiêu"; `kpi` +
 *   `totalRows` + `missingCodes` tính trên TẬP ĐANG LỌC. Cả hai GỒM đơn hệ cũ (anh chốt: không phân biệt cũ/mới).
 * ⚠ `buyVnd`/`profitVnd` `null` = người xem không được thấy giá vốn, HOẶC tập lọc không có đơn nào ngoài nháp
 *   (đúng `orderKpi`: nháp không có giá vốn).
 */
export const OrderBoardSummaryDto = z.object({
  totalRows: z.number().int(),
  counts: z.object({
    all: z.number().int(), draft: z.number().int(), today: z.number().int(), tomorrow: z.number().int(),
    noleg: z.number().int(), running: z.number().int(), unbilled: z.number().int(), owing: z.number().int(),
    reefer: z.number().int(),
  }),
  kpi: z.object({
    sellVnd: z.number(),
    onBehalfVnd: z.number(),
    buyVnd: z.number().nullable(),
    profitVnd: z.number().nullable(),
  }),
  /** Thầu phụ trên chặng (không bị NCC từ chối) của mọi đơn không nháp trong phạm vi — nuôi ô lọc "Đối tác". */
  partnerIds: z.array(Uuid),
  /** Người xem đọc được sổ công nợ — `false` ⇒ ẩn chip "Còn nợ" và ô "Thu tiền" như hôm nay. */
  arReadable: z.boolean(),
  /** Dán NHIỀU mã: những mã KHÔNG có trong tập đang lọc. Một mã (đang gõ) ⇒ `null`. */
  missingCodes: z.array(z.string()).nullable(),
})

/** "Chọn hết" — tối đa `ORDER_BULK_MAX` dòng đầu của tập đang lọc, đúng thứ tự bảng, gồm cả nháp. */
export const OrderBoardSelectAllDto = z.object({ rows: z.array(OrderBoardRowDto) })

/**
 * ⬇ Một LÔ của tệp Excel Đơn hàng. Chặng · chi phí · chứng từ mua · trả NCC của ĐƠN MỚI vẫn đọc ở
 * `POST /az/dieu-hanh/xuat-excel` (đơn hệ cũ không có những thứ đó).
 * ⚠ `invoices` là `null` khi người xem không đọc được sổ công nợ. `firstOrderDates` tính CẢ đơn hệ cũ.
 */
export const OrderBoardExcelDto = z.object({
  rows: z.array(OrderBoardRowDto),
  nextCursor: z.string().nullable(),
  revenue: z.array(z.object({
    jobId: Uuid, freightVnd: z.number(), surchargeVnd: z.number(), otherVnd: z.number(), onBehalfVnd: z.number(),
    revenueVnd: z.number(), vatVnd: z.number(), grandTotalVnd: z.number(),
  })),
  invoices: z.array(z.object({ jobId: Uuid, invoiceNo: z.string().nullable(), invoiceDate: ISODate.nullable() })).nullable(),
  firstOrderDates: z.array(z.object({ customerId: Uuid, date: ISODate })),
})

/** Đơn gần nhất của một khách (mới tạo trước) — KHÔNG gồm đơn hệ cũ: không chép giá đơn cũ sang đơn mới. */
export const LatestOrdersDto = z.object({ rows: z.array(OrderDto) })

/**
 * Tỉnh/thành đã từng gõ ở điểm lấy/giao của đơn trong phạm vi xem (đợt Đ4) — `OrderModal` gợi ý tỉnh NƯỚC NGOÀI cho
 * dịch vụ quốc tế mà không phải tải toàn bộ đơn. Đã bỏ khoảng trắng hai đầu, bỏ rỗng, không trùng, sắp theo chữ.
 */
export const OrderProvincesDto = z.object({ rows: z.array(z.string()) })

/** CHẶNG (F12). Không có ô tiền nào — giá mua cước là `AzCost` của F14. */
export const LegDto = z.object({
  id: Uuid,
  jobId: Uuid,
  seq: z.number(),
  name: z.string().nullable(),
  vendorId: Uuid.nullable(),
  truckPlate: z.string().nullable(),
  fromAddr: z.string(),
  toAddr: z.string(),
  planDate: ISODate,
  actualDeliveredAt: ISODate.nullable(),
  status: z.enum(['new', 'assigned', 'at_pickup', 'loaded', 'in_transit', 'at_drop', 'delivered', 'cancelled']),
  driveLink: z.string().nullable(),
  vendorConfirmed: z.boolean(),
  vendorConfirmedAt: ISOTimestamp.nullable(),
  vendorConfirmedVia: z.enum(['vendor', 'company']).nullable(),
  vendorRejected: z.boolean(),
  vendorRejectedAt: ISOTimestamp.nullable(),
  vendorRejectReason: z.string().nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
})

/**
 * MỘT DÒNG "VIỆC CỦA TÔI" trên cổng thầu phụ (F13) — chặng + phần đơn thầu phụ được thấy.
 *
 * ⚠ KHÔNG CÓ Ô TIỀN NÀO, và đó là hợp đồng: bản cũ giấu hẳn hai dòng Tổng thu/Tổng phí ở cổng
 *   (`NccPortal.tsx:727`, ghi rõ "bảo mật"). Máy chủ ghép với khung nhìn `v_order_ref` vốn không
 *   có `sell_price`/`revenues`/`cost_price_at` — không có đường nào để tiền rò sang đây.
 */
export const VendorLegDto = LegDto.extend({
  jobRef: z.string(),
  jobStatus: z.string(),
  jobPlanDate: ISODate,
  jobContNo: z.string().nullable(),
  jobTrainNo: z.string().nullable(),
  jobSealNo: z.string().nullable(),
  customerName: z.string().nullable(),
  /** Điểm lấy / giao của ĐƠN — nhà thầu cần địa chỉ và người liên hệ để chạy chuyến (18/09/2026). */
  jobPickupAddr: z.string().nullable(),
  jobDropAddr: z.string().nullable(),
  jobPickups: z.array(z.unknown()).nullable(),
  jobDrops: z.array(z.unknown()).nullable(),
  /** Hàng hoá · tuyến · gói dịch vụ của ĐƠN (`0141`) — hai cột giữa bảng "Đơn hàng của tôi". */
  jobCargo: z.string().nullable(),
  jobWeightTon: z.number().nullable(),
  jobContType: z.string().nullable(),
  jobRouteName: z.string().nullable(),
  jobPackName: z.string().nullable(),
})

/**
 * MỘT ĐƠN "DỰ KIẾN GIAO CHO TÔI" — tab 📋 Kế hoạch của cổng thầu phụ (`NccPortal.tsx:185`).
 * Đơn có "Đối tác dự kiến" = thầu phụ đang đăng nhập và CHƯA có chặng nào. Không có ô tiền nào.
 */
export const VendorPlannedJobDto = z.object({
  id: Uuid,
  ref: z.string(),
  planDate: ISODate.nullable(),
  customerName: z.string().nullable(),
  cargo: z.string().nullable(),
  weightTon: z.number().nullable(),
  contType: z.string().nullable(),
  routeName: z.string().nullable(),
  pickupAddr: z.string().nullable(),
  dropAddr: z.string().nullable(),
  pickups: z.array(z.unknown()).nullable(),
  drops: z.array(z.unknown()).nullable(),
  plannedVendorConfirmed: z.boolean(),
  plannedVendorConfirmedAt: ISOTimestamp.nullable(),
  version: z.number(),
})

/** Thẻ nhận diện trên thanh tiêu đề cổng thầu phụ: tên · MST · số máy (`NccPortal.tsx:436-447`). */
export const VendorMeDto = z.object({
  vendorName: z.string(),
  taxCode: z.string().nullable(),
  phone: z.string().nullable(),
})

/**
 * MỘT KHOẢN CHI PHÍ ĐƠN HÀNG (F14).
 *
 * ⚠ TOÀN BỘ lược đồ này là GIÁ VỐN — không có trường nào ở đây an toàn để gửi cho người không
 *   được xem giá vốn. Vì thế nó KHÔNG có bản "đã cắt trường" như `OrderDto`: cửa trả nó chặn ở
 *   mức CỬA (`mayTouchCost`), chứ không cắt bớt khoá rồi vẫn trả về.
 */
/** 🚚 Kế hoạch bắn chặng — mỗi đơn: số chặng đang có, các chặng đề xuất (tuyến · giá · nguồn giá), cẩu hạ, cột phụ phí RF. */
export const DispatchPlanDto = z.object({
  vendorId: Uuid,
  isRatraco: z.boolean(),
  nLeg: z.number(),
  maxAddLeg: z.number(),
  jobs: z.array(z.object({
    jobId: Uuid,
    base: z.number(),
    legs: z.array(z.object({
      seq: z.number(), label: z.string(), fromAddr: z.string(), toAddr: z.string(), amountVnd: z.number(),
      note: z.string().nullable(), rateId: z.string().nullable(), exId: z.string().nullable(),
    })),
    crane: z.array(z.object({ label: z.string(), amountVnd: z.number() })),
    rfSurchargeLegIndex: z.number().nullable(),
  })),
})
export type DispatchPlanDto = z.infer<typeof DispatchPlanDto>

export const DispatchResultDto = z.object({
  legIds: z.array(Uuid),
  costIds: z.array(Uuid),
  skipped: z.array(z.object({ ref: z.string(), reason: z.string() })),
})
export type DispatchResultDto = z.infer<typeof DispatchResultDto>

export const CostDto = z.object({
  id: Uuid,
  jobId: Uuid,
  legId: Uuid.nullable(),
  vendorId: Uuid.nullable(),
  kind: z.enum(['freight', 'other', 'vendor_advance', 'onbehalf', 'joborder']),
  label: z.string(),
  amount: MoneyOut,
  payeeKind: z.enum(['customer', 'vendor', 'staff']).nullable(),
  payeeId: Uuid.nullable(),
  payeeName: z.string().nullable(),
  priceMode: z.enum(['bid', 'nego']).nullable(),
  bidRateId: Uuid.nullable(),
  settleMode: z.enum(['immediate', 'monthly']),
  expenseItem: z.string().nullable(),
  debitAccount: z.string().nullable(),
  creditAccount: z.string().nullable(),
  whtRate: z.number().nullable(),
  vatRate: z.number().nullable(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  invoiceSymbol: z.string().nullable(),
  mgmtOnly: z.boolean().nullable(),
  payStatus: z.enum(['draft', 'acc_confirmed', 'requested', 'paid']),
  docStatus: z.enum(['missing', 'received', 'original']),
  accConfirmedBy: Uuid.nullable(),
  accConfirmedAt: ISOTimestamp.nullable(),
  statementRef: z.string().nullable().optional(),
  apDocNo: z.string().nullable().optional(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
})

/**
 * TỔNG HỢP GIÁ VỐN của MỘT đơn (F14).
 *
 * ⚠ Hai khoá `vendorCost` và `grossMargin` nằm trong `COST_FIELD_NAMES` — đúng danh sách mà cổng
 *   `no-cost-leak` quét ở mọi phản hồi. Đặt đúng tên là CỐ Ý: nếu một ngày nào đó khối này bị ghép
 *   nhầm vào phản hồi của cửa khác, cổng đó đỏ ngay.
 * ⚠ `grossMargin` là `number | null` chứ không phải `0`: đơn chưa có doanh thu thì lãi gộp là
 *   CHƯA BIẾT.
 * ⚠ Ba con số là `number`, không phải `MoneyOut`: đây là TỔNG để hiển thị, không phải giá trị đem
 *   ghi sổ. Tiền cần cộng trừ chính xác thì nằm ở `amount` của từng dòng.
 */
export const CostSummaryDto = z.object({
  vendorCost: z.number(),
  onBehalf: z.number(),
  grossMargin: z.number().nullable(),
})

/**
 * TỆP ĐÍNH KÈM (F12) — ảnh POD.
 * ⚠ KHÔNG có trường nội dung: danh sách chỉ mang tên và cỡ; tải nội dung bằng cửa riêng.
 */
export const AttachmentDto = z.object({
  id: Uuid,
  ownerKind: z.enum(['job', 'leg', 'contract', 'ap_invoice', 'ar_invoice', 'cont_repair', 'cont_fuel_log', 'cont_fuel_transfer', 'ap_statement', 'payment_request']),
  ownerId: Uuid,
  kind: z.string(),
  fileName: z.string(),
  mime: z.string(),
  bytes: z.number(),
  uploadedBy: Uuid.nullable(),
  uploadedBySource: z.enum(['app_user', 'vendor_user']),
  createdAt: ISOTimestamp,
})

/** Một kỳ GIA HẠN — bàn giao bản gốc tính theo KỲ, nên mỗi kỳ mang ô nhận riêng. */
export const ContractRenewalDto = z.object({
  id: Uuid,
  validFrom: ISODate,
  validTo: ISODate,
  note: z.string(),
  createdAt: ISOTimestamp,
  createdBy: z.string(),
  origReceivedBy: z.string().nullable(),
  origReceivedAt: ISOTimestamp.nullable(),
  origReceivedDate: ISODate.nullable(),
})

/** Một PHỤ LỤC đã phát hành. `sourceQuoteRef` khác `null` ⇒ soạn thẳng từ một báo giá đã chốt. */
export const ContractAppendixDto = z.object({
  id: Uuid,
  no: z.number(),
  body: z.string(),
  templateId: Uuid.nullable(),
  sourceQuoteRef: z.string().nullable(),
  createdAt: ISOTimestamp,
  createdBy: z.string(),
})

export const ContractCommentDto = z.object({
  id: Uuid,
  at: ISOTimestamp,
  byName: z.string(),
  byRole: z.string(),
  text: z.string(),
})

export const ContractDto = z.object({
  id: Uuid,
  /** ⚠ ĐÚNG MỘT trong hai có giá trị — hợp đồng bán (khách) hoặc mua (thầu phụ). Xem `0061`. */
  customerId: Uuid.nullable(),
  vendorId: Uuid.nullable(),
  contractNo: z.string().nullable(),
  /** Mẫu đã soạn từ đó, và thân đã soạn — bản in (F11) đọc hai ô này. */
  templateId: Uuid.nullable(),
  body: z.string().nullable(),
  status: z.enum(['draft', 'proposed', 'approved', 'rejected', 'signed']),
  signDate: ISODate.nullable(),
  validFrom: ISODate.nullable(),
  validTo: ISODate.nullable(),
  creditDays: z.number().nullable(),
  value: MoneyOut.nullable(),
  detailsFilled: z.boolean(),
  assignedApproverId: Uuid.nullable(),
  /** Người TRÌNH lần trình ký đang chờ (`0168`) — `null` ở lần trình cũ: lùi về tên trong `history` (như máy chủ). */
  proposedById: Uuid.nullable(),
  proposeNote: z.string().nullable(),
  origSubmittedBy: z.string().nullable(),
  origReceivedBy: z.string().nullable(),
  origReceivedDate: ISODate.nullable(),
  /** Kế toán trả về "chờ bổ sung" kèm lý do — `null` = không có cờ. Cờ này lật `origStoredCurrent`. */
  origSupplement: z.object({ reason: z.string(), by: z.string(), at: z.string() }).nullable(),
  /** 🔗 Link bản Word đã sửa (Drive/Docs) để copy gửi khách — `null` = chưa dán. */
  shareUrl: z.string().nullable(),
  /** Nhật ký các bước duyệt/ký — mảng sự kiện tự do, màn hình hiện thành dòng thời gian. */
  history: z.array(z.unknown()).nullable(),
  /* ── Ba khối con của hợp đồng (`0124`), nằm trong ba cột JSONB của `0019` ───────────────── */
  renewals: z.array(ContractRenewalDto),
  appendices: z.array(ContractAppendixDto),
  comments: z.array(ContractCommentDto),
  /** ⭐ Hạn hiệu lực THẬT, tính cả gia hạn — màn KHÔNG tự tính lại. */
  validToEffective: ISODate.nullable(),
  /** ⭐ Kỳ ĐANG CHẠY đã có bản gốc chưa — căn cứ chi hoa hồng. Khác `origReceivedBy` khi đã gia hạn. */
  origStoredCurrent: z.boolean(),
  version: z.number(),
})

export const ContractPriceDto = z.object({
  id: Uuid,
  customerId: Uuid.nullable(),
  vendorId: Uuid.nullable(),
  code: z.string(),
  fromPlace: z.string().nullable(),
  toPlace: z.string().nullable(),
  contType: z.string().nullable(),
  cargo: z.string().nullable(),
  unitPrice: MoneyOut,
  vatRate: z.number(),
  effectiveFrom: ISODate,
  effectiveTo: ISODate.nullable(),
  contractId: Uuid.nullable(),
  note: z.string().nullable(),
  active: z.boolean(),
  version: z.number(),
})

/**
 * ⚠ `costPriceAt` và `estBuyPrice` là TUỲ CHỌN — máy chủ **XOÁ HẲN hai khoá này** khỏi phản hồi
 *   với người không được xem giá vốn (E16, `no-cost-leak.ts`). `null` là giá trị THẬT ("chưa nhập
 *   giá vốn"), khác hẳn "không có quyền thấy". Đừng `?? null` chúng ở tầng đọc: làm thế là gộp lại
 *   đúng hai trạng thái mà máy chủ vừa cố tách ra.
 */
export const QuoteDto = z.object({
  id: Uuid,
  ref: z.string(),
  /** Ngày lập — bản in báo giá (F11) in nó ở dòng "Tp.Hồ Chí Minh, ngày…". */
  createdAt: ISOTimestamp,
  /** Gói dịch vụ — mã `QT*` ⇒ bản in dùng mẫu Quốc tế. */
  packId: z.string().nullable(),
  customerId: Uuid,
  customerAddrOverride: z.string().nullable(),
  kind: z.enum(['outbound', 'inbound', 'internal']),
  pickupAddr: z.string(),
  dropAddr: z.string(),
  /** Điểm lấy / giao CHI TIẾT — khuôn do màn bán hàng chốt, máy chủ lưu nguyên như `revenues`. */
  pickups: z.array(z.unknown()).nullable(),
  drops: z.array(z.unknown()).nullable(),
  cargo: z.string().nullable(),
  /** Khối lượng hàng, TẤN. `null` = báo giá không nói gì về tấn (phần lớn báo giá cont). */
  weightTon: z.number().nullable(),
  contSize: z.enum(['20', '40', '45']).nullable(),
  contType: z.string().nullable(),
  listPriceId: z.string().nullable(),
  /** Các dòng doanh thu tự do của báo giá — cấu trúc do màn quyết, máy chủ chỉ lưu nguyên. */
  revenues: z.array(z.unknown()).nullable(),
  listPriceAt: MoneyOut.nullable(),
  costPriceAt: MoneyOut.nullable().optional(),
  sellPrice: MoneyOut,
  estBuyPrice: MoneyOut.nullable().optional(),
  priceMode: z.enum(['list', 'cost']).nullable(),
  markupPct: z.number().nullable(),
  validUntil: ISODate.nullable(),
  paymentDays: z.number().nullable(),
  terms: z.string().nullable(),
  approverId: Uuid.nullable(),
  submittedBy: z.string().nullable(),
  approvedBy: z.string().nullable(),
  rejectedReason: z.string().nullable(),
  status: z.enum(['draft', 'pending', 'approved', 'sent', 'won', 'lost']),
  jobId: z.string().nullable(),
  note: z.string().nullable(),
  driveUrl: z.string().nullable(),
  /** Nhật ký các bước trình/duyệt/gửi — cùng khuôn với `ContractDto.history`. */
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
})

export const AppUserDto = z.object({
  id: Uuid,
  email: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  code: z.string().nullable(),
  phone: z.string().nullable(),
  role: z.string(),
  active: z.boolean(),
  status: z.string(),
  bankAccount: z.string().nullable(),
  bankName: z.string().nullable(),
  apScope: z.enum(['own', 'all']).nullable(),
  azSalesScope: z.enum(['own', 'all']),
  azSeeVendorCost: z.boolean(),
  azQuoteFromCost: z.boolean(),
  version: z.number(),
  hireDate: ISODate.nullable(),
  monthlyTarget: MoneyOut.nullable(),
  empTypePeriods: z.array(z.object({ effectiveFrom: ISODate, empType: z.enum(['staff', 'ctv']), by: z.string().optional(), at: z.string().optional() })),
  empTypeNow: z.enum(['staff', 'ctv']),
  /**
   * PHÁP NHÂN người này vào được — cột "Pháp nhân (Duyệt chi)" của bản cũ
   * (`Catalog.tsx:1135-1145`), đọc từ `user_entity` chứ không phải một ô nhập riêng.
   *
   * ⚠ RỖNG KHÔNG PHẢI LÀ "VÀO ĐƯỢC TẤT CẢ" — ngược hẳn bản cũ. Cedar lấy pháp nhân từ `entityIds`
   *   của phiên, nên không có dòng `user_entity` nào là 403 ở MỌI cửa (xem `createAppUser`). Màn
   *   phải cảnh báo, không được hiểu nhầm thành "chưa khoá".
   */
  entities: z.array(z.object({ name: z.string(), badge: z.string(), color: z.string() })),
  /** Đã có mật khẩu đăng nhập bằng tài khoản (`0177`) — `false` = chỉ vào bằng FADO SSO. Hash KHÔNG BAO GIỜ đi ra. */
  passSet: z.boolean(),
  /** Đang dùng mật khẩu TẠM do admin/KTT cấp, chưa đặt mật khẩu riêng. */
  mustChangePass: z.boolean(),
})

/** Tình trạng mật khẩu CỦA CHÍNH MÌNH — thẻ 🔒 Mật khẩu (E13 sửa 19/10/2026). */
export const PasswordStateDto = z.object({
  passSet: z.boolean(),
  passSetAt: z.string().nullable(),
  mustChangePass: z.boolean(),
})

/** Mật khẩu tạm vừa cấp cho NHÂN SỰ nội bộ — CHỈ có trong phản hồi lệnh cấp, không có cửa đọc lại. */
export const UserTempPasswordDto = z.object({ tempPassword: z.string() })

/** Tài khoản CỔNG THẦU PHỤ (`vendor_user`, E15) — tab Danh mục ▸ Người dùng ▸ Đối tác/Thầu phụ. */
export const VendorUserDto = z.object({
  id: Uuid,
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  /** NCC đang gắn — `null` = chưa gắn, cổng không cho thấy việc nào (fail-closed). */
  vendorId: Uuid.nullable(),
  vendorCode: z.string().nullable(),
  vendorName: z.string().nullable(),
  active: z.boolean(),
  /** Còn đang dùng mật khẩu tạm — thầu phụ chưa vào lần nào để đổi. */
  mustChangePass: z.boolean(),
  /** CHƯA có mật khẩu — vai ngoài admin/GĐ cấp cổng (rà 17/10); không đăng nhập được tới khi admin/GĐ cấp mật khẩu. */
  passwordPending: z.boolean(),
  /** Khoá do admin/GĐ — chỉ admin/GĐ mở lại (`0165`). Chỉ có nghĩa khi `active = false`. */
  lockedByManager: z.boolean(),
  lastLoginAt: z.string().nullable(),
  version: z.number(),
})

/**
 * Mật khẩu tạm vừa sinh — CHỈ có trong phản hồi của lệnh tạo / cấp lại (E17). Máy chủ không giữ
 * bản thô, nên không có cửa nào đọc lại được.
 */
export const VendorTempPasswordDto = z.object({ tempPassword: z.string() })
/** `tempPassword: null` = người cấp không phải admin/GĐ — tài khoản chờ admin/GĐ cấp mật khẩu (rà 17/10). */
export const VendorUserIssuedDto = z.object({ account: VendorUserDto, tempPassword: z.string().nullable() })

/** Danh sách người dùng RÚT GỌN — cửa `/api/v1/users`, chỉ đủ để đổ ô chọn. */
export const UserRefDto = z.object({
  id: Uuid,
  name: z.string(),
  role: z.string(),
})

export const ApprovalRuleDto = z.object({
  id: Uuid,
  module: z.string(),
  step: z.string(),
  roles: z.array(z.string()),
  defaultAssigneeId: Uuid.nullable(),
  extraUserIds: z.array(Uuid),
  version: z.number(),
})

export const OpsItemDto = z.object({
  id: Uuid,
  kind: z.string(),
  /** Ký hiệu — CHỈ Dịch vụ có. */
  code: z.string().nullable(),
  name: z.string(),
  note: z.string().nullable(),
  active: z.boolean(),
  /** Thứ tự hiện ra (cũng là thứ tự ô chọn). Màn cần nó để kéo–thả đổi chỗ. */
  sortOrder: z.number(),
  version: z.number(),
})

export const PriceVersionDto = z.object({
  id: Uuid,
  name: z.string(),
  ownerId: Uuid.nullable(),
  vendorId: Uuid.nullable(),
  effectiveFrom: ISODate,
  status: z.enum(['pending', 'approved', 'rejected']).nullable(),
  requestedBy: z.string().nullable(),
  approverId: Uuid.nullable(),
  approvedBy: z.string().nullable(),
  rejectNote: z.string().nullable(),
  fuelPrice: MoneyOut.nullable(),
  roundTo: MoneyOut.nullable(),
  roundMode: z.enum(['ceil', 'round', 'floor']).nullable(),
  note: z.string().nullable(),
  version: z.number(),
})

/** Một dòng giá công bố cho thống kê theo giai đoạn — giá công bố đã tính, nhóm đã xếp ở máy chủ. */
export const PricePeriodRowDto = z.object({
  versionId: Uuid,
  fromPlace: z.string(),
  toPlace: z.string(),
  contType: z.string().nullable(),
  costPrice: MoneyOut,
  markupPct: z.number(),
  listPrice: MoneyOut,
  group: z.enum(['gaga', 'rf', 'short', 'sur']),
})
export const PriceStatsDto = z.object({ versions: z.array(PriceVersionDto), rows: z.array(PricePeriodRowDto) })
/** Kết quả Duyệt / Trả về một phiên giá công bố — số người đã nhận chuông (bản cũ "Đã báo N người"). */
export const DecidePriceVersionResultDto = z.object({ notified: z.number() })
/**
 * 🗑 QUẢN LÝ PHIÊN — MỘT phiên trong bản rà (proship 0bafe24): số dòng giá · số đơn từng dùng · khoá 🔒 · lý do gợi ý dọn.
 * `inEffect` = phiên đang / sắp hiệu lực — máy chủ chặn xoá. `reasons` ⊂ từ chối · rỗng · trùng.
 */
export const ManagedPriceVersionDto = z.object({
  id: Uuid,
  name: z.string(),
  ownerId: Uuid.nullable(),
  vendorId: Uuid.nullable(),
  effectiveFrom: ISODate,
  status: z.enum(['pending', 'approved', 'rejected']).nullable(),
  requestedBy: z.string().nullable(),
  createdAt: z.string(),
  rows: z.number(),
  jobRefs: z.number(),
  inEffect: z.boolean(),
  reasons: z.array(z.enum(['rejected', 'empty', 'duplicate'])),
  suggested: z.boolean(),
})
/** Bản rà MỌI phiên giá công bố — một khối, mới nhất lên trên. */
export const PriceVersionAuditDto = z.object({ rows: z.array(ManagedPriceVersionDto) })
/** Kết quả xoá phiên: số phiên + số dòng giá đã đi theo. */
export const DeletePriceVersionsResultDto = z.object({ deleted: z.number(), rows: z.number() })

/**
 * HÓA ĐƠN BÁN (F15).
 *
 * ⚠ `subtotal` GỒM thu hộ — đó là số khách phải trả trước thuế. DOANH THU THẬT =
 *   `subtotal − onBehalfTotal`. Dùng thẳng `subtotal` cho báo cáo doanh thu là báo dôi đúng bằng
 *   phần thu hộ (user 21/07 — bản cũ đã sai đúng chỗ này).
 */
export const ArInvoiceDto = z.object({
  id: Uuid,
  docNo: z.string(),
  invoiceNo: z.string().nullable(),
  customerId: Uuid,
  jobId: Uuid.nullable(),
  jobRef: z.string().nullable(),
  docDate: ISODate,
  postDate: ISODate,
  dueDate: ISODate.nullable(),
  invName: z.string(),
  invTax: z.string().nullable(),
  invAddress: z.string().nullable(),
  subtotal: MoneyOut,
  vat: MoneyOut,
  onBehalfTotal: MoneyOut,
  total: MoneyOut,
  paidAmount: MoneyOut,
  status: z.enum(['issued', 'partial', 'paid', 'cancelled']),
  mgmtOnly: z.boolean(),
  statementRef: z.string().nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  invoiceDate: ISODate.nullable(),
  invoiceSymbol: z.string().nullable(),
  invoiceFormNo: z.string().nullable(),
  amisIssuedInvoice: z.boolean(),
  sourceType: z.enum(['order', 'statement', 'manual', 'opening']),
})

/** MỘT DÒNG hóa đơn — một dòng cho MỖI THUẾ SUẤT, cộng một dòng riêng cho thu hộ. */
export const ArInvoiceLineDto = z.object({
  id: Uuid,
  invoiceId: Uuid,
  jobId: Uuid.nullable(),
  jobRef: z.string().nullable(),
  kind: z.enum(['freight', 'other', 'surcharge', 'onbehalf']),
  label: z.string(),
  amount: MoneyOut,
  vatRate: z.number(),
  vatAmount: MoneyOut,
  acctRevenue: z.string().nullable(),
  acctReceivable: z.string().nullable(),
  acctVat: z.string().nullable(),
  sortOrder: z.number(),
  itemCode: z.string().nullable(),
  amisServiceCode: z.string().nullable(),
  plate: z.string().nullable(),
  quantity: z.number().nullable(),
  unitPrice: MoneyOut.nullable(),
  unit: z.string().nullable(),
  serviceName: z.string().nullable(),
})

/** 🧾 Một dòng sổ chứng từ bán — hóa đơn + mã khách · NVKD · số dòng/đơn · định khoản · thu tiền · AMIS. */
export const ArDocRowDto = ArInvoiceDto.extend({
  customerCode: z.string().nullable(),
  salesMainName: z.string().nullable(),
  lineCount: z.number(),
  jobCount: z.number(),
  acctReceivable: z.array(z.string()),
  acctRevenue: z.array(z.string()),
  acctVat: z.array(z.string()),
  payState: z.enum(['paid', 'partial', 'unpaid']),
  amisState: z.enum(['pushed', 'excel', 'pending']),
  amisPushedAt: ISOTimestamp.nullable(),
  amisExcelAt: ISOTimestamp.nullable(),
  amisExcelBy: z.string().nullable(),
})
export type ArDocRowDto = z.infer<typeof ArDocRowDto>

export const ArDocDetailDto = z.object({
  invoice: ArInvoiceDto,
  lines: z.array(ArInvoiceLineDto),
  payments: z.array(z.object({
    id: Uuid, payDate: ISODate, amount: MoneyOut, method: z.string(), note: z.string().nullable(),
    txnId: Uuid.nullable(), voucherNo: z.string().nullable(), txnDescription: z.string().nullable(),
    contraAccount: z.string().nullable(), accountKind: z.string().nullable(), accountName: z.string().nullable(),
  })),
  blockers: z.array(z.string()),
  pushed: z.boolean(),
})
export type ArDocDetailDto = z.infer<typeof ArDocDetailDto>

export const ArPaymentDto = z.object({
  id: Uuid,
  invoiceId: Uuid,
  payDate: ISODate,
  amount: MoneyOut,
  method: z.enum(['bank', 'cash', 'offset']),
  bankAccountId: Uuid.nullable(),
  note: z.string().nullable(),
  version: z.number(),
  /** Nguồn của khoản thu (C15, `0157`) — chỉ có ở danh sách khoản thu của một hoá đơn. `null` = ghi tay / di trú. */
  sourceKind: z.enum(['bank', 'offset', 'advance']).nullable().optional(),
  /** Số phiếu nguồn (BC… / PT… / DT…). */
  sourceRef: z.string().nullable().optional(),
})

/**
 * ⭐ MỘT KHOẢN CỌC / KHÁCH TRẢ TRƯỚC. `leftAmount` là số CÒN CẤN ĐƯỢC — máy chủ tính, màn không tự
 * cộng: hai chỗ cùng cộng lấy là hai chỗ có ngày lệch nhau, và lệch ở đây là tiêu quá số khách đưa.
 */
export const ArAdvanceDto = z.object({
  id: Uuid,
  customerId: Uuid,
  recvDate: ISODate,
  amount: MoneyOut,
  method: z.string(),
  bankAccountId: Uuid.nullable(),
  note: z.string().nullable(),
  usedAmount: MoneyOut,
  leftAmount: MoneyOut,
  version: z.number(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  /** Dòng sổ tiền SINH RA cọc (phần dư của phiếu thu, `0162`) — `null` = cọc ghi tay. Cọc này gỡ ở Sổ phụ, không xoá lẻ. */
  bankTxnId: Uuid.nullable(),
  /** Số phiếu (BC… / PT…) của dòng sổ đó. */
  bankVoucherNo: z.string().nullable(),
})

/** Một hoá đơn còn nợ mà cọc cấn vào được, kèm số còn nợ và hạn — thứ quyết thứ tự lấp sẵn. */
export const ArAllocTargetDto = z.object({
  invoiceId: Uuid,
  docNo: z.string(),
  remain: MoneyOut,
  dueDate: ISODate.nullable(),
  docDate: ISODate,
})

/** Một lần đã cấn — để người đọc thấy tiền cọc đi vào hoá đơn nào. */
export const ArAllocDto = z.object({
  id: Uuid,
  advanceId: Uuid,
  paymentId: Uuid,
  invoiceId: Uuid,
  docNo: z.string(),
  payDate: ISODate,
  amount: MoneyOut,
  /** `version` của khoản thu sinh từ lần cấn — NHẢ cọc = xoá khoản thu đó (`/ar/thu-tien/:id/delete`). */
  paymentVersion: z.number(),
})

/** Một dòng danh sách cọc (`GET /ar/coc`) — cọc kèm các lần đã cấn, cho cột "Cấn vào hóa đơn". */
export const ArAdvanceListRowDto = ArAdvanceDto.extend({ allocs: z.array(ArAllocDto) })

export const ArAllocViewDto = z.object({
  advance: ArAdvanceDto,
  targets: z.array(ArAllocTargetDto),
  /** Gợi ý lấp sẵn: hoá đơn đến hạn trước được cấn trước. Người dùng sửa lại được. */
  suggested: z.array(z.object({ invoiceId: Uuid, amount: MoneyOut })),
  allocs: z.array(ArAllocDto),
})

/**
 * MỘT DÒNG HÀNG CHỜ xuất hóa đơn — đơn đang xin, kèm số tiền để kế toán biết mình sắp xuất bao nhiêu.
 * ⚠ KHÔNG có ô giá vốn nào: hàng chờ đọc qua `v_order_bill_ref`, khung nhìn không mang cột giá vốn.
 */
export const BillQueueDto = z.object({
  id: Uuid,
  ref: z.string(),
  customerId: Uuid,
  planDate: ISODate,
  orderStatus: z.string(),
  billMode: z.enum(['immediate', 'monthly']).nullable(),
  billNote: z.string().nullable(),
  billSplit: z.array(z.string()).nullable(),
  billInvName: z.string().nullable(),
  billInvTax: z.string().nullable(),
  billInvAddress: z.string().nullable(),
  grandTotal: MoneyOut,
  version: z.number(),
})

/** Tuổi nợ NĂM MỐC — `future` là CHƯA ĐẾN HẠN, không phải "không có hạn nên coi như quá hạn". */
export const ArAgingDto = z.object({
  future: z.number(),
  b30: z.number(),
  b60: z.number(),
  b90: z.number(),
  over90: z.number(),
})

/** MỘT DÒNG công nợ: hóa đơn + đã thu + còn nợ + mốc tuổi nợ, TÍNH TỚI một ngày. */
export const ArDebtRowDto = z.object({
  invoice: ArInvoiceDto,
  paid: z.number(),
  owed: z.number(),
  bucket: z.enum(['future', 'b30', 'b60', 'b90', 'over90']),
  lastPaidOn: ISODate.nullable(),
})

export const ArDebtGroupDto = z.object({
  customerId: Uuid,
  customerName: z.string(),
  customerCode: z.string().nullable(),
  customerTax: z.string().nullable(),
  rows: z.array(ArDebtRowDto),
  /** Tổng CÒN NỢ của khách này. Tên `owedTotal` chứ không phải `total` — số cộng để hiển thị. */
  owedTotal: z.number(),
})

export const ArDebtReportDto = z.object({
  groups: z.array(ArDebtGroupDto),
  buckets: ArAgingDto,
  owedTotal: z.number(),
  /** PHÁT SINH LŨY KẾ — tổng + đã thu mọi hoá đơn còn sống, KHÔNG cắt theo ngày chốt (Dashboard ▸ Công nợ). */
  billedTotal: z.number(),
  paidTotal: z.number(),
})

/**
 * Một DÒNG của bảng giá công bố, trong một phiên bản.
 * Ba cột đầu đến từ dòng GIÁ VỐN — ghép sẵn để màn khỏi tra lần hai.
 */
export const ListPriceRowDto = z.object({
  id: Uuid,
  versionId: Uuid,
  costRateId: Uuid,
  fromPlace: z.string(),
  toPlace: z.string(),
  contType: z.string().nullable(),
  /** ⛔ VẮNG HẲN với người không được xem giá vốn (`listPriceRows`, rà Lô 3 17/10) — cổng `cost-boundary` canh khoá này. */
  costPrice: MoneyOut.optional(),
  /** PHẦN TRĂM, không phải tiền. ⛔ Vắng cùng `costPrice`: giá công bố ÷ (1 + markup) ra đúng giá vốn. */
  markupPct: z.optional(z.number()),
  /** Giá GÕ TAY — có thì nó THẮNG markup. `null` = đang dùng công thức. */
  priceOverride: MoneyOut.nullable(),
  listPrice: MoneyOut,
  note: z.string().nullable(),
  version: z.number(),
  /** Nhóm của dòng giá vốn (Ga–Ga · RF · Lấy/giao · Phụ phí) — chia tab bảng giá công bố. */
  group: z.enum(['gaga', 'rf', 'short', 'sur']),
  packName: z.string().nullable(),
  vendorId: Uuid.nullable(),
})

/** Tờ bảng giá công bố đường sắt (`azRailPriceDoc*.ts` bản cũ) — GIÁ BÁN sau markup, không có giá vốn. */
export const RailPriceBoardDto = z.object({
  version: z.object({ id: Uuid, name: z.string(), effectiveFrom: ISODate }).nullable(),
  pending: z.object({ name: z.string(), effectiveFrom: ISODate, approverName: z.string().nullable() }).nullable(),
  gaga: z.array(z.object({ fromPlace: z.string(), toPlace: z.string(), full: MoneyOut, half: MoneyOut.nullable(), transitHours: z.number().nullable() })),
  rf: z.array(z.object({ fromPlace: z.string(), toPlace: z.string(), full: MoneyOut, transitHours: z.number().nullable() })),
  short: z.array(z.object({ fromPlace: z.string(), toPlace: z.string(), ward: z.string().nullable(), province: z.string().nullable(), price: MoneyOut })),
  surcharges: z.array(z.object({ service: z.string(), unit: z.string(), price: MoneyOut, note: z.string().nullable() })),
})

/** Một CHẶNG trong bảng 🔍 Đối soát giá thầu phụ. */
export const PriceAuditRowDto = z.object({
  legId: Uuid,
  jobId: Uuid,
  vendorId: Uuid.nullable(),
  planDate: ISODate,
  legName: z.string().nullable(),
  fromPlace: z.string(),
  toPlace: z.string(),
  booked: MoneyOut,
  /** `null` = KHÔNG dò ra dòng giá nào để so — "không biết" khác "bằng 0". */
  bookPrice: MoneyOut.nullable(),
  diff: MoneyOut.nullable(),
  /** `bid` = tra bảng giá lúc bắn chặng · `nego` = điều hành gõ tay. */
  priceMode: z.string().nullable(),
  statementRef: z.string().nullable(),
  /** Giá bảng lấy từ đâu: `ex` = ngoại lệ đã ghim (📌) · `bid` = dòng giá tra lúc bắn · `rate` = dò lại. */
  bookSource: z.enum(['ex', 'bid', 'rate']).nullable(),
})

export const PriceAuditDto = z.object({
  rows: z.array(PriceAuditRowDto),
  overTotal: MoneyOut,
  underTotal: MoneyOut,
  noBookCount: z.number(),
})

/** BẢNG KÊ BÁN (F16) — gom nhiều đơn của MỘT khách vào MỘT hóa đơn. */
export const ArStatementDto = z.object({
  id: Uuid,
  ref: z.string(),
  customerId: Uuid,
  mode: z.enum(['immediate', 'monthly']),
  status: z.enum(['draft', 'issued', 'cancelled']),
  total: MoneyOut,
  invName: z.string().nullable(),
  invTax: z.string().nullable(),
  invAddress: z.string().nullable(),
  mgmtOnly: z.boolean(),
  note: z.string().nullable(),
  arInvoiceId: Uuid.nullable(),
  arDocNo: z.string().nullable(),
  invoiceNo: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  /** Số đơn đã gom — bản cũ hiện cột "Số đơn" (`Az.tsx:19381` `s.jobIds.length`). */
  jobCount: z.number(),
  /** Ngày hóa đơn AR đã xuất — bản cũ hiện cột "Ngày HĐ". `null` khi còn nháp. */
  invoiceDate: ISODate.nullable(),
  /** ✂️ Bản của cặp tách hóa đơn (1 · 2) — bản cũ `splitPart`, huy hiệu "✂️ Bản n/2". `null` là bảng kê thường. */
  splitPart: z.union([z.literal(1), z.literal(2)]).nullable(),
  /** Mã bảng kê bản 1 mà bản 2 tách ra (bản cũ `splitOfRef`). */
  splitOfRef: z.string().nullable(),
})

/** Một container trên bảng kê bán gửi khách — `azSalesStatementDoc.ts:27` bản cũ. Tiền là chuỗi (§2). */
export const StatementPrintRowDto = z.object({
  jobId: Uuid,
  ref: z.string(),
  packDate: ISODate.nullable(),
  deliverDate: ISODate.nullable(),
  fromPlace: z.string(),
  toPlace: z.string(),
  cargo: z.string(),
  pickupPlate: z.string(),
  trainNo: z.string(),
  deliverPlate: z.string(),
  contType: z.string(),
  contNo: z.string(),
  unitPrice: MoneyOut,
  surcharge: MoneyOut,
  subtotal: MoneyOut,
  vatRatePct: z.number(),
  vat: MoneyOut,
  onBehalf: MoneyOut,
  total: MoneyOut,
  note: z.string(),
  /* ── Hai ô CHỈ để chọn TK doanh thu ở màn xuất chứng từ (`revenueAcct`), không in ra giấy. ── */
  packId: z.string().nullable(),
  routeName: z.string().nullable(),
})

/** 🧾 Xem trước yêu cầu xuất chứng từ bán nhiều đơn — `why` rỗng là đơn xin được. */
/**
 * 👁 CHI TIẾT ĐƠN (`JobDetail`) — phần máy chủ phải tính: cơ cấu doanh thu (cước · phụ thu · DT khác · VAT theo thuế suất · thu hộ)
 * và hóa đơn bán gắn với đơn (kể cả hóa đơn gom từ bảng kê). Khách · sale · thầu phụ · chặng · chi phí màn đã có từ các cửa riêng.
 */
export const OrderDetailDto = z.object({
  totals: z.object({
    freightVnd: z.number(), surchargeVnd: z.number(), otherVnd: z.number(), revenueVnd: z.number(),
    vatRatePct: z.number(), vatVnd: z.number(), onBehalfVnd: z.number(), grandTotalVnd: z.number(),
    vatGroups: z.array(z.object({ vatRate: z.number(), baseVnd: z.number(), vatVnd: z.number(), labels: z.array(z.string()) })),
  }),
  invoices: z.array(z.object({ invoice: ArInvoiceDto, lines: z.array(ArInvoiceLineDto), createdBy: z.string().nullable() })),
  payState: z.enum(['none', 'unpaid', 'partial', 'paid']),
})

/**
 * 💰 HOA HỒNG CỦA CHÍNH TÔI (`0126`) — cửa hẹp cho sale xem phần của mình.
 *
 * ⚠ CỐ Ý KHÔNG CÓ hai sheet và không có số toàn công ty. Sheet Chi tiết mang lãi gộp từng đơn; ai
 *   đọc được nó là suy ngược ra giá vốn cả kỳ. Ở đây chỉ có tiền của chính người gọi.
 */
export const MyCommissionDto = z.object({
  ym: z.string(),
  archived: z.boolean(),
  locked: z.boolean(),
  basis: z.object({
    id: Uuid, name: z.string(), empType: z.string(),
    hhGop: z.number(), thue: z.number(), kpiBonus: z.number(), totalBonus: z.number(),
    orderRefs: z.array(z.string()), missingOrigDocRefs: z.array(z.string()),
    hhMain: z.number(), hhSub: z.number(), mainOrderCount: z.number(), subOrderCount: z.number(),
  }).nullable(),
  payouts: z.array(z.object({
    id: Uuid, ref: z.string(), ym: z.string(), paidAt: ISODate.nullable(),
    hhGop: z.number(), thue: z.number(), kpiBonus: z.number(), totalBonus: z.number(),
    orderRefs: z.array(z.string()), createdAt: ISOTimestamp, note: z.string().nullable(),
    requestRef: z.string().nullable(), requestStatus: z.string().nullable(),
    requestPaidDate: ISODate.nullable(),
  })),
  fixedSalaryVnd: z.number(),
  cultureScore: z.number().nullable(),
})

/** 📊 Dữ liệu báo cáo vận hành: chặng chi tiết + giá vốn theo (đơn, thầu phụ) + giá vốn đơn + ngày hóa đơn. Giá vốn `null` với người không xem được. */
export const OpsReportDataDto = z.object({
  legs: z.array(z.object({
    id: Uuid, jobId: Uuid, seq: z.number(), vendorId: Uuid.nullable(), fromAddr: z.string(), toAddr: z.string(), planDate: ISODate,
    truckPlate: z.string().nullable(), status: z.string(), vendorConfirmed: z.boolean(), vendorRejected: z.boolean(),
  })),
  costByVendor: z.array(z.object({ jobId: Uuid, vendorId: Uuid.nullable(), amountVnd: z.number() })).nullable(),
  jobCost: z.array(z.object({ jobId: Uuid, costVnd: z.number() })).nullable(),
  invoiceDates: z.array(z.object({ jobId: Uuid, date: ISODate })),
})

/**
 * ⬇ DỮ LIỆU TỆP EXCEL ĐƠN HÀNG (`exportOrdersXlsx`, `proship-trans/src/pages/Az.tsx:1841`) — một lượt cho cả loạt đơn
 * đang xuất: chặng, TỪNG khoản chi phí kèm chứng từ mua, trạng thái "Trả NCC", ngày đơn đầu tiên của khách.
 * ⚠ `costs`/`apInvoices`/`vendorPay` là `null` với người không được xem giá vốn.
 */
export const OrderExportDataDto = z.object({
  legs: z.array(z.object({
    id: Uuid, jobId: Uuid, seq: z.number(), vendorId: Uuid.nullable(), fromAddr: z.string(), toAddr: z.string(), planDate: ISODate,
    truckPlate: z.string().nullable(), vendorRejected: z.boolean(),
  })),
  costs: z.array(z.object({
    id: Uuid, jobId: Uuid, legId: Uuid.nullable(), vendorId: Uuid.nullable(),
    kind: z.enum(['freight', 'other', 'vendor_advance', 'onbehalf', 'joborder']),
    label: z.string(), amount: MoneyOut, vatRate: z.number().nullable(),
    payeeKind: z.enum(['customer', 'vendor', 'staff']).nullable(), payeeId: Uuid.nullable(), payeeName: z.string().nullable(),
    invoiceNo: z.string().nullable(), creditAccount: z.string().nullable(), note: z.string().nullable(),
    payStatus: z.enum(['draft', 'acc_confirmed', 'requested', 'paid']),
    statementRef: z.string().nullable(), apInvoiceId: Uuid.nullable(),
  })).nullable(),
  apInvoices: z.array(z.object({
    id: Uuid, docNo: z.string(), invoiceNo: z.string().nullable(), invoiceDate: ISODate.nullable(),
    totalVnd: MoneyOut, paidVnd: MoneyOut, status: z.string(),
    payRequestRefs: z.string().nullable(), paidDate: ISODate.nullable(),
  })).nullable(),
  vendorPay: z.array(z.object({ jobId: Uuid, state: z.enum(['paid', 'partial', 'unpaid', 'none']) })).nullable(),
  firstOrderDates: z.array(z.object({ customerId: Uuid, date: ISODate })),
  /** Cơ cấu doanh thu từng đơn — cùng hàm `revenueBreakdown` của màn chi tiết đơn, không chép luật sang web. */
  revenue: z.array(z.object({
    jobId: Uuid, freightVnd: z.number(), surchargeVnd: z.number(), otherVnd: z.number(), onBehalfVnd: z.number(),
    revenueVnd: z.number(), vatVnd: z.number(), grandTotalVnd: z.number(),
  })),
  /** Tổng chi phí của đơn (đã gồm phân bổ dầu + khấu hao) — cùng số cột "Mua" của bảng Điều hành. `null` khi không xem giá vốn. */
  jobCost: z.array(z.object({ jobId: Uuid, costVnd: z.number() })).nullable(),
})

/** 📋 Tóm tắt từng đơn cho bảng Điều hành (`OpsTab`) — `cost` là `null` với người không được xem giá vốn. */
export const OpsBoardDto = z.object({
  rows: z.array(z.object({
    jobId: Uuid,
    legs: z.object({ count: z.number(), done: z.number(), trips: z.number(), vendorIds: z.array(Uuid), plates: z.array(z.string()), waitingVendor: z.boolean(), rejected: z.boolean() }),
    cost: z.object({
      costVnd: z.number(), profitVnd: z.number().nullable(), costCount: z.number(),
      unpaidCount: z.number(), unpaidVnd: z.number(), stmtState: z.enum(['', 'none', 'has']),
      /** Khoản đã qua KẾ TOÁN XÁC NHẬN — nuôi chip "💵 xác nhận chi phí" ở màn bán hàng. */
      confirmedCount: z.number(),
    }).nullable(),
    invoiceDate: ISODate.nullable(),
    /** 📣 Đơn đang mở thầu (huy hiệu trên danh sách điều hành). */
    biddingOpen: z.boolean(),
    /**
     * 📁 NGĂN CHỨNG TỪ ĐÃ CÓ TỆP của đơn — nuôi MÀU icon 📁 ở ô lấy/giao (`StopCell`).
     *
     * ⚠ Là DANH SÁCH NGĂN (`pod:<id điểm>`), cố ý không phải một cờ "đơn có tệp": tệp neo vào TỪNG
     *   ĐIỂM (xem chú thích đầu `StopModal`), mà đơn nhiều điểm giao là chuyện thường — một cờ
     *   chung sẽ tô cam cả điểm chưa ai chụp gì.
     * ⚠ CHỈ có/không, KHÔNG có số đếm: bản cũ đã bỏ số cạnh icon (user 27/08 "bỏ số 1 2, không cần
     *   đếm"), nên trả số về đây là mở đường cho màn bày lại thứ người dùng đã bỏ.
     */
    docSlots: z.array(z.string()),
  })),
})

/** Kiểm trước khi đổi trạng thái một lô đơn — `skip` là lý do không đổi được; `unwind` là số chặng / khoản sẽ bị gỡ. */
export const StatusFlowCheckDto = z.object({
  rows: z.array(z.object({
    jobId: Uuid, ref: z.string(), version: z.number(), orderStatus: z.string(), skip: z.string().nullable(),
    unwind: z.object({ legs: z.number(), costs: z.number(), draftVnd: z.number().nullable() }).nullable(),
    hasVendor: z.boolean(), planDropDate: ISODate.nullable(),
    /** Đơn HÀNG LẠNH · có ảnh giao hàng (POD) ở chặng nào — màn cảnh báo "lạnh thiếu POD" trước khi Đã giao (L1). */
    reefer: z.boolean(), hasPod: z.boolean(),
    pendingLegs: z.array(z.object({ id: Uuid, seq: z.number(), fromAddr: z.string(), toAddr: z.string(), status: z.string(), vendorId: Uuid.nullable(), version: z.number() })),
  })),
})

/** ⬆️ Nhập giá NCC hàng loạt — lỗi từng dòng (theo thứ tự gửi) + số chặng / khoản đã ghi (dryRun ⇒ 0). */
export const BulkCostImportResultDto = z.object({
  rows: z.array(z.object({ ref: z.string(), jobId: Uuid.nullable(), error: z.string().nullable() })),
  legCount: z.number(),
  costCount: z.number(),
})

/** 📥 Nhập đơn hàng loạt — `errors[].index` là vị trí dòng trong `rows` gửi lên; `refs` là mã đơn đã tạo (dryRun ⇒ rỗng). */
export const BulkOrderImportDto = z.object({
  errors: z.array(z.object({ index: z.number(), message: z.string() })),
  refs: z.array(z.string()),
})

/** 📥 Sửa doanh thu hàng loạt — `code` ổn định cho giao diện dịch; `error` là câu tiếng Việt của máy chủ. */
export const BulkRevenueResultDto = z.object({
  rows: z.array(z.object({
    ref: z.string(), jobId: Uuid.nullable(), customerId: Uuid.nullable(),
    code: z.enum(['notFound', 'amountZero', 'cancelled', 'delivered', 'amisLoaded', 'ledgerLocked']).nullable(), error: z.string().nullable(),
  })),
  updated: z.number(),
})

/** ✏️ Cờ khoá cấp ĐƠN của popup Sửa chặng đã bắn. */
export const LegEditStateDto = z.object({
  orderLocked: z.boolean(), sellAmisLock: z.boolean(), maxLegs: z.number(), maxSurcharges: z.number(),
})

export const StatementRequestPreviewDto = z.object({
  customer: z.object({
    id: Uuid, name: z.string(), invoiceName: z.string().nullable(), tax: z.string().nullable(), address: z.string().nullable(),
  }).nullable(),
  rows: z.array(z.object({
    jobId: Uuid, ref: z.string(), customerId: Uuid, grandTotal: z.number(), plates: z.array(z.string()),
    packDate: ISODate.nullable(), fromPlace: z.string(), toPlace: z.string(), why: z.string(),
  })),
  print: z.object({
    ref: z.string(),
    customerName: z.string(),
    customerTax: z.string().nullable(),
    customerAddress: z.string().nullable(),
    salesMainName: z.string().nullable(),
    contract: z.object({ contractNo: z.string(), signDate: ISODate.nullable() }).nullable(),
    fromDate: ISODate.nullable(),
    toDate: ISODate.nullable(),
    rows: z.array(StatementPrintRowDto),
    total: MoneyOut,
  }),
})

export const StatementPrintDto = z.object({
  statement: ArStatementDto,
  customerName: z.string(),
  customerTax: z.string().nullable(),
  customerAddress: z.string().nullable(),
  salesMainName: z.string().nullable(),
  contract: z.object({ contractNo: z.string(), signDate: ISODate.nullable() }).nullable(),
  fromDate: ISODate.nullable(),
  toDate: ISODate.nullable(),
  rows: z.array(StatementPrintRowDto),
})

/**
 * MỘT DÒNG BẢNG KÊ THU HỘ — tiền AZ ứng trả hộ khách rồi thu lại.
 * ⚠ Đây là BÁO CÁO đọc từ dòng doanh thu `kind='onbehalf'` của đơn, không phải một bảng riêng.
 */
export const OnBehalfRowDto = z.object({
  jobId: Uuid,
  jobRef: z.string(),
  customerId: Uuid,
  planDate: ISODate,
  label: z.string(),
  amount: MoneyOut,
})

/* ══ MUA — PHẢI TRẢ (F17) ════════════════════════════════════════════════════════════════════ */

/** Một container trên bảng kê mua gửi thầu phụ — `azStatementDoc.ts:25` bản cũ. Tiền là chuỗi (§2). */
export const ApStatementPrintRowDto = z.object({
  jobId: Uuid,
  jobRef: z.string(),
  packDate: z.string(),
  deliverDate: z.string().nullable(),
  contNo: z.string(),
  contType: z.string(),
  wagonNo: z.string(),
  cargo: z.string(),
  fromPlace: z.string(),
  toPlace: z.string(),
  plates: z.tuple([z.string(), z.string(), z.string()]),
  plateLegIds: z.tuple([z.string(), z.string(), z.string()]),
  runDate: z.string(),
  actualPickup: z.string(),
  legFares: z.tuple([MoneyOut, MoneyOut, MoneyOut, MoneyOut]),
  /** Ô tiền SỬA ĐƯỢC tại chỗ (chỉ ô do đúng MỘT khoản tạo ra) — khoá `leg1`…`leg4`·`pickupSur`·`dropSur`. */
  editable: z.record(z.object({ id: Uuid, version: z.number() })),
  pickupSurcharge: MoneyOut,
  dropSurcharge: MoneyOut,
  total: MoneyOut,
  revenue: MoneyOut,
  vatRate: z.number().nullable(),
  note: z.string(),
})

export const ApStatementDto = z.object({
  id: Uuid,
  ref: z.string(),
  /** Chỉ có khi đối tượng là NCC — bảng kê chi phí đơn hàng trả cho khách / nhân viên thì `null` (`0098`). */
  vendorId: Uuid.nullable(),
  kind: z.enum(['vendor', 'joborder', 'onbehalf']),
  payeeKind: z.enum(['vendor', 'customer', 'staff']),
  payeeId: Uuid,
  payeeName: z.string().nullable(),
  feeLabel: z.string().nullable(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  invoiceSymbol: z.string().nullable(),
  vatRate: z.number().nullable(),
  status: z.enum(['draft', 'confirmed', 'posted', 'cancelled']),
  total: MoneyOut,
  confirmedAt: ISOTimestamp.nullable(),
  confirmedBy: z.string().nullable(),
  mgmtOnly: z.boolean(),
  note: z.string().nullable(),
  apInvoiceId: Uuid.nullable(),
  apDocNo: z.string().nullable(),
  vendorAckAt: ISOTimestamp.nullable(),
  vendorAckBy: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
})

/** Kết quả tra bảng kê theo mã (`GET /ap/bang-ke/theo-ma?ref=`) — `null` khi không có / đã huỷ. */
export const ApStatementRefLookupDto = z.object({
  statement: ApStatementDto.pick({ id: true, ref: true, kind: true, status: true }).nullable(),
})

export const ApStatementPrintDto = z.object({
  statement: ApStatementDto,
  /** STK thầu phụ chỉ có với vai ghi sổ — vai khác nhận `null`. */
  vendor: z.object({
    code: z.string().nullable(), name: z.string(), tax: z.string().nullable(), address: z.string().nullable(),
    bankAccount: z.string().nullable(), bankName: z.string().nullable(),
  }),
  rows: z.array(ApStatementPrintRowDto),
})

/* ══ 📋 TAB BẢNG KÊ NCC (`PurchaseDocsTab`) ══════════════════════════════════════════════════════════════════════ */

export const VendorStmtPayRequestDto = z.object({
  id: Uuid, ref: z.string(), status: z.string(), amount: MoneyOut, paidAmount: MoneyOut,
})

/** Một khoản của rổ bảng kê — màn gom `vendorId|mã bảng kê` và lọc tick-nhiều như bản cũ. */
export const VendorStmtCostDto = z.object({
  id: Uuid,
  jobId: Uuid,
  jobRef: z.string(),
  planDate: ISODate.nullable(),
  packId: Uuid.nullable(),
  legId: Uuid.nullable(),
  vendorId: Uuid,
  kind: z.string(),
  label: z.string(),
  amount: MoneyOut,
  vatRate: z.number().nullable(),
  payStatus: z.string(),
  statementId: Uuid.nullable(),
  statementRef: z.string().nullable(),
  apInvoiceId: Uuid.nullable(),
  mgmtOnly: z.boolean(),
  invoiceNo: z.string().nullable(),
  createdAt: ISOTimestamp,
})
export type VendorStmtCostDto = z.infer<typeof VendorStmtCostDto>

export const VendorStmtHeadDto = ApStatementDto.extend({
  vendorCode: z.string().nullable(),
  vendorName: z.string().nullable(),
  vendorTax: z.string().nullable(),
  apInvoiceStatus: z.string().nullable(),
  apInvoiceNo: z.string().nullable(),
  apInvoiceDate: ISODate.nullable(),
  apInvoiceSymbol: z.string().nullable(),
  apPaidAmount: MoneyOut.nullable(),
  amisPushed: z.boolean(),
  payRequests: z.array(VendorStmtPayRequestDto),
})
export type VendorStmtHeadDto = z.infer<typeof VendorStmtHeadDto>

export const VendorStmtRegisterDto = z.object({
  costs: z.array(VendorStmtCostDto),
  statements: z.array(VendorStmtHeadDto),
  vendors: z.array(z.object({ id: Uuid, code: z.string().nullable(), name: z.string(), tax: z.string().nullable() })),
})
export type VendorStmtRegisterDto = z.infer<typeof VendorStmtRegisterDto>

export const VendorStmtWarningsDto = z.object({
  kpi: z.object({ total: MoneyOut, unpaid: MoneyOut, pendingCount: z.number(), pendingVendorCount: z.number() }),
  noPrice: z.array(z.object({ costId: Uuid, jobId: Uuid, jobRef: z.string(), vendorId: Uuid, label: z.string() })),
  dxMismatch: z.array(z.object({
    jobId: Uuid, jobRef: z.string(), arDx: z.boolean(), buys: z.array(z.object({ vendorId: Uuid.nullable(), dx: z.boolean() })),
  })),
  waitingInvoice: z.array(z.object({ id: Uuid, docNo: z.string(), vendorName: z.string(), total: MoneyOut, version: z.number() })),
})
export type VendorStmtWarningsDto = z.infer<typeof VendorStmtWarningsDto>

export const OnBehalfPayBoardDto = z.object({
  pending: z.array(z.object({
    customerId: Uuid, customerName: z.string(), totalVnd: z.number(),
    jobs: z.array(z.object({
      jobId: Uuid, jobRef: z.string(), customerId: Uuid, customerName: z.string(), cancelled: z.boolean(),
      amountVnd: z.number(), leg1VendorId: Uuid.nullable(),
    })),
  })),
  statements: z.array(z.object({
    id: Uuid, ref: z.string(), version: z.number(), customerId: Uuid, customerName: z.string().nullable(), total: MoneyOut,
    status: z.string(), apInvoiceId: Uuid.nullable(), apDocNo: z.string().nullable(), createdAt: ISOTimestamp,
    jobRefs: z.array(z.string()), vendorIds: z.array(Uuid.nullable()), payStatus: z.string(),
    payRequest: z.object({ id: Uuid, ref: z.string(), status: z.string() }).nullable(),
    stage: z.enum(['draft', 'acc_confirmed', 'requested', 'paid']),
    vendors: z.array(z.object({ id: Uuid, code: z.string().nullable(), name: z.string() })),
  })),
  totals: z.object({ pendingVnd: z.number(), outVnd: z.number(), paidVnd: z.number() }),
})
export type OnBehalfPayBoardDto = z.infer<typeof OnBehalfPayBoardDto>

export const VendorStmtDetailDto = z.object({
  statement: ApStatementDto.nullable(),
  vendor: z.object({
    id: Uuid, code: z.string().nullable(), name: z.string(), tax: z.string().nullable(), address: z.string().nullable(),
    bankAccount: z.string().nullable(), bankName: z.string().nullable(),
  }),
  costIds: z.array(Uuid),
  rows: z.array(ApStatementPrintRowDto),
  /** Đã ghi sổ ⇒ số tiền + cờ DX đọc từ CHÍNH chứng từ (không tính lại theo cờ bảng kê). */
  apInvoice: z.object({
    id: Uuid, docNo: z.string(), invoiceNo: z.string().nullable(), invoiceDate: ISODate.nullable(),
    invoiceSymbol: z.string().nullable(), status: z.string(),
    subtotal: MoneyOut, vat: MoneyOut, total: MoneyOut, mgmtOnly: z.boolean(),
  }).nullable(),
  payRequests: z.array(VendorStmtPayRequestDto),
  amisPushed: z.boolean(),
  editLock: z.object({ reason: z.enum(['paid', 'dntt', 'ap']), ref: z.string().nullable() }).nullable(),
  addable: z.array(z.object({ jobId: Uuid, jobRef: z.string(), costCount: z.number().int() })),
  paid: z.boolean(),
  defaultVat: z.number(),
  nextApDocNo: z.string(),
  nextApDocNoDx: z.string(),
  /** Có khoản DX ⇒ máy chủ ghi sổ DX (không thuế) bất kể ô "Phiếu chính thức" (C11). */
  costDx: z.boolean(),
})
export type VendorStmtDetailDto = z.infer<typeof VendorStmtDetailDto>

/**
 * HÓA ĐƠN MUA.
 * ⚠ `whtTotal` là thuế TNCN GIỮ LẠI — KHÔNG trừ khỏi `total`. Số THỰC CHI cho nhà thầu =
 *   `total − whtTotal`; phần thuế được coi như đã cấn trừ khi đóng chứng từ (user 11/08).
 */
export const ApInvoiceDto = z.object({
  id: Uuid,
  docNo: z.string(),
  vendorId: Uuid.nullable(),
  payeeKind: z.enum(['vendor', 'customer', 'staff']),
  payeeId: Uuid,
  statementRef: z.string().nullable(),
  docDate: ISODate,
  postDate: ISODate,
  dueDate: ISODate.nullable(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  invoiceSymbol: z.string().nullable(),
  vendorName: z.string(),
  vendorTax: z.string().nullable(),
  subtotal: MoneyOut,
  vat: MoneyOut,
  whtTotal: MoneyOut,
  total: MoneyOut,
  paidAmount: MoneyOut,
  status: z.enum(['posted', 'partial', 'paid', 'cancelled']),
  mgmtOnly: z.boolean(),
  note: z.string().nullable(),
  /** F21 — thầu phụ đã xác nhận số liệu chưa. Khác `status`, vốn nói về TIỀN. */
  vendorAckAt: ISOTimestamp.nullable(),
  vendorAckBy: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  sourceType: z.enum(['statement', 'manual', 'opening']),
  vendorType: z.enum(['subcon', 'fuel', 'maintenance', 'vetc', 'salary', 'other']),
  paymentMethod: z.enum(['bank', 'cash', 'both']),
  creditDays: z.number().nullable(),
  bankAccountId: Uuid.nullable(),
  invoiceFormNo: z.string().nullable(),
  /** Phiếu ĐNTT CÒN SỐNG đang chi chứng từ này (`0157`) — có thì Sổ phụ / đối trừ không gắn được (C10). */
  livePayRef: z.string().nullable().optional(),
})

/* ══ ĐỐI SOÁT THẦU PHỤ (F21) ═════════════════════════════════════════════════════════════════ */

/**
 * Chứng từ mua như CỔNG THẦU PHỤ thấy — `ApInvoiceDto` + dòng cước rút gọn (tên khoản · mã đơn · tiền ·
 * %VAT), đúng khối "chi tiết dòng cước" của bản cũ (`NccPortal.tsx:973`). Không có tài khoản kế toán.
 */
export const VendorApInvoiceDto = ApInvoiceDto.extend({
  lines: z.array(z.object({ label: z.string(), jobRef: z.string().nullable(), amount: MoneyOut, vatRate: z.number() })),
})

export const ReconItemDto = z.object({ label: z.string(), amount: MoneyOut })

/**
 * Đề xuất đối soát do THẦU PHỤ tự lập. Đây là LỜI KHAI của bên ngoài — không phải chứng từ, không
 * sinh bút toán, không vào công nợ.
 */
export const ReconRequestDto = z.object({
  id: Uuid,
  vendorId: Uuid,
  vendorName: z.string(),
  jobRefs: z.array(z.string()),
  items: z.array(ReconItemDto),
  total: MoneyOut,
  note: z.string().nullable(),
  status: z.enum(['pending', 'reviewed']),
  createdByName: z.string(),
  reviewedByName: z.string().nullable(),
  reviewedAt: ISOTimestamp.nullable(),
  replyNote: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
})

export const ReconSummaryDto = z.object({
  invoiceTotal: MoneyOut,
  paidTotal: MoneyOut,
  dueTotal: MoneyOut,
  ackCount: z.number(),
  waitingCount: z.number(),
})

export const ApInvoiceLineDto = z.object({
  id: Uuid,
  invoiceId: Uuid,
  jobId: Uuid.nullable(),
  jobRef: z.string().nullable(),
  label: z.string(),
  amount: MoneyOut,
  vatRate: z.number(),
  vatAmount: MoneyOut,
  whtRate: z.number(),
  whtAmount: MoneyOut,
  acctExpense: z.string().nullable(),
  acctPayable: z.string().nullable(),
  acctVat: z.string().nullable(),
  acctWht: z.string().nullable(),
  expenseItem: z.string().nullable(),
  sortOrder: z.number(),
  itemCode: z.string().nullable(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  amisServiceCode: z.string().nullable(),
  quantity: z.number().nullable(),
  unitPrice: MoneyOut.nullable(),
  unit: z.string().nullable(),
})

/** 🧾 Một dòng sổ chứng từ mua — chứng từ + mã/địa chỉ NCC, tóm tắt định khoản, loại chứng từ, trạng thái nạp AMIS. */
export const ApDocRowDto = ApInvoiceDto.extend({
  vendorCode: z.string().nullable(),
  vendorAddress: z.string().nullable(),
  lineCount: z.number(),
  /** Dòng chưa có khoản mục chi phí — đẩy hàng loạt hỏi "N dòng thiếu KMCP" (bản cũ `Az.tsx:26356-26357`, L45). */
  noExpenseItemCount: z.number().int(),
  kind: z.enum(['mua', 'fee']),
  acctExpense: z.array(z.string()),
  acctPayable: z.array(z.string()),
  acctVat: z.array(z.string()),
  lineInvoiceNo: z.boolean(),
  /** 📦 Đơn hàng mà dòng của chứng từ thuộc về (`ap-NÚT-3`). */
  jobs: z.array(z.object({ id: Uuid, ref: z.string() })),
  amisState: z.enum(['pushed', 'excel', 'pending']),
  amisPushedAt: ISOTimestamp.nullable(),
  amisExcelAt: ISOTimestamp.nullable(),
  amisExcelBy: z.string().nullable(),
})
export type ApDocRowDto = z.infer<typeof ApDocRowDto>

/** Phiếu chứng từ mua mở ra: dòng + đã trả bằng phiếu nào + ĐNTT + thứ MISA còn thiếu. */
export const ApDocDetailDto = z.object({
  invoice: ApInvoiceDto,
  lines: z.array(ApInvoiceLineDto),
  payments: z.array(z.object({
    via: z.enum(['bank', 'pay']), sourceId: Uuid, payDate: ISODate, amount: MoneyOut,
    docRef: z.string().nullable(), note: z.string().nullable(),
  })),
  payRequest: z.object({
    id: Uuid, ref: z.string(), status: z.string(), amount: MoneyOut, paidAmount: MoneyOut, uncRef: z.string().nullable(),
  }).nullable(),
  blockers: z.array(z.string()),
  pushed: z.boolean(),
})
export type ApDocDetailDto = z.infer<typeof ApDocDetailDto>

export const PushApAmisResultDto = z.object({
  /** Số chứng từ mua đã XÁC NHẬN ĐÃ NẠP MISA (mô phỏng — T53); trống khi lô bị chặn. */
  marked: z.array(z.string()),
  failed: z.array(z.object({ docNo: z.string(), reason: z.string() })),
})
export type PushApAmisResultDto = z.infer<typeof PushApAmisResultDto>

/** "Đẩy AMIS" ở sổ HĐ bán (`/az/billing` ▸ Chứng từ · `/ar`) — cùng luồng xác nhận đã nạp MISA (mô phỏng, T53) như phía mua. */
export const PushArAmisResultDto = z.object({
  /** Số hoá đơn bán đã XÁC NHẬN ĐÃ NẠP MISA; trống khi lô bị chặn. */
  marked: z.array(z.string()),
  failed: z.array(z.object({ docNo: z.string(), reason: z.string() })),
})
export type PushArAmisResultDto = z.infer<typeof PushArAmisResultDto>

/** Một khoản chi phí nhìn từ bảng kê mua — GIÁ VỐN, chỉ ai có quyền mới nhận được. */
export const ApCostDto = z.object({
  id: Uuid,
  jobId: Uuid,
  vendorId: Uuid.nullable(),
  kind: z.string(),
  label: z.string(),
  amount: MoneyOut,
  vatRate: z.number().nullable(),
  whtRate: z.number().nullable(),
  invoiceNo: z.string().nullable(),
  expenseItem: z.string().nullable(),
  debitAccount: z.string().nullable(),
  creditAccount: z.string().nullable(),
  mgmtOnly: z.boolean().nullable(),
  payStatus: z.string(),
  statementRef: z.string().nullable(),
  apInvoiceId: Uuid.nullable(),
  version: z.number(),
  note: z.string().nullable().optional(),
})

/* ══ BẢNG KÊ CHI PHÍ ĐƠN HÀNG (`0098`) ═══════════════════════════════════════════════════════════════ */

export const JobFeePayeeDto = z.object({ kind: z.enum(['vendor', 'customer', 'staff']), id: Uuid, name: z.string() })

export const JobFeePendingDto = z.object({
  groups: z.array(z.object({
    key: z.string(),
    label: z.string(),
    payee: JobFeePayeeDto,
    items: z.array(z.object({
      jobId: Uuid, jobRef: z.string(), label: z.string(), amountVnd: z.number(), note: z.string().nullable(),
      payee: JobFeePayeeDto, date: z.string(),
    })),
  })),
  hidden: z.array(z.object({
    jobRef: z.string(), label: z.string(), amountVnd: z.number(), date: z.string(), why: z.enum(['noInvoice', 'outOfRange']),
  })),
})

/** 👁 Xem trước ghi sổ hàng loạt bảng kê chi phí đơn hàng — bản cũ `Az.tsx:24205-24212` (L47). */
export const JobFeeBulkPostPreviewDto = z.object({
  rows: z.array(z.object({ id: Uuid, ref: z.string(), payeeName: z.string(), docNo: z.string(), total: MoneyOut })),
  skipped: z.array(z.object({ ref: z.string(), reason: z.string() })),
})
export type JobFeeBulkPostPreviewDto = z.infer<typeof JobFeeBulkPostPreviewDto>

export const JobFeeStatementDto = z.object({
  statement: ApStatementDto,
  costs: z.array(ApCostDto.extend({ jobRef: z.string() })),
  invoice: ApInvoiceDto.nullable(),
  sales: z.array(z.object({
    jobId: Uuid, jobRef: z.string(), customerName: z.string(), docNo: z.string().nullable(), total: MoneyOut.nullable(), paid: MoneyOut.nullable(),
  })),
})

/** MỘT DÒNG công nợ PHẢI TRẢ: chứng từ mua + còn nợ + mốc tuổi nợ, tính tới một ngày. */
export const ApDebtRowDto = z.object({
  invoice: ApInvoiceDto,
  paid: z.number(),
  owed: z.number(),
  bucket: z.enum(['future', 'b30', 'b60', 'b90', 'over90']),
})

export const ApDebtGroupDto = z.object({
  vendorId: Uuid,
  vendorName: z.string(),
  vendorCode: z.string().nullable(),
  vendorTax: z.string().nullable(),
  rows: z.array(ApDebtRowDto),
  owedTotal: z.number(),
})

export const ApDebtReportDto = z.object({
  groups: z.array(ApDebtGroupDto),
  buckets: ArAgingDto,
  owedTotal: z.number(),
  /** PHÁT SINH LŨY KẾ — tổng + đã trả mọi chứng từ còn sống, KHÔNG cắt theo ngày chốt (Dashboard ▸ Công nợ). */
  billedTotal: z.number(),
  paidTotal: z.number(),
})

/**
 * ĐỀ NGHỊ THANH TOÁN (F18).
 * ⚠ `amount` là SỐ XIN CHI = phải trả − thuế TNCN giữ lại. `whtAmount` để riêng cho người đọc
 *   thấy vì sao số này nhỏ hơn tổng chứng từ mua.
 */
export const PaymentRequestDto = z.object({
  id: Uuid,
  ref: z.string(),
  docDate: ISODate,
  apInvoiceId: Uuid.nullable(),
  apDocNo: z.string().nullable(),
  statementRef: z.string().nullable(),
  vendorId: Uuid.nullable(),
  vendorName: z.string(),
  payeeAccount: z.string().nullable(),
  payeeBank: z.string().nullable(),
  payeeHolder: z.string().nullable(),
  payeeTax: z.string().nullable(),
  category: z.string().nullable(),
  expenseItem: z.string().nullable(),
  description: z.string(),
  amount: MoneyOut,
  subtotal: MoneyOut.nullable(),
  vatAmount: MoneyOut.nullable(),
  whtAmount: MoneyOut,
  deadline: ISODate.nullable(),
  status: z.enum(['pending', 'approved', 'scheduled', 'paid', 'rejected']),
  submitted: z.boolean(),
  submittedAt: ISOTimestamp.nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: ISOTimestamp.nullable(),
  rejectReason: z.string().nullable(),
  scheduledPayDate: ISODate.nullable(),
  paidAmount: MoneyOut,
  paidDate: ISODate.nullable(),
  /**
   * ⭐ Ngày thực chi của TỪNG đợt, tăng dần, không trùng (`0134`, bản cũ `payDateFilter.ts`).
   * `paidDate` chỉ là ngày ghi nhận CUỐI — lọc/nhóm theo "ngày chi" phải đọc danh sách này, nếu
   * không thì phiếu trả nhiều đợt biến mất khỏi mọi ngày trừ ngày cuối (`docs/PARITY.md` §4 A2).
   */
  payDates: z.array(ISODate),
  payMethod: z.enum(['bank', 'cash', 'offset']).nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  createdBy: z.string().nullable(),
  /** Id người lập (`0158`) — phiếu cũ trống, luật "người lập không tự duyệt" lùi về so `createdBy` (tên). */
  createdById: Uuid.nullable(),
  approverId: Uuid.nullable(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  hasInvoice: z.enum(['yes', 'no']).nullable(),
  invoices: z.array(z.object({
    no: z.string(), date: z.string(), preTax: blankAsAbsent(z.number()), vatRate: blankAsAbsent(z.number()), vatAmount: blankAsAbsent(z.number()),
    amount: blankAsAbsent(z.number()), note: z.string().optional(), objCode: z.string().optional(), objName: z.string().optional(),
  })),
  quantity: z.string().nullable(),
  unitPrice: MoneyOut.nullable(),
  ckContent: z.string().nullable(),
  payeeCode: z.string().nullable(),
  docHandover: z.enum(['pending', 'handed', 'received']),
  handedBy: z.string().nullable(),
  handedAt: ISOTimestamp.nullable(),
  receivedBy: z.string().nullable(),
  receivedAt: ISOTimestamp.nullable(),
  chiefPayOk: z.boolean(),
  /** Chứng từ AMIS lập từ phiếu nhập tay (`0106`). */
  amisDocIds: z.array(Uuid),
  amisDocNos: z.array(z.string()),
  /** Số phiếu sổ phụ / sổ quỹ đã ghi cho phiếu (`0121`). */
  ledgerVoucherNos: z.array(z.string()),
  /** Tiền đã ghi nhận chi mà chưa có dòng sổ — cần "Ghi sổ phụ" bổ sung. */
  ledgerMissing: MoneyOut,
})

/** Bảng định khoản mặc định / đã gửi của phiếu ĐNTT nhập tay (`DnttPostingPreview`). */
export const PostingInputDto = z.object({
  lines: z.array(z.object({
    description: z.string(), debitAcc: z.string(), creditAcc: z.string(), afterTax: z.number(),
    preTax: blankAsAbsent(z.number()), vatRate: blankAsAbsent(z.number()), expenseItem: z.string().optional(), invoiceNo: z.string().optional(),
    invoiceDate: z.string().optional(), objCode: z.string().optional(), objName: z.string().optional(),
  })),
  mgmtOnly: z.boolean(),
  otherJournal: z.boolean(),
  invoiceReceipt: z.enum(['attached', 'not_attached', 'none']),
  accountingDate: z.string().optional(),
  dueDate: z.string().optional(),
})

/** Một chứng từ AMIS của phiếu (`DnttDocLinesCard`). */
export const RequestDocDto = z.object({
  id: Uuid, docNo: z.string(), docDate: z.string(), docType: z.string(), status: z.string(), mgmtOnly: z.boolean(), total: MoneyOut,
  partnerName: z.string().nullable(), partnerTax: z.string().nullable(), note: z.string().nullable(), invoiceReceipt: z.string().nullable(),
  accountingDate: z.string().nullable(), dueDate: z.string().nullable(), version: z.number(),
  lines: z.array(z.object({
    lineNo: z.number(), debitAcc: z.string(), creditAcc: z.string(), amount: MoneyOut, description: z.string().nullable(),
    expenseItem: z.string().nullable(), invoiceNo: z.string().nullable(), invoiceDate: z.string().nullable(), vatRate: z.number().nullable(),
  })),
})

/** Bước nào người đang đăng nhập bấm được — để màn ẩn nút đúng, không đoán theo vai. */
export const PayStepsDto = z.object({
  approve: z.boolean(),
  schedulePay: z.boolean(),
  markPaid: z.boolean(),
})

/**
 * CHỨNG TỪ CHI (F19) — `PC-…` tiền mặt · `UNC-…` chuyển khoản.
 * ⚠ Hai dãy số RIÊNG vì đối chiếu với hai sổ khác nhau (sổ quỹ ↔ sổ phụ ngân hàng).
 */
export const PaymentDocDto = z.object({
  id: Uuid,
  ref: z.string(),
  kind: z.enum(['cash', 'bank', 'loan']),
  docDate: ISODate,
  amount: MoneyOut,
  payeeName: z.string(),
  payeeAccount: z.string().nullable(),
  payeeBank: z.string().nullable(),
  content: z.string().nullable(),
  bankAccountId: Uuid.nullable(),
  status: z.enum(['issued', 'cancelled']),
  note: z.string().nullable(),
  /** Năm ô chỉ có ở `kind === 'loan'` — chi bằng khoản vay (`0065`). */
  lenderCode: z.string().nullable(),
  lenderName: z.string().nullable(),
  loanRef: z.string().nullable(),
  debitAccount: z.string().nullable(),
  creditAccount: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  /* ── Tab Giải ngân của Sổ phụ (`0120`) ── */
  createdBy: z.string().nullable(),
  /** Số phiếu ĐNTT mà chứng từ này chi cho — theo thứ tự dòng. */
  requestRefs: z.array(z.string()),
  /** Kế toán xác nhận đã nạp tệp "Khế ước vay" vào MISA. */
  excelAt: ISOTimestamp.nullable(),
  /** Báo Nợ / phiếu chi TM sổ tiền sinh từ chứng từ (`0121`). */
  ledgerVoucherNos: z.array(z.string()),
})

/**
 * BÁO GIÁ MẪU (`0066`) — khung dựng sẵn cho những tuyến bán đi bán lại.
 * ⚠ `revenues` để `z.array(z.unknown())` đúng như `QuoteDto.revenues`: một khuôn duy nhất cho dòng
 *   doanh thu, khai lại lần hai là hai khuôn sẽ lệch nhau.
 */
/** XIN GIA HẠN NỢ (`0067`) — phiếu mở tạm khoá tín dụng của một khách. */
/** ĐỊNH KHOẢN ĐÃ NHỚ THEO NCC (`0068`) — cặp tài khoản ngầm định của một nhà cung cấp. */
/** HỘP THƯ ĐI (`0071`) — sổ ghi những lá thư đòi nợ đã soạn và đã địa chỉ cho ai. */
/** THEO DÕI PHẢI THU TIỀN CHI HỘ (`0073`) — tiền mình ứng trả hộ khách rồi thu lại. */
/** NHẬT KÝ ĐỒNG BỘ (`0074`) — việc gì đã xảy ra lúc nào, khác trạng thái của từng chứng từ. */
/** KẾ HOẠCH TIỀN TRONG NGÀY (`0075`) — hai ô dự kiến kế toán gõ. */
export const CashPlanDto = z.object({
  id: Uuid,
  planDate: ISODate,
  expIn: MoneyOut,
  expOut: MoneyOut,
  note: z.string().nullable(),
  updatedBy: z.string().nullable(),
  version: z.number(),
})

export const SyncLogDto = z.object({
  id: Uuid,
  direction: z.enum(['push_amis', 'pull_woka']),
  records: z.number(),
  status: z.enum(['success', 'error']),
  detail: z.string(),
  docId: Uuid.nullable(),
  docNo: z.string().nullable(),
  byName: z.string().nullable(),
  createdAt: ISOTimestamp,
})

export const OnBehalfRecvDto = z.object({
  id: Uuid,
  ref: z.string(),
  arInvoiceId: Uuid,
  arDocNo: z.string(),
  invoiceNo: z.string().nullable(),
  docDate: ISODate,
  customerId: Uuid.nullable(),
  customerName: z.string(),
  jobRefs: z.string(),
  amount: MoneyOut,
  status: z.enum(['pending', 'confirmed', 'received']),
  confirmedBy: z.string().nullable(),
  confirmedAt: ISOTimestamp.nullable(),
  postDate: ISODate.nullable(),
  receivedDate: ISODate.nullable(),
  receivedAmount: MoneyOut.nullable(),
  bankRef: z.string().nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
})

export const OutboxLetterDto = z.object({
  id: Uuid,
  kind: z.enum(['debt_confirm', 'debt_statement', 'ap_debt_confirm', 'ap_debt_statement']),
  customerId: Uuid.nullable(),
  vendorId: Uuid.nullable(),
  partyName: z.string(),
  recipients: z.array(z.string()),
  asOf: ISODate,
  subject: z.string(),
  body: z.string(),
  owedTotal: MoneyOut,
  overdueTotal: MoneyOut,
  invoiceCount: z.number(),
  sentAt: ISOTimestamp.nullable(),
  createdAt: ISOTimestamp,
  createdBy: z.string().nullable(),
  version: z.number(),
})

export const VendorPostingDto = z.object({
  id: Uuid,
  vendorId: Uuid,
  vendorName: z.string(),
  debitAcc: z.string().nullable(),
  creditAcc: z.string().nullable(),
  vatAcc: z.string().nullable(),
  vatRate: z.number().nullable(),
  itemCode: z.string().nullable(),
  pinned: z.boolean(),
  version: z.number(),
})

export const CreditExtensionDto = z.object({
  id: Uuid,
  customerId: Uuid,
  customerName: z.string(),
  extensionDays: z.number(),
  reason: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  requestedAt: ISOTimestamp,
  requestedBy: z.string(),
  requestedById: Uuid.nullable(),
  requestedByRole: z.string().nullable(),
  decidedAt: ISOTimestamp.nullable(),
  decidedBy: z.string().nullable(),
  effectiveFrom: ISODate.nullable(),
  effectiveTo: ISODate.nullable(),
  rejectedReason: z.string().nullable(),
  version: z.number(),
})

export const QuoteTemplateDto = z.object({
  id: Uuid,
  name: z.string(),
  kind: z.enum(['outbound', 'inbound', 'internal']),
  pickupAddr: z.string().nullable(),
  dropAddr: z.string().nullable(),
  revenues: z.array(z.unknown()),
  terms: z.string().nullable(),
  active: z.boolean(),
  version: z.number(),
})

/** 📎 Bảng kê của một phiếu ĐNTT — `null` khi phiếu không lập từ bảng kê hoặc bảng kê đã bị xoá. */
export const RequestStatementDto = z.object({ statementId: Uuid.nullable() })

/** 🏦 Một lần giải ngân (vay) trả cho một phiếu ĐNTT — `amount` là phần của riêng phiếu đó trong chứng từ. */
export const RequestLoanPayDto = z.object({
  docId: Uuid,
  ref: z.string(),
  docDate: ISODate,
  amount: MoneyOut,
})

export const PaymentDocLineDto = z.object({
  id: Uuid,
  docId: Uuid,
  requestId: Uuid,
  amount: MoneyOut,
  sortOrder: z.number(),
})

/** Một phiếu ĐNTT nhìn từ màn giải ngân — chỉ phần quyết định phân bổ. */
export const PayableRequestDto = z.object({
  id: Uuid,
  ref: z.string(),
  vendorId: Uuid.nullable(),
  vendorName: z.string(),
  amount: MoneyOut,
  paidAmount: MoneyOut,
  deadline: ISODate.nullable(),
  status: z.string(),
})

/* ══ TRA CỨU ĐƠN CÔNG KHAI (F20) ═════════════════════════════════════════════════════════════ */

/**
 * ⭐ DANH SÁCH CHO PHÉP CỦA TRANG CÔNG KHAI — đối chiếu với `OrderDto` ở trên để thấy cái gì KHÔNG
 * có ở đây: `sellPrice` · `costPriceAt` · `listPriceAt` · `revenues` · `ownerUserId` ·
 * `plannedVendorId` · `note` · `salesNote` · `history` · `version`. Người đọc trang này không đăng
 * nhập; thêm một trường vào đây là một quyết định lộ dữ liệu, không phải một tiện ích.
 */
export const TrackPodDto = z.object({
  id: Uuid,
  fileName: z.string(),
  mime: z.string(),
  bytes: z.number(),
})

export const TrackViewDto = z.object({
  ref: z.string(),
  orderStatus: z.string(),
  cancelled: z.boolean(),
  journey: z.array(z.string()),
  currentStep: z.number(),
  planDate: ISODate,
  pickupAddr: z.string(),
  dropAddr: z.string(),
  location: z.string(),
  trainNo: z.string().nullable(),
  cargo: z.string().nullable(),
  contNo: z.string().nullable(),
  contSize: z.string().nullable(),
  contType: z.string().nullable(),
  legDone: z.number(),
  legTotal: z.number(),
  actualDeliveredAt: ISODate.nullable(),
  pods: z.array(TrackPodDto),
})

/* ══ CHỨNG TỪ AMIS · ĐẨY MISA (F24) ══════════════════════════════════════════════════════════ */

export const AmisDocDto = z.object({
  id: Uuid,
  docNo: z.string(),
  docDate: ISODate,
  docType: z.string(),
  source: z.enum(['ar', 'ap', 'cash', 'offset', 'fuel', 'fuel_alloc', 'depr_alloc', 'dntt', 'manual']),
  sourceRef: z.string(),
  sourceId: Uuid.nullable(),
  status: z.enum(['draft', 'queued', 'pushed', 'rejected']),
  mgmtOnly: z.boolean(),
  subtotal: MoneyOut,
  vatTotal: MoneyOut,
  total: MoneyOut,
  /** MISA tra đối tượng bằng MÃ SỐ THUẾ, không bằng tên. */
  partnerTax: z.string().nullable(),
  partnerName: z.string().nullable(),
  note: z.string().nullable(),
  queuedAt: ISOTimestamp.nullable(),
  pushAttempts: z.number(),
  pushedAt: ISOTimestamp.nullable(),
  misaRef: z.string().nullable(),
  rejectReason: z.string().nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  /** Ngày hạch toán kế toán chọn (`0106`); trống = theo ngày chứng từ. Tệp Excel chứng từ đọc nó. */
  accountingDate: ISODate.nullish(),
  /** Dấu "Đã nạp MISA" — người + giờ (T53). Chỉ sổ chứng từ `/chung-tu` mang; các cửa khác vắng. */
  excelBy: z.string().nullish(),
  excelAt: ISOTimestamp.nullish(),
})

export const AmisDocLineDto = z.object({
  lineNo: z.number(),
  debitAcc: z.string(),
  creditAcc: z.string(),
  amount: MoneyOut,
  partnerTax: z.string().nullable(),
  description: z.string().nullable(),
  /** Ô của mẫu MISA "Mua dịch vụ" 57 cột — hộp chi tiết (tab Hạch toán / Thuế) đọc và sửa được. */
  costObject: z.string().nullable(),
  quantity: z.number().nullable(),
  expenseItem: z.string().nullable(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  vatRate: z.number().nullable(),
})

/** `GET /chung-tu/:id` — chứng từ + dòng + người đánh dấu "Đã nạp Excel" (null = chưa): hộp xoá nhắc xoá bên MISA
 *  (người dùng chốt 17/10 — đã nạp Excel vẫn xoá được, chỉ "đã đẩy" mới chặn). */
export const AmisDocDetailDto = z.object({
  doc: AmisDocDto,
  lines: z.array(AmisDocLineDto),
  excelBy: z.string().nullable(),
  /** Giờ đánh dấu "Đã nạp" — hộp chi tiết hiện "✓ Đã nạp MISA bởi … · ngày" như bản cũ `amis.d.pushedBy` (T53). */
  excelAt: ISOTimestamp.nullish(),
})

/** `POST /chung-tu/xac-nhan-da-nap` — số chứng từ đã đánh dấu + phiếu không đủ điều kiện kèm lý do (T53). */
export const ConfirmLoadedResultDto = z.object({
  marked: z.array(z.string()),
  failed: z.array(z.object({ docNo: z.string(), reason: z.string() })),
})

/* ══ ĐỐI TRỪ CÔNG NỢ · KHÉP KÍN · NHẮC NỢ (F23) ══════════════════════════════════════════════ */

export const DebtOffsetDto = z.object({
  id: Uuid,
  ref: z.string(),
  arInvoiceId: Uuid,
  apInvoiceId: Uuid,
  amount: MoneyOut,
  /** Khách ≠ nhà cung cấp — TỰ SUY từ mã số thuế, người duyệt phải lưu ý. */
  crossParty: z.boolean(),
  debitAccount: z.string(),
  creditAccount: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  note: z.string().nullable(),
  rejectReason: z.string().nullable(),
  createdBy: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: ISOTimestamp.nullable(),
  arPaymentId: Uuid.nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  docDate: z.string(),
  arDocNo: z.string().nullable(),
  apDocNo: z.string().nullable(),
  partnerName: z.string().nullable(),
  apPartnerName: z.string().nullable(),
  amisDocNo: z.string().nullable(),
})

/** Chứng từ mua nhìn từ màn đối trừ — chỉ đủ để chọn, không bày dòng chi tiết. */
export const ApInvoiceRefDto = z.object({
  id: Uuid,
  docNo: z.string(),
  vendorId: Uuid,
  vendorName: z.string(),
  vendorTax: z.string().nullable(),
  docDate: ISODate,
  dueDate: ISODate.nullable(),
  total: MoneyOut,
  /** Thuế TNCN giữ lại — trần đối trừ là số thực chi còn lại `total − whtTotal − paidAmount` (`apRemainToPay`). */
  whtTotal: MoneyOut,
  paidAmount: MoneyOut,
  status: z.string(),
})

export const ClosedLoopRowDto = z.object({
  jobId: Uuid.nullable(),
  jobRef: z.string(),
  /** Ngày kế hoạch của đơn (dòng hoá đơn lẻ: ngày chứng từ). */
  date: z.string(),
  customerName: z.string(),
  /** Mọi NCC của đơn, nối `·`; rỗng khi đơn chưa có chứng từ mua. */
  vendorName: z.string(),
  revenue: MoneyOut,
  cost: MoneyOut,
  grossMargin: MoneyOut,
  /** `null` = CHƯA CÓ doanh thu. Khác hẳn 0%. */
  marginPct: z.number().nullable(),
  collected: MoneyOut,
  paid: MoneyOut,
  /** Số khách phải trả (GỒM VAT và thu hộ) và phần CÒN THU = `billed − collected`, cùng một gốc. */
  billed: MoneyOut,
  outstanding: MoneyOut,
  /** Số phải trả NCC (GỒM VAT) và phần CÒN TRẢ = `apBilled − paid`. */
  apBilled: MoneyOut,
  apOutstanding: MoneyOut,
  /** `null` = đơn chưa có chứng từ vế đó. */
  arStatus: z.enum(['paid', 'partial', 'issued']).nullable(),
  apStatus: z.enum(['paid', 'partial', 'unpaid']).nullable(),
})

export const ClosedLoopReportDto = z.object({
  from: ISODate,
  to: ISODate,
  basis: z.enum(['pickup', 'invoice']),
  rows: z.array(ClosedLoopRowDto),
  totals: z.object({
    revenue: MoneyOut,
    cost: MoneyOut,
    grossMargin: MoneyOut,
    marginPct: z.number().nullable(),
    collected: MoneyOut,
    paid: MoneyOut,
    billed: MoneyOut,
    outstanding: MoneyOut,
    apOutstanding: MoneyOut,
    netCash: MoneyOut,
    /** Còn phải thu − còn phải trả. */
    netDebt: MoneyOut,
    rowCount: z.number(),
  }),
})

export const DunningLineDto = z.object({
  docNo: z.string(),
  invoiceNo: z.string().nullable(),
  docDate: ISODate,
  dueDate: ISODate.nullable(),
  owed: MoneyOut,
  daysOverdue: z.number(),
})

/** Sổ chi tiết công nợ 131 của một khách (`0109`). */
export const Ledger131Dto = z.object({
  rows: z.array(z.object({ date: z.string(), docNo: z.string(), desc: z.string(), debitVnd: z.number(), creditVnd: z.number(), balanceVnd: z.number() })),
  debitVnd: z.number(),
  creditVnd: z.number(),
})

export const OpeningImportResultDto = z.object({ docNos: z.array(z.string()), totalVnd: z.number() })

/** Email sale phụ trách — thư nhắc nợ gửi RIÊNG từng sale (`0107`). */
export const SaleMailDto = z.object({ id: Uuid, name: z.string(), email: z.string() })

/** Kết quả điền bù hạn thanh toán cho hóa đơn trống hạn. */
export const FillDueResultDto = z.object({ filled: z.number(), skipped: z.number(), skippedCustomers: z.array(z.string()) })

export const DunningLetterDto = z.object({
  customerId: Uuid,
  customerName: z.string(),
  lines: z.array(DunningLineDto),
  owedTotal: MoneyOut,
  overdueTotal: MoneyOut,
  worstDays: z.number(),
  subject: z.string(),
  body: z.string(),
})

/* ══ SỔ PHỤ NGÂN HÀNG · QUỸ TIỀN MẶT (F22) ═══════════════════════════════════════════════════ */

export const MoneyAccountDto = z.object({
  id: Uuid,
  accountNo: z.string(),
  bankName: z.string(),
  shortName: z.string().nullable(),
  currency: z.string(),
  /** `bank` = tài khoản ngân hàng · `cash` = quỹ tiền mặt. Một kiểu vì cùng phép tính số dư. */
  kind: z.enum(['bank', 'cash']),
  openingBalance: MoneyOut,
  openingDate: ISODate.nullable(),
  sortOrder: z.number(),
  active: z.boolean(),
})

export const BankTxnDto = z.object({
  id: Uuid,
  accountId: Uuid,
  txnDate: ISODate,
  postingDate: ISODate.nullable(),
  type: z.enum(['thu', 'chi']),
  amount: MoneyOut,
  description: z.string(),
  counterparty: z.string().nullable(),
  objectCode: z.string().nullable(),
  contraAccount: z.string().nullable(),
  cat: z.enum(['thu_cuoc', 'thu_khac', 'chi_cuoc', 'chi_khac', 'noi_bo']).nullable(),
  voucherNo: z.string().nullable(),
  arInvoiceId: Uuid.nullable(),
  apInvoiceId: Uuid.nullable(),
  arPaymentId: Uuid.nullable(),
  /**
   * ⭐ MỘT DÒNG TIỀN CẤN NHIỀU HOÁ ĐƠN — cách chia tiền. Rỗng khi dòng chỉ cấn một hoá đơn; lúc đó
   * `arInvoiceId` mới là chỗ ghi. Hai chỗ không bao giờ cùng có giá trị.
   */
  arAllocs: z.array(z.object({ invoiceId: Uuid, docNo: z.string().nullable(), amount: MoneyOut })),
  /** ⭐ Phần dư ĐÃ THÀNH CỌC của khách (F15-3, `0162`) — có thì dòng ĐÃ GẮN ĐỦ: số tiền = tổng cấn + cọc. */
  arAdvance: z.object({ id: Uuid, amount: MoneyOut }).nullable(),
  matchedAt: ISOTimestamp.nullable(),
  matchNote: z.string().nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  /* ── Các ô AMIS + hóa đơn GTGT (`0082`) ── */
  payeeName: z.string().nullable(),
  partyAddress: z.string().nullable(),
  staffCode: z.string().nullable(),
  attachedDocs: z.number().nullable(),
  expenseItem: z.string().nullable(),
  jobRef: z.string().nullable(),
  payKind: z.enum(['invoice', 'bank', 'other']).nullable(),
  hasInvoice: z.boolean(),
  vatRate: z.number().nullable(),
  vatAmount: MoneyOut.nullable(),
  acctVat: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  invoiceNo: z.string().nullable(),
  goodsGroup: z.string().nullable(),
  amisReason: z.string().nullable(),
  amisEntryDesc: z.string().nullable(),
  mgmtOnly: z.boolean(),
  /* ── Cột hiển thị của 7 tab Sổ phụ (`0120`) ── */
  createdBy: z.string().nullable(),
  pairTxnId: Uuid.nullable(),
  /** ✍ Kế toán trưởng ký điện tử (`0137`) — bản in phiếu hiện chữ ký ở ô Kế toán trưởng. */
  chiefSignedBy: z.string().nullable(),
  chiefSignedAt: ISOTimestamp.nullable(),
  /** Kế toán xác nhận đã nạp tệp Excel vào MISA — KHÔNG phải cờ "đã đẩy API". */
  excelAt: ISOTimestamp.nullable(),
  excelBy: z.string().nullable(),
  /** Chứng từ kế toán của phiếu còn sống — `null` = chưa có CT (bộ lọc AMIS theo chứng từ của Sổ phụ). */
  amisDocStatus: z.enum(['draft', 'queued', 'pushed']).nullable(),
  matchedDocNo: z.string().nullable(),
  matchedDocCancelled: z.boolean(),
  /** Số chứng từ kế toán đã ghi sổ cho dòng (CT của chính phiếu, hoặc của phiếu ĐNTT nguồn) — `null` = chưa có. */
  amisDocNo: z.string().nullable(),
  /** Id của chứng từ đó — link `/chung-tu?open=<id>` (T15). */
  amisDocId: Uuid.nullable(),
  /* ── Nguồn CHI ĐNTT (`0121`) ── */
  paymentDocId: Uuid.nullable(),
  paymentRequestId: Uuid.nullable(),
  /** `UNC-… · DNTT-…` hoặc `DNTT-…`. */
  paySourceRef: z.string().nullable(),
})

export const AccountSummaryDto = z.object({
  account: MoneyAccountDto,
  openingBalance: MoneyOut,
  inflow: MoneyOut,
  outflow: MoneyOut,
  closingBalance: MoneyOut,
})

/* ══ TỆP EXCEL NẠP MISA (`0080`) ═══════════════════════════════════════════════════════════════
 * ⚠ Ô SỐ GIỮ DẠNG CHUỖI (`{ t: 'n', v: '10000000.00' }`) — tiền đi qua JSON ở dạng thập phân (§2)
 *   và chỉ thành số thực ĐÚNG MỘT LẦN, lúc ghi ô Excel ở trình duyệt.
 */
const MisaCellDto = z.object({ t: z.enum(['s', 'n']), v: z.string() })

export const MisaSheetDto = z.object({
  sheetName: z.string(),
  fileStem: z.string(),
  guide: z.array(z.string()),
  /** Khoá là CHỈ SỐ CỘT dạng chuỗi — JSON không có khoá số. */
  groupNotes: z.record(z.string(), z.string()),
  columns: z.array(z.string()),
  rows: z.array(z.array(MisaCellDto)),
  gaps: z.array(z.string()),
  sourceIds: z.array(Uuid),
  /** Trong `sourceIds`, những chứng từ ĐÃ được đánh dấu "đã nạp Excel". */
  markedIds: z.array(Uuid),
  /** Trình bày tệp riêng của từng mẫu bản cũ (bề rộng cột · cao dòng tiêu đề / ghi chú · viền) — vắng = mặc định. */
  layout: z.object({
    colWidths: z.array(z.number()).optional(),
    headerHeight: z.optional(z.number()),
    noteHeight: z.optional(z.number()),
    noteItalic: z.boolean().optional(),
    headerBorder: z.boolean().optional(),
  }).optional(),
})

export const ExcelMarkResultDto = z.object({ changed: z.number() })

/** Một dòng bảng chọn của mẫu "Danh mục đối tượng". KHÔNG mang số tài khoản ngân hàng — chỉ tệp có. */
export const MisaObjectItemDto = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  taxCode: z.string(),
  address: z.string(),
  paymentTermDays: z.number(),
  isCustomer: z.boolean(),
  isVendor: z.boolean(),
  merged: z.boolean(),
  createdAt: ISODate.nullable(),
})

export const MisaObjectsDto = z.object({
  objects: z.array(MisaObjectItemDto),
  noDate: z.number(),
  customers: MisaSheetDto,
  vendors: MisaSheetDto,
})

/* ══ RÀ SOÁT SỔ TIỀN · TỰ VÁ · PHIẾU ĐÔI (`0079`) ═══════════════════════════════════════════
 *
 * ⚠ KHÔNG CÓ `msg`. Bộ rà dựng sẵn một câu tiếng Việt cho nhật ký phía máy chủ, nhưng cửa REST CẮT
 *   nó đi: màn phải dựng câu từ `code` + `params` để người xem tiếng Anh/Trung đọc được (§9). Khai
 *   `msg` ở đây là mời người sau đem câu tiếng Việt thẳng lên màn.
 * ⚠ `params` để THÔ — tiền là chuỗi thập phân, ngày là ISO. Màn định dạng theo ngôn ngữ đang xem.
 */
const IssueParamsDto = z.record(z.string(), z.union([z.string(), z.number()]))

export const AuditIssueDto = z.object({
  level: z.enum(['error', 'warn']),
  kind: z.string(),
  code: z.string(),
  params: IssueParamsDto,
  /* `.nullable()`, KHÔNG `.optional()`: hợp đồng đầu ra LUÔN có khoá, vắng thì `null` — đúng khuôn
     cả tệp này, và cổng `blank-field` bắt `.optional()` trần trên ngày/mã định danh. */
  ref: z.string().nullable(),
  date: ISODate.nullable(),
  amount: MoneyOut.nullable(),
  txnId: Uuid.nullable(),
})

export const LedgerReportDto = z.object({
  issues: z.array(AuditIssueDto),
  summary: z.object({
    count: z.number(), errors: z.number(), warns: z.number(),
    byKind: z.record(z.string(), z.number()),
  }),
  /** Bao nhiêu chỗ máy tự sửa được — quyết định có bày nút "tự vá" hay không. */
  healable: z.number(),
  /** Bộ rà mới đọc một phần sổ (chạm trần) — màn phải nói thật chuyện đó. */
  capped: z.boolean(),
  scanCap: z.number(),
})

export const PairIssueDto = z.object({
  level: z.enum(['error', 'warn']),
  kind: z.string(),
  code: z.string(),
  params: IssueParamsDto,
  txnId: Uuid,
  pairId: Uuid.nullable(),
  voucherNo: z.string(),
  date: ISODate,
  amount: MoneyOut,
})

export const PairReportDto = z.object({
  issues: z.array(PairIssueDto),
  errors: z.number(),
  warns: z.number(),
  paired: z.number(),
  capped: z.boolean(),
  scanCap: z.number(),
})

export const HealAppliedDto = z.object({
  arInvoices: z.number(),
  apInvoices: z.number(),
  unmatched: z.number(),
  /** Tổng tiền "đã thu/đã trả" bị kẹp về — ĐỘ LỚN của chỗ lệch, không chỉ số chỗ. */
  capped: MoneyOut,
  /** Nhật ký từng miếng vá: đã sửa gì, vì sao. */
  log: z.array(z.string()),
})

export const OpeningIssueDto = z.object({
  accountId: Uuid,
  name: z.string(),
  count: z.number(),
  openingDate: ISODate,
  netAmount: MoneyOut,
  /** Mốc ghi ở TƯƠNG LAI — phép tính đã bỏ qua mốc, chỉ nhắc kế toán sửa ngày. */
  future: z.boolean(),
  firstTxnDate: ISODate.nullable(),
})

export const LedgerSummaryDto = z.object({
  accounts: z.array(AccountSummaryDto),
  issues: z.array(OpeningIssueDto),
  bankClosing: MoneyOut,
  cashClosing: MoneyOut,
  totalClosing: MoneyOut,
})

export const CashFlowReportDto = z.object({
  byCat: z.record(z.string(), MoneyOut),
  /** KHÔNG gồm chuyển nội bộ. */
  inflowTotal: MoneyOut,
  outflowTotal: MoneyOut,
  netFlow: MoneyOut,
  internalTotal: MoneyOut,
  txnCount: z.number(),
})

export const ForecastLineDto = z.object({
  invoiceId: Uuid,
  docNo: z.string(),
  vendorName: z.string(),
  dueDate: ISODate.nullable(),
  owed: MoneyOut,
  overdue: z.boolean(),
})

export const ForecastDto = z.object({
  asOf: ISODate,
  onHand: MoneyOut,
  lines: z.array(ForecastLineDto),
  dueTotal: MoneyOut,
  /** ÂM là báo động, KHÔNG phải lỗi — xem `@rs/bank.forecast`. */
  afterPaying: MoneyOut,
})

/**
 * KHOÁ SỔ — một bản ghi khoá. Bản cũ (`proship-trans` ▸ `LedgerLock`) bày CẢ BA thứ dưới đây ngay
 * trên thẻ trạng thái: khoá lúc nào · ai khoá · ghi chú kỳ. Trả thiếu chúng là buộc màn hiện một
 * ngày trần trụi, không nói được ai chốt sổ — mà đó chính là thứ kế toán cần khi tra lại.
 */
export const LedgerLockDto = z.object({
  id: Uuid,
  lockDate: ISODate,
  lockedAt: z.string(),
  /** `null` khi người khoá đã bị xoá mềm khỏi danh sách người dùng. */
  lockedBy: z.string().nullable(),
  note: z.string().nullable(),
})

/**
 * MỘT DÒNG LỊCH SỬ khoá/mở sổ. `0006_ledger_lock.sql` GIỮ mọi bản ghi (mở khoá chỉ đặt
 * `unlocked = true`; `DELETE` đã bị REVOKE) — dòng đã mở vẫn là chứng cứ, nên nó vẫn ra cửa này.
 */
export const LedgerLockHistoryDto = LedgerLockDto.extend({
  unlocked: z.boolean(),
  unlockedAt: z.string().nullable(),
  unlockedBy: z.string().nullable(),
})

export const LegacyImportResultDto = z.object({
  inserted: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  statusKept: z.number().int(),
  ownerCleared: z.number().int(),
  errors: z.array(z.object({ index: z.number().int(), ref: z.string(), message: z.string() })),
})

export const ReserveRefsResultDto = z.object({ added: z.number().int(), existing: z.number().int() })

export const SetLegacyStatusResultDto = z.object({
  changed: z.number().int(),
  skipped: z.array(z.object({ jobId: Uuid, ref: z.string(), reason: z.string() })),
})

/** Hình dạng MỘT TRANG — mọi cửa danh sách trả về dạng này từ đợt N2 (§8.3). */
export interface PageOf<T> {
  rows: T[]
  /** `null` nghĩa là hết dữ liệu. */
  nextCursor: string | null
  /** Con trỏ lùi — `null` là đang ở trang đầu. Chỉ cửa đã chuyển sang cửa sổ hai chiều mới có. */
  prevCursor?: string | null
  /** Tổng số dòng của TOÀN BỘ tập đang lọc — nuôi "Hiện 21–40 / 137" và "Trang 2/7". */
  total?: number | null
}

/* ══ GIÁ ĐƯỜNG SẮT · ĐỐI SOÁT (F25) ══════════════════════════════════════════════════════════ */

/**
 * MỤC GIÁ CÔNG BỐ NHẬP TAY (🧮 Giá cước vendor).
 * `ratio` là HỆ SỐ nhân, không phải tiền — nên là số, không phải `MoneyOut`.
 */
export const PriceCatDto = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  ratio: z.number(),
  roundTo: MoneyOut.nullable(),
  effectiveFrom: ISODate.nullable(),
  packId: Uuid.nullable(),
  sortOrder: z.number(),
  active: z.boolean(),
  version: z.number(),
})

/** Một dòng giá trong mục. ⭐ `sell` được TÍNH RA, không lưu — xem `@rs/pricing` ▸ `sellOf`. */
export const PriceCatRowDto = z.object({
  id: Uuid,
  catId: Uuid,
  label: z.string(),
  fromPlace: z.string().nullable(),
  toPlace: z.string().nullable(),
  contType: z.string().nullable(),
  vendorId: Uuid.nullable(),
  buy: MoneyOut,
  /** `null` = theo tỷ lệ của MỤC. Khác hẳn `0`. */
  ratio: z.number().nullable(),
  sell: MoneyOut,
  note: z.string().nullable(),
  sortOrder: z.number(),
  version: z.number(),
})

/** Một GIAI ĐOẠN đơn giá phân bổ (dầu hoặc khấu hao vỏ lạnh). */
export const AllocPriceDto = z.object({
  id: Uuid,
  kind: z.enum(['fuel', 'depreciation']),
  effectiveFrom: ISODate,
  /** `null` = CÒN HIỆU LỰC. */
  effectiveTo: ISODate.nullable(),
  price: MoneyOut,
  unit: z.string().nullable(),
  /** `null` = áp cho MỌI loại cont. */
  contType: z.string().nullable(),
  note: z.string().nullable(),
  active: z.boolean(),
  version: z.number(),
})

/* ══ GIÁ CHO FORM ĐƠN HÀNG ═══════════════════════════════════════════════════════════════════════ */

/**
 * 💸 PHIẾU ĐNTT chạm tới khoản chi phí của MỘT đơn (`0127`) — nguồn của hộp "Chi phiếu ĐNTT" ở màn đơn.
 * ⚠ Số tiền phiếu ĐNTT của một đơn CHÍNH LÀ giá vốn đơn đó ⇒ cửa trả nó khai `costFields`.
 */
export const JobPayRequestDto = z.object({
  id: Uuid,
  ref: z.string(),
  status: z.string(),
  amount: MoneyOut,
  paidAmount: MoneyOut,
  /** Số uỷ nhiệm chi gần nhất đã gắn với phiếu; `null` khi chưa chi. */
  uncRef: z.string().nullable(),
  version: z.number(),
})

export const OrderPriceRowDto = z.object({
  listPriceId: Uuid,
  vendorId: Uuid.nullable(),
  fromPlace: z.string(),
  toPlace: z.string(),
  contType: z.string().nullable(),
  packId: Uuid.nullable(),
  listPrice: MoneyOut,
  /** `null` với người không được xem giá vốn. */
  costPrice: MoneyOut.nullable(),
})

export const OrderPriceLookupDto = z.object({
  onDate: ISODate,
  rows: z.array(OrderPriceRowDto),
  combo: z.object({
    lines: z.array(z.object({ label: z.string(), sellPrice: MoneyOut, costPrice: MoneyOut.nullable(), note: z.string().nullable() })),
    sellTotal: MoneyOut,
    costTotal: MoneyOut.nullable(),
    missing: z.array(z.string()),
  }).nullable(),
})

export const OrderAllocLookupDto = z.object({
  fuel: AllocPriceDto.nullable(),
  depreciation: AllocPriceDto.nullable(),
  deprRouteOk: z.boolean(),
})

/** ⭐ `overlaps` là CẢNH BÁO, không phải lỗi — xem `@rs/pricing` ▸ `allocOverlaps`. */
export const AllocBoardDto = z.object({
  kind: z.enum(['fuel', 'depreciation']),
  rows: z.array(AllocPriceDto),
  overlaps: z.array(z.tuple([Uuid, Uuid])),
})

/** Soát chồng kỳ trước khi lưu giá phân bổ: kỳ cũ bị chồng · đơn đã điền số của các kỳ đó · ngày khép kỳ cũ. */
export const AllocUsedOrderDto = z.object({ id: Uuid, ref: z.string(), planDate: ISODate, amount: MoneyOut, version: z.number().int() })
export const AllocClashDto = z.object({
  clashes: z.array(AllocPriceDto),
  used: z.array(AllocUsedOrderDto),
  closeTo: ISODate,
})

/** Giá dầu DO của MỘT ngày. */
export const FuelPriceDto = z.object({
  id: Uuid,
  onDate: ISODate,
  price: MoneyOut,
  note: z.string().nullable(),
  version: z.number(),
})

/** Một kỳ báo cáo tài chính đã nhập. `values` = mã chỉ tiêu → số tiền (chuỗi thập phân, §2). */
export const BctcPeriodDto = z.object({
  id: Uuid,
  period: z.string(),
  values: z.record(z.string(), MoneyOut),
  fileName: z.string().nullable(),
  importedBy: z.string().nullable(),
  importedAt: ISOTimestamp,
  version: z.number(),
})

export const BctcTargetDto = z.object({
  id: Uuid,
  year: z.number(),
  code: z.string(),
  targetValue: MoneyOut,
  version: z.number(),
})

/** ⭐ CẢ 12 THÁNG trả về thô — Tháng · Quý · Luỹ kế gộp lại ở màn từ cùng 12 con số đó. */
export const BctcYearDto = z.object({
  year: z.number(),
  periods: z.array(BctcPeriodDto),
  targets: z.array(BctcTargetDto),
  years: z.array(z.number()),
})

/** Một ô của lưới KH-BC TH — chỉ tiêu người dùng gõ. */
export const DimTargetDto = z.object({
  id: Uuid,
  year: z.number(),
  month: z.number(),
  dim: z.enum(['pack', 'route', 'cont']),
  key: z.string(),
  keyLabel: z.string(),
  targetValue: MoneyOut,
  note: z.string().nullable(),
  version: z.number(),
})

/** Sản lượng năm trước, nhập tay. SỐ LƯỢNG đơn/cont — không phải tiền. */
export const PrevQtyDto = z.object({
  id: Uuid,
  year: z.number(),
  month: z.number(),
  dim: z.enum(['pack', 'route', 'cont']),
  key: z.string(),
  keyLabel: z.string(),
  qty: z.number(),
  version: z.number(),
})

/** 📌 Thực hiện NHẬP TAY tháng cũ (`0180`) — đè số tự tính trên lưới và mọi báo cáo KH-BC TH. */
export const ActualOverrideDto = z.object({
  id: Uuid,
  year: z.number(),
  month: z.number(),
  dim: z.enum(['pack', 'route', 'cont']),
  key: z.string(),
  keyLabel: z.string(),
  amount: MoneyOut,
  version: z.number(),
})

/** ⭐ Cả lưới trong MỘT lần đọc. `actuals` được TÍNH RA từ đơn thật, không lưu. */
export const KhthGridDto = z.object({
  year: z.number(),
  dim: z.enum(['pack', 'route', 'cont']),
  targets: z.array(DimTargetDto),
  prevQty: z.array(PrevQtyDto),
  actuals: z.array(z.object({
    month: z.number(), key: z.string(),
    revenue: MoneyOut, margin: MoneyOut, orders: z.number(),
  })),
  overrides: z.array(ActualOverrideDto),
})

/** ⭐ Dữ liệu mọi slide KH-BC TH: chỉ tiêu năm nay + năm sau · sản lượng năm trước · thực hiện theo 3 cơ sở ngày × gói × tuyến × loại cont. */
export const KhthReportDto = z.object({
  year: z.number(),
  targets: z.array(DimTargetDto),
  prevQty: z.array(PrevQtyDto),
  facts: z.array(z.object({
    basis: z.enum(['plan', 'actual', 'invoice']), year: z.number(), month: z.number(),
    packId: z.string(), packName: z.string(), route: z.string(), cont: z.enum(['rf', 'dry', 'none']),
    revenue: MoneyOut, margin: MoneyOut, orders: z.number(),
  })),
  arTotals: z.object({ invoiced: MoneyOut, collected: MoneyOut }),
  /** 📌 Thực hiện nhập tay theo gói, năm trước + năm nay — đè doanh thu gói/tháng của mọi slide. */
  overrides: z.array(ActualOverrideDto),
})

export const RateBatchDto = z.object({
  id: Uuid,
  ref: z.string(),
  vendorId: Uuid,
  effectiveFrom: ISODate,
  rowCount: z.number(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: ISOTimestamp,
})

export const RateHitDto = z.object({
  id: Uuid,
  fromPlace: z.string(),
  toPlace: z.string(),
  contType: z.string().nullable(),
  priceFull: MoneyOut,
  priceHalf: MoneyOut.nullable(),
  effectiveFrom: ISODate,
})

/**
 * MỘT DÒNG GIÁ VỐN — nguyên liệu của tab 📊 Phân tích giá vốn (`/az/ops-catalog`).
 * ⚠ `fuelPrice` là giá dầu DO tại KỲ của dòng giá này. Bản cũ giữ giá dầu trong một kho riêng
 *   (`az-fuels`); rs-erp gắn thẳng lên dòng giá (`cost_rate.fuel_price`) nên không cần kho đó.
 */
/**
 * ══ NGOẠI LỆ GIÁ — hai loại, KHÔNG gộp ═══════════════════════════════════════════════════════
 *
 * ⚠ Hai bảng này trả lời hai câu hỏi khác nhau, nên chúng là hai hình dạng chứ không phải một
 *   bảng có cờ phân loại:
 *     · `RailPriceExDto`        — ghim giá cho MỘT ĐIỂM lấy/giao cụ thể (ga + địa chỉ). Dùng khi
 *       phép dò giá đường ngắn theo phường/xã lấy nhầm giá của điểm khác trong cùng tỉnh.
 *     · `RailLegCustomerExDto`  — ghim giá chặng Ga→Ga cho MỘT KHÁCH. Cùng tuyến, cùng loại cont,
 *       nhưng khách này có giá riêng đã thoả thuận.
 *
 * ⚠ Các cột `*Key` là bản CHUẨN HOÁ của tên (bỏ dấu, bỏ hoa thường) mà máy chủ dùng để dò. Chúng
 *   nằm trong hợp đồng vì máy chủ THẬT SỰ trả về — cổng `dto-parity` chạy `.strict()`, thừa cũng
 *   đỏ như thiếu.
 */
export const RailPriceExDto = z.object({
  id: Uuid,
  vendorId: Uuid.nullable(),
  station: z.string(),
  stationKey: z.string(),
  province: z.string().nullable(),
  ward: z.string().nullable(),
  wardKey: z.string(),
  addr: z.string(),
  addrKey: z.string(),
  amount: MoneyOut,
  effectiveFrom: ISODate,
  active: z.boolean(),
  jobRef: z.string().nullable(),
  byName: z.string().nullable(),
  byRole: z.string().nullable(),
  note: z.string().nullable(),
  basedOnId: Uuid.nullable(),
  version: z.number(),
})

export const RailLegCustomerExDto = z.object({
  id: Uuid,
  vendorId: Uuid.nullable(),
  customerId: Uuid,
  fromStation: z.string(),
  fromStationKey: z.string(),
  toStation: z.string(),
  toStationKey: z.string(),
  cont: z.string(),
  amount: MoneyOut,
  effectiveFrom: ISODate,
  active: z.boolean(),
  jobRef: z.string().nullable(),
  byName: z.string().nullable(),
  byRole: z.string().nullable(),
  note: z.string().nullable(),
  version: z.number(),
})

export const CostRateDto = z.object({
  id: Uuid,
  vendorId: Uuid,
  packCode: z.string().nullable(),
  packId: Uuid.nullable(),
  fromPlace: z.string(),
  toPlace: z.string(),
  contType: z.string().nullable(),
  note: z.string().nullable(),
  priceFull: MoneyOut,
  priceHalf: MoneyOut.nullable(),
  fuelPrice: MoneyOut.nullable(),
  effectiveFrom: ISODate,
  /** Giờ chạy Ga–Ga — in lên tờ bảng giá công bố. */
  transitHours: z.number().nullable(),
  /** Phường/xã của điểm lấy/giao (xe đường ngắn). */
  ward: z.string().nullable(),
  /** Phường/xã thứ hai — một mức giá phủ hai phường/xã (sáp nhập, tên cũ/mới). */
  ward2: z.string().nullable(),
  /** Nhóm khai lúc nhập phiên (`0088`); `null` = phiên cũ, màn tự đoán theo loại cont / tên ga. */
  group: z.enum(['gaga', 'rf', 'short', 'sur']).nullable(),
  version: z.number(),
  /** Thứ tự ghi vào = thứ tự dòng tệp nhập (`0173`, T31) — màn sắp tuyến theo số nhỏ nhất của tuyến như bản cũ. */
  sortOrder: z.number().nullable(),
})

export const ImportRateResultDto = z.object({
  id: Uuid,
  ref: z.string(),
  added: z.number(),
  /** Dòng KHÔNG đọc được, kèm số thứ tự — người nhập sửa đúng dòng đó rồi dán lại. */
  skipped: z.array(z.object({ lineNo: z.number(), text: z.string() })),
})

export const RailStatementDto = z.object({
  id: Uuid,
  ref: z.string(),
  vendorId: Uuid,
  periodFrom: ISODate,
  periodTo: ISODate,
  status: z.enum(['draft', 'reviewed']),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: ISOTimestamp.nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
})

const RailDiffDto = z.object({ field: z.string(), ours: z.string(), theirs: z.string() })

/**
 * ⭐ MỘT KHOẢN SẼ ĐỔI nếu người soát chấp nhận số của nhà vận chuyển.
 * ⚠ `costId === null` ⇒ KHÔNG đổi được và `blocked` nói vì sao. Vẫn trả ra chứ không lọc bỏ: một
 *   chặng lệch mà bị giấu đi trông y hệt một chặng khớp.
 */
/** Năm ô tiền một dòng đối soát: xếp · đường sắt · dỡ · cẩu + phát sinh · tổng. */
const RailAmountsDto = z.object({ load: MoneyOut, rail: MoneyOut, drop: MoneyOut, extra: MoneyOut, total: MoneyOut })

const RailPatchDto = z.object({
  costId: Uuid.nullable(),
  field: z.enum(['load', 'rail', 'drop', 'extra', 'supp']),
  from: MoneyOut,
  to: MoneyOut,
  blocked: z.string().nullable(),
})

/**
 * ⭐ MỘT DÒNG ĐỐI SOÁT. `status` là kết luận của máy, `matchBy` là LÝ DO — người soát phải kiểm
 * được vì sao máy dò ra chuyến đó, nếu không thì cả bảng chỉ là màu xanh không ai tin.
 */
export const RailReconRowDto = z.object({
  rowNo: z.number(),
  contNo: z.string(),
  status: z.enum(['ok', 'diff', 'missing', 'dup']),
  jobId: Uuid.nullable(),
  jobRef: z.string().nullable(),
  /** Khớp nhờ dấu hiệu gì; dòng `dup` mang LÝ DO không chắc. */
  matchBy: z.string().nullable(),
  /** Người soát đã CHỌN TAY đơn cho dòng này (`picked_job_ref`) — màn cho gỡ. */
  picked: z.boolean(),
  moneyDiffs: z.array(RailDiffDto),
  infoDiffs: z.array(RailDiffDto),
  costIds: z.array(Uuid),
  /** Khách của đơn dò ra — `null` khi chưa dò ra đơn. */
  customerId: Uuid.nullable(),
  /** Mã bảng kê MUA mà khoản của đơn đã nằm trong. Rỗng = chưa lập — màn báo để khỏi gom trùng. */
  stmtRefs: z.array(z.string()),
  locked: z.array(z.string()),
  candidates: z.array(z.string()),
  patches: z.array(RailPatchDto),
  /** Tiền từng ô hai bên — 5 cột so tiền, và màn tự cộng lại tổng khi bỏ dòng khỏi phiên. `ours` null = chưa dò ra đơn. */
  theirs: RailAmountsDto,
  ours: RailAmountsDto.nullable(),
})

export const RailReconSummaryDto = z.object({
  ok: z.number(),
  diff: z.number(),
  missing: z.number(),
  dup: z.number(),
  theirTotal: MoneyOut,
  rowCount: z.number(),
})

export const RailReconResultDto = z.object({
  statement: RailStatementDto,
  rows: z.array(RailReconRowDto),
  summary: RailReconSummaryDto,
})

/** Kết quả LẤY SỐ NCC: bảng đã soát lại, số khoản ghi được, và từng khoản bỏ qua kèm lý do. */
export const RailApplyResultDto = z.object({
  result: RailReconResultDto,
  applied: z.number(),
  skipped: z.array(z.object({ rowNo: z.number(), reason: z.string() })),
})

/* ══ THỐNG KÊ · HIỆU QUẢ NHÀ THẦU · DASHBOARD (F26) ══════════════════════════════════════════ */

/** Bốn con số dùng lại ở MỌI bảng của báo cáo — khai một lần để bốn bảng không lệch nhau. */
const MarginDto = {
  revenue: MoneyOut,
  cost: MoneyOut,
  grossMargin: MoneyOut,
  /** `null` khi chưa có doanh thu — "chưa biết" khác "bằng 0". */
  marginPct: z.number().nullable(),
}

export const StatsOverviewDto = z.object({
  ...MarginDto,
  jobCount: z.number(),
  cancelledCount: z.number(),
  noRevenueCount: z.number(),
  /** Dư nợ phải thu HÔM NAY của khách trong phạm vi xem — ảnh chụp hiện tại, không theo kỳ (rà 16/10 T50). */
  arOutstanding: MoneyOut,
  /** Phần quá hạn (hạn trước HÔM NAY) trong `arOutstanding`. */
  arOverdue: MoneyOut,
  apOutstanding: MoneyOut,
  netWorkingCapital: MoneyOut,
})

/** Tổng hợp kỳ cho sale — số khách · khách mới kèm tên (`Az.tsx:17385`). */
export const StatsSummaryDto = z.object({
  custCount: z.number(),
  newCustCount: z.number(),
  newCustomers: z.array(z.string()),
})

/** Một ô sale × tháng của bảng "Doanh số sale qua các tháng". */
export const StatsSaleMonthDto = z.object({
  ownerUserId: Uuid.nullable(),
  ownerName: z.string(),
  month: z.string(),
  jobCount: z.number(),
  revenue: MoneyOut,
  grossMargin: MoneyOut,
})

export const StatsMonthDto = z.object({
  ...MarginDto,
  /** `YYYY-MM`. Dải LIÊN TỤC: tháng không có đơn nào vẫn có một điểm bằng 0. */
  month: z.string(),
  jobCount: z.number(),
})

export const StatsSaleDto = z.object({
  ...MarginDto,
  ownerUserId: Uuid.nullable(),
  ownerName: z.string(),
  jobCount: z.number(),
  custCount: z.number(),
  newCustCount: z.number(),
  outstanding: MoneyOut,
  /** Phần QUÁ HẠN nằm TRONG `outstanding`, không phải một con số song song. */
  overdue: MoneyOut,
})

export const StatsVendorDto = z.object({
  vendorId: Uuid.nullable(),
  vendorName: z.string(),
  jobCount: z.number(),
  costTotal: MoneyOut,
  sharePct: z.number().nullable(),
  revenue: MoneyOut,
  grossMargin: MoneyOut,
  marginPct: z.number().nullable(),
  apOutstanding: MoneyOut,
})

export const StatsRouteDto = z.object({
  ...MarginDto,
  route: z.string(),
  contType: z.string().nullable(),
  jobCount: z.number(),
  avgRevenue: MoneyOut,
  avgCost: MoneyOut,
  /** `null` khi KHÔNG đơn nào của tuyến đi theo bảng giá công bố nào. */
  avgListPrice: MoneyOut.nullable(),
  vsListPct: z.number().nullable(),
})

/** Một GÓI DỊCH VỤ trong donut "Tỷ trọng doanh thu theo gói dịch vụ" của Dashboard. */
export const StatsPackDto = z.object({
  ...MarginDto,
  packId: Uuid.nullable(),
  pack: z.string(),
  jobCount: z.number(),
  contCount: z.number(),
  /** Tỷ trọng trên tổng doanh thu CẢ KỲ — `null` khi cả kỳ chưa có doanh thu. */
  sharePct: z.number().nullable(),
  /** Bao nhiêu đơn CHƯA có khoản chi phí nào — dòng "⚠ N đơn chưa có chi phí" dưới ô lãi gộp. */
  noCostCount: z.number(),
})

/** Một KHÁCH HÀNG trong bảng "Theo khách hàng" — nhãn cột đầu của bản cũ là "Nhà máy". */
export const StatsCustomerDto = z.object({
  ...MarginDto,
  customerId: Uuid.nullable(),
  customerName: z.string(),
  jobCount: z.number(),
  /** Bao nhiêu đơn CHƯA có khoản chi phí nào — lãi gộp của chúng đang cao giả. */
  noCostCount: z.number(),
})

/** Một ĐƠN trong bảng "Hiệu quả từng đơn" — đơn xấu nhất lên đầu. */
export const StatsOrderDto = z.object({
  ...MarginDto,
  jobId: Uuid,
  ref: z.string(),
  customerName: z.string(),
  packName: z.string().nullable(),
  /** Chênh so với giá công bố đóng băng lúc bắn đơn. `null` = đơn không đi theo bảng giá nào. */
  vsList: MoneyOut.nullable(),
  legDone: z.number(),
  legTotal: z.number(),
  noCost: z.boolean(),
})

/** Một THÁNG trong bảng "⚠ Quá hạn xuất hóa đơn". */
export const StatsUnbilledDto = z.object({
  /** `YYYY-MM`. */
  period: z.string(),
  lateMonths: z.number(),
  jobCount: z.number(),
  requestedCount: z.number(),
  notRequestedCount: z.number(),
  custCount: z.number(),
  unbilledRevenue: MoneyOut,
  orders: z.array(z.object({
    jobId: Uuid, ref: z.string(), customerName: z.string(), date: z.string(), routeName: z.string().nullable(),
    revenue: MoneyOut, billStatus: z.string(),
  })),
})

/** 📈 Hiệu quả thầu phụ — ba bảng + dữ liệu bấm số ra chi tiết (`@rs/stats.vendorPnl`). */
const OpenOrderLineDto = z.object({
  id: Uuid, ref: z.string(), customerId: Uuid.nullable(), routeName: z.string().nullable(),
  dueAt: z.string().nullable(), orderStatus: z.string(),
})

export const VendorPnlDto = z.object({
  liveCount: z.number(), allLiveCount: z.number(), multiExcluded: z.number(),
  rows: z.array(z.object({
    vendorId: Uuid, orderCount: z.number(), legCount: z.number(), revVnd: z.number(), vendorCostVnd: z.number(), orderCostVnd: z.number(),
    unpaidVnd: z.number(), grossVnd: z.number(), marginPct: z.number().nullable(),
    orders: z.array(z.object({ jobId: Uuid, legCount: z.number(), revVnd: z.number(), vendorCostVnd: z.number() })),
    legIds: z.array(Uuid),
  })),
  totals: z.object({ revVnd: z.number(), grossVnd: z.number(), marginPct: z.number().nullable() }),
  buy: z.array(z.object({ vendorId: Uuid.nullable(), count: z.number(), buyVnd: z.number(), vatVnd: z.number(), unpaidVnd: z.number(), unpaidVatVnd: z.number() })),
  periods: z.array(z.string()),
  monthly: z.array(z.object({ vendorId: Uuid.nullable(), unconfirmedVnd: z.number(), confirmedVnd: z.number(), vatVnd: z.number(), sumVnd: z.number() })),
  costs: z.array(z.object({
    id: Uuid, jobId: Uuid, vendorId: Uuid.nullable(), label: z.string(), amountVnd: z.number(), statementRef: z.string().nullable(),
    payStatus: z.string(), period: z.string(), monthly: z.boolean(),
  })),
  vendorOptions: z.array(Uuid),
  vendors: z.array(z.object({ id: Uuid, label: z.string() })),
  orders: z.array(z.object({ id: Uuid, ref: z.string(), customerName: z.string(), pickupDate: z.string(), revVnd: z.number(), orderStatus: z.string() })),
  legs: z.array(z.object({
    id: Uuid, jobId: Uuid, jobRef: z.string(), seq: z.number(), fromAddr: z.string().nullable(), toAddr: z.string().nullable(),
    planDate: z.string().nullable(), costVnd: z.number(),
  })),
  jobRefs: z.array(z.object({ id: Uuid, ref: z.string() })),
  /* Dải "ĐƠN ĐANG MỞ" ngay dưới tiêu đề (bản cũ `Az.tsx:613`) — KHÔNG ăn theo bộ lọc ba bảng. */
  openOrders: z.object({
    noLeg: z.array(OpenOrderLineDto), running: z.array(OpenOrderLineDto), late: z.array(OpenOrderLineDto),
  }),
})

const PeriodStatsDto = z.object({ orders: z.number(), revenue: z.number(), profit: z.number() })

/**
 * 📊 DASHBOARD ▸ TỔNG QUAN — SẢN LƯỢNG · VỎ LẠNH (`0122`). ⚠ Tiền ở đây là SỐ (không phải chuỗi thập phân): đây là số ĐÃ CỘNG để
 * vẽ biểu đồ / thẻ, không phải số ghi sổ — cùng quy ước với các báo cáo slide KH-BC TH.
 */
/** Biểu đồ trọn năm của Dashboard ▸ Tổng quan (`0148`) — 12 tháng, số đã cộng để vẽ (cùng quy ước `DashOverviewDto`). */
export const DashYearChartDto = z.object({
  year: z.number(),
  months: z.array(z.object({ month: z.number(), revenue: z.number(), costValue: z.number(), profit: z.number() })),
})

export const DashOverviewDto = z.object({
  basis: z.enum(['plan', 'actual', 'invoice']),
  volume: z.object({ contOrders: z.number(), reefer: z.number(), dry: z.number(), other: z.number() }),
  volumeByMonth: z.array(z.object({ month: z.number(), reefer: z.number(), dry: z.number() })),
  reefer: z.object({
    orders: z.number(), customers: z.number(), revenue: z.number(), costValue: z.number(), profit: z.number(),
    marginPct: z.number().nullable(), depreciation: z.number(), deliveredInPeriod: z.number(),
    byPack: z.array(z.object({ pack: z.string(), orders: z.number(), revenue: z.number(), costValue: z.number() })),
    rotation: z.array(z.object({ pack: z.string(), route: z.string(), trips: z.number(), usedConts: z.number() })),
    allConts: z.number(),
    rotationUsedConts: z.number(),
    detail: z.array(z.object({
      ref: z.string(), customerName: z.string(), customerTax: z.string(), pack: z.string(), contType: z.string(), contNo: z.string(),
      date: ISODate, revenue: z.number(), costValue: z.number(),
    })),
    prev: PeriodStatsDto, yoy: PeriodStatsDto,
    planTarget: z.number(), containerCount: z.number(), commonCost: z.number(), salaryRate: z.number(), salaryCost: z.number(),
    interestCost: z.number(),
    months: z.array(z.object({ period: z.string(), interestCost: z.number() })),
  }),
})

export const StatsReportDto = z.object({
  from: ISODate,
  to: ISODate,
  overview: StatsOverviewDto,
  summary: StatsSummaryDto,
  monthly: z.array(StatsMonthDto),
  bySale: z.array(StatsSaleDto),
  bySaleMonthly: z.array(StatsSaleMonthDto),
  byVendor: z.array(StatsVendorDto),
  byRoute: z.array(StatsRouteDto),
  byPack: z.array(StatsPackDto),
  byCustomer: z.array(StatsCustomerDto),
  byOrder: z.array(StatsOrderDto),
  /** ⚠ KHÔNG ăn theo kỳ báo cáo — mọi tháng TRƯỚC tháng của `to` mà còn đơn chưa ra chứng từ. */
  unbilled: z.array(StatsUnbilledDto),
  /**
   * Người xem được xem giá vốn (rà 16/10 C6). `false` ⇒ máy chủ đã xoá giá vốn / lãi gộp (về 0, tỷ lệ `null`), `byVendor`
   * và `byOrder` rỗng — màn ẩn các cột đó như bản cũ (`canCost`).
   */
  costVisible: z.boolean(),
  /** Chỉ thấy đơn của khách mình phụ trách (phạm vi `own`). */
  ownScope: z.boolean(),
})

/* ══ HOA HỒNG SALE (F27) ═════════════════════════════════════════════════════════════════════ */

/**
 * ⭐ MÔ HÌNH BẢN CŨ (`CommissionPane` · `PhatHoaHongTab`): số là ĐỒNG nguyên, máy chủ tính từ chứng từ — màn chỉ hiển thị.
 * ⚠ Ô tiền mang tên nghiệp vụ bản cũ (`hhGop` · `thue` · `totalBonus` · `rev` · `lng`), không phải `amount`: đây là số thống kê
 *   của bảng tính chứ không phải chứng từ ghi sổ — chứng từ thật (ĐNTT, AMIS) đi qua đúng miền của nó.
 */
const HhTierDto = z.object({ min: z.number().nullable(), max: z.number().nullable(), rate: z.number() })
const KpiTierDto = z.object({ target: z.number(), bonus: z.number(), minMonths: z.number() })
const SheetCell = z.union([z.string(), z.number()])
const EmpTypeDto = z.enum(['staff', 'ctv'])
const PhatStatusDto = z.enum(['paid', 'held', 'waiting', 'eligible']).nullable()

export const CommissionOrderRowDto = z.object({
  ref: z.string(), jobId: Uuid, customerName: z.string(), customerId: Uuid, invDate: z.string(),
  rev: z.number(), lng: z.number(), marginPct: z.number(), hhFull: z.number(), role: z.enum(['main', 'sub']), share: z.number(), hhShare: z.number(),
  paid: z.boolean(), branch: z.string(), overridden: z.boolean(), overrideReason: z.string().nullable(), hasOrigDoc: z.boolean(),
  rate: z.number(), chietKhau: z.number(), otherCosts: z.number(), cp1: z.number(), cp2: z.number(), cp3: z.number(),
  invoiceNo: z.string().nullable(), chiefCreated: z.boolean(), origRev: z.number(), origCost: z.number(),
})
export const CommissionBranchRowDto = z.object({
  branch: z.string(), branchKind: z.enum(['noidia', 'qte']), rev: z.number(), lng: z.number(), avgMarginPct: z.number(), rate: z.number(),
  hhFullBranch: z.number(), chietKhau: z.number(), otherCosts: z.number(), cp1: z.number(), cp2: z.number(), cp3: z.number(), mainCost: z.number(),
})
export const CommissionSaleRowDto = z.object({
  id: Uuid, name: z.string(), rev: z.number(), lng: z.number(), hhMain: z.number(), hhSub: z.number(), orders: z.array(CommissionOrderRowDto),
  empType: EmpTypeDto, hhGop: z.number(), thue: z.number(), hhThuc: z.number(), months: z.number(), kpiBonus: z.number(),
  kpiBonusGross: z.number(), debtCustomers: z.number(), debtCustomersQT: z.number(), debtPenalty: z.number(), totalBonus: z.number(),
  branches: z.array(CommissionBranchRowDto), kpiGateOk: z.boolean(), cultureScore: z.number(), qteEffOk: z.boolean(),
})
export const CommissionPayoutBasisDto = z.object({
  id: Uuid, name: z.string(), empType: EmpTypeDto, hhGop: z.number(), thue: z.number(), kpiBonus: z.number(), totalBonus: z.number(),
  orderRefs: z.array(z.string()), missingOrigDocRefs: z.array(z.string()), hhMain: z.number(), hhSub: z.number(),
  mainOrderCount: z.number(), subOrderCount: z.number(),
})
const CommissionSheetsShape = {
  summaryHeaders: z.array(z.string()), summaryRows: z.array(z.array(SheetCell)),
  detailHeaders: z.array(z.string()), detailRows: z.array(z.array(SheetCell)),
  payoutBasis: z.array(CommissionPayoutBasisDto), phatStatuses: z.array(PhatStatusDto),
}

export const CommissionMonthCalcDto = z.object({
  ym: z.string(), cutoff: ISODate, rowsAgg: z.array(CommissionSaleRowDto), skippedNoDoc: z.number(), notYetPaidCount: z.number(),
  skippedNoSaleList: z.array(z.object({ id: Uuid, ref: z.string(), customerName: z.string(), rev: z.number(), invDate: z.string(), invoiceNo: z.string().nullable() })),
  chiefCreatedExcluded: z.array(z.object({ id: Uuid, ref: z.string(), customerName: z.string(), rev: z.number() })),
  companyTarget: z.number(), companyActual: z.number(), companyKpiRate: z.number(),
  ...CommissionSheetsShape,
})

/** Một lần phát (một sale · một tháng nguồn) và phiếu ĐNTT nó trỏ tới. */
export const CommissionMonthPayoutDto = z.object({
  id: Uuid, ref: z.string(), saleId: Uuid.nullable(), saleName: z.string(), ym: z.string(), paidAt: ISODate.nullable(),
  hhGop: z.number(), thue: z.number(), kpiBonus: z.number(), totalBonus: z.number(), orderRefs: z.array(z.string()),
  /** Thu lại · chi bổ sung của lần phát — net thực chi = totalBonus − clawback + extraPay (bản cũ PART C2). */
  clawback: z.number(), extraPay: z.number(),
  createdBy: z.string().nullable(), createdAt: ISOTimestamp, note: z.string().nullable(),
  requestRef: z.string().nullable(), requestStatus: z.string().nullable(), requestPaidDate: ISODate.nullable(),
  payeeCode: z.string().nullable(), payeeAccount: z.string().nullable(), payeeBank: z.string().nullable(),
})

export const CommissionSaleBriefDto = z.object({
  id: Uuid, name: z.string(), code: z.string().nullable(), email: z.string().nullable(), empTypeNow: EmpTypeDto,
  bankAccount: z.string().nullable(), bankName: z.string().nullable(),
})

export const CommissionMonthStateDto = z.object({
  ym: z.string(), canSeeAll: z.boolean(), isChief: z.boolean(),
  snapshot: z.object({
    ym: z.string(), cutoff: ISODate, savedAt: ISOTimestamp, savedBy: z.string(), locked: z.boolean(), version: z.number(),
    companyTarget: z.number(), companyActual: z.number(), companyKpiRate: z.number(), ...CommissionSheetsShape,
  }).nullable(),
  fixedSalaries: z.array(z.object({ saleId: Uuid, salaryVnd: z.number() })),
  cultureScores: z.array(z.object({ saleId: Uuid, score: z.number() })),
  payouts: z.array(CommissionMonthPayoutDto),
  users: z.array(CommissionSaleBriefDto),
})

/** Một bản sửa tay số của một đơn trong kỳ (`0064`). `null` ở ô tiền = KHÔNG đè. */
export const CommissionOrderOverrideDto = z.object({
  id: Uuid, period: z.string(), jobRef: z.string(), saleId: Uuid.nullable(), saleName: z.string(),
  revOverride: MoneyOut.nullable(), costOverride: MoneyOut.nullable(), branchTag: z.string().nullable(), reason: z.string(),
  createdBy: z.string().nullable(), version: z.number(),
})

export const CommissionDryRunDto = z.object({ rows: z.array(z.object({ ref: z.string(), saleName: z.string(), before: z.number(), after: z.number() })) })

export const CommissionPolicyVersionDto = z.object({
  id: Uuid, effectiveFrom: ISODate, hhTierNoiDia: z.array(HhTierDto), hhTierQuocTe: z.array(HhTierDto), kpiTiers: z.array(KpiTierDto),
  note: z.string().nullable(), createdBy: z.string(), createdAt: ISOTimestamp,
})
export const CommissionPolicyStateDto = z.object({
  versions: z.array(CommissionPolicyVersionDto),
  active: z.object({ hhNoiDia: z.array(HhTierDto), hhQuocTe: z.array(HhTierDto), kpi: z.array(KpiTierDto), versionId: Uuid.nullable() }),
  canEdit: z.boolean(),
  sales: z.array(z.object({
    id: Uuid, name: z.string(),
    empTypePeriods: z.array(z.object({ effectiveFrom: z.string(), empType: EmpTypeDto, by: z.string().optional() })),
  })),
})

const PoolOrderDto = z.object({
  ref: z.string(), saleId: Uuid, saleName: z.string(), empType: EmpTypeDto, hh: z.number(), ym: z.string(), customerName: z.string(),
})
export const CommissionPhatBoardDto = z.object({
  rows: z.array(z.object({
    saleId: Uuid, saleName: z.string(), empType: EmpTypeDto, eligible: z.array(PoolOrderDto), waiting: z.array(PoolOrderDto),
    held: z.array(PoolOrderDto), thue: z.number(), bankName: z.string().nullable(), bankAccount: z.string().nullable(), code: z.string().nullable(),
  })),
  holds: z.array(z.object({ id: Uuid, ref: z.string(), saleId: Uuid.nullable(), version: z.number() })),
})
export const CommissionPhatResultDto = z.object({ requestRef: z.string(), docNos: z.array(z.string()), saleCount: z.number(), totalNet: z.number() })
export const CommissionDiscrepancyDto = z.object({ rows: z.array(z.object({ ym: z.string(), diff: z.number() })) })

const CommissionReportSaleDto = z.object({
  id: Uuid, name: z.string(), totalBonus: z.number(), empType: EmpTypeDto, orderCount: z.number(),
  rev: z.number().nullable(), lng: z.number().nullable(), hhMain: z.number(), hhSub: z.number(), hhGop: z.number(), thue: z.number(),
  kpiBonusGross: z.number().nullable(), debtPenalty: z.number().nullable(), kpiBonus: z.number(),
})
export const CommissionReportDto = z.object({
  months: z.array(z.object({
    ym: z.string(), locked: z.boolean(), computedTotal: z.number(), issued: z.number(), paidOut: z.number(), remainToIssue: z.number(), remainDebt: z.number(),
    rev: z.number().nullable(), lng: z.number().nullable(), sales: z.array(CommissionReportSaleDto),
  })),
  payouts: z.array(CommissionMonthPayoutDto),
  people: z.array(z.object({ id: Uuid, code: z.string().nullable() })),
  canSeeAll: z.boolean(),
  snapshots: z.array(z.object({ ym: z.string(), savedAt: ISOTimestamp, savedBy: z.string(), companyActual: z.number(), locked: z.boolean() })),
})
export const CommissionSnapshotFileDto = z.object({
  ym: z.string(), savedAt: ISOTimestamp,
  summaryHeaders: z.array(z.string()), summaryRows: z.array(z.array(SheetCell)), detailHeaders: z.array(z.string()), detailRows: z.array(z.array(SheetCell)),
  payouts: z.array(CommissionMonthPayoutDto),
})

/** Một dòng tuyến của tờ trình chính sách hoa hồng khách (`0097`). */
export const CustPolicyRowDto = z.object({
  id: Uuid,
  seq: z.number(),
  route: z.string(),
  service: z.string().nullable(),
  unitSellPrice: MoneyOut.nullable(),
  policyAmount: MoneyOut,
  payeeName: z.string(),
  payeeCode: z.string().nullable(),
  bankAccount: z.string().nullable(),
  bankName: z.string().nullable(),
  note: z.string().nullable(),
})

/** Tờ trình CHÍNH SÁCH HOA HỒNG KHÁCH — một khách, nhiều tuyến, hai cấp duyệt. */
export const CustPolicyDto = z.object({
  id: Uuid,
  ref: z.string(),
  docDate: ISODate,
  effectiveFrom: ISODate,
  effectiveTo: ISODate,
  customerId: Uuid,
  customerName: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  source: z.enum(['pane', 'order']),
  managerApproverId: Uuid.nullable(),
  directorApproverId: Uuid.nullable(),
  submittedBy: z.string().nullable(),
  submittedAt: ISOTimestamp.nullable(),
  managerBy: z.string().nullable(),
  managerAt: ISOTimestamp.nullable(),
  directorBy: z.string().nullable(),
  directorAt: ISOTimestamp.nullable(),
  approvedAt: ISOTimestamp.nullable(),
  rejectedReason: z.string().nullable(),
  rejectedBy: z.string().nullable(),
  history: z.array(z.object({ at: z.string(), by: z.string().optional(), label: z.string() })),
  createdBy: z.string().nullable(),
  createdAt: ISOTimestamp,
  version: z.number(),
  rows: z.array(CustPolicyRowDto),
})

/** Kết quả tra chính sách khớp khách + tuyến + dịch vụ. `null` = chưa có tờ nào khớp. */
export const CustPolicyMatchDto = z.object({ policy: CustPolicyDto, row: CustPolicyRowDto }).nullable()

/* ══ CHÀO GIÁ THẦU PHỤ (F28) ═════════════════════════════════════════════════════════════════ */

export const BidRequestDto = z.object({
  id: Uuid,
  ref: z.string(),
  jobId: Uuid,
  title: z.string(),
  fromAddr: z.string(),
  toAddr: z.string(),
  planDate: ISODate,
  contType: z.string().nullable(),
  note: z.string().nullable(),
  deadline: ISOTimestamp.nullable(),
  status: z.enum(['open', 'closed', 'awarded', 'cancelled']),
  awardedOfferId: Uuid.nullable(),
  awardedLegId: Uuid.nullable(),
  createdBy: z.string().nullable(),
  awardedBy: z.string().nullable(),
  awardedAt: ISOTimestamp.nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
  openedAt: ISOTimestamp.nullable(),
  selectReason: z.string().nullable(),
})

export const BidOfferDto = z.object({
  id: Uuid,
  requestId: Uuid,
  vendorId: Uuid,
  vendorName: z.string(),
  amount: MoneyOut,
  leadDays: z.number().nullable(),
  note: z.string().nullable(),
  status: z.enum(['submitted', 'withdrawn', 'won', 'lost']),
  submittedAt: ISOTimestamp,
  version: z.number(),
  /** Tên công ty · MST · SĐT tự khai lúc chào giá (tài khoản dùng chung). */
  companyName: z.string().nullable(),
  taxCode: z.string().nullable(),
  phone: z.string().nullable(),
  serviceMode: z.enum(['rail', 'road', 'sea']).nullable(),
  hasInvoice: z.boolean(),
  vatPercent: z.number().nullable(),
})

/** Giá đã xếp hạng. `rank` 0 = đã rút, không xếp hạng. */
export const RankedOfferDto = BidOfferDto.extend({
  rank: z.number(),
  /** Đắt hơn giá tốt nhất bao nhiêu phần trăm. `null` khi chưa có giá nào để so. */
  vsBestPct: z.number().nullable(),
})

export const BidSummaryDto = z.object({
  offerCount: z.number(),
  liveCount: z.number(),
  bestAmount: MoneyOut.nullable(),
  worstAmount: MoneyOut.nullable(),
  spreadPct: z.number().nullable(),
})

export const BidDetailDto = z.object({
  request: BidRequestDto,
  offers: z.array(RankedOfferDto),
  summary: BidSummaryDto,
})

/**
 * ⭐ CỔNG THẦU PHỤ — CHỈ phiên và ĐÚNG giá của chính họ.
 * ⚠ KHÔNG có `offers` của ai khác và KHÔNG có `summary`. Biết "giá tốt nhất hiện tại là 9 triệu"
 *   đủ để bỏ 8,9 triệu — đó chính là phiên đấu giá ngược mà luật cốt tử của F28 cấm.
 */
export const MyBidDto = z.object({
  request: BidRequestDto,
  job: z.object({ ref: z.string(), pickupAddr: z.string().nullable(), dropAddr: z.string().nullable(), planDate: ISODate.nullable() }),
  myOffer: BidOfferDto.nullable(),
  myOffers: z.array(BidOfferDto),
})

/** 📣 Bảng "Mở thầu" của điều hành — mỗi đơn lần mở mới nhất, kèm thông tin đơn · số chặng · MỌI chào giá. */
export const BiddingBoardDto = z.object({
  today: ISODate,
  rows: z.array(z.object({
    request: BidRequestDto,
    job: z.object({
      id: Uuid, ref: z.string(), orderStatus: z.string(), customerName: z.string(), planDate: ISODate.nullable(),
      routeName: z.string().nullable(), contType: z.string().nullable(), packName: z.string().nullable(),
      pickupAddr: z.string().nullable(), dropAddr: z.string().nullable(),
    }),
    legCount: z.number(),
    offers: z.array(BidOfferDto),
  })),
})

export const OpenBiddingResultDto = z.object({ opened: z.array(z.string()), alreadyOpen: z.number(), notEligible: z.number() })

/** Chọn giá xong: NCC dùng để bắn chặng (tạo mới theo MST nếu chưa có) + giá đã chào — màn mở form bắn chặng điền sẵn. */
export const BidPickResultDto = z.object({ jobId: Uuid, vendorId: Uuid, vendorCreated: z.boolean(), amount: MoneyOut })

/* ══ VỎ CONTAINER: ĐIỀU HÀNH (F29) ═══════════════════════════════════════════════════════════ */

export const ContainerDto = z.object({
  id: Uuid,
  code: z.string(),
  size: z.string().nullable(),
  contType: z.string().nullable(),
  madeYear: z.number().nullable(),
  ownership: z.enum(['own', 'leased']),
  status: z.enum(['available', 'on_order', 'maintenance', 'retired']),
  location: z.string().nullable(),
  note: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  /** Trục điều hành của bản cũ — sáu trạng thái, vị trí tại ga, đơn đang gắn, cắm điện. */
  opsStatus: z.enum(['at_station', 'operating', 'picking_up', 'empty_move', 'other', 'repairing']),
  locationId: Uuid.nullable(),
  locationName: z.string().nullable(),
  currentJobId: Uuid.nullable(),
  currentJobRef: z.string().nullable(),
  plugged: z.boolean(),
  opsChangedAt: z.string(),
})

/** Vỏ kèm "bận tới khi nào". `null` = rảnh, HOẶC bận mà chưa biết tới bao giờ — đọc `status`. */
export const FleetRowDto = ContainerDto.extend({ busyUntil: ISODate.nullable() })

export const FleetSummaryDto = z.object({
  fleetSize: z.number(),
  available: z.number(),
  onOrder: z.number(),
  maintenance: z.number(),
  retired: z.number(),
  /** Tính trên vỏ CÒN DÙNG ĐƯỢC, không trên tổng vỏ. `null` khi không còn vỏ nào dùng được. */
  utilisationPct: z.number().nullable(),
})

export const FleetDto = z.object({
  rows: z.array(FleetRowDto),
  summary: FleetSummaryDto,
})

export const ContOrderDto = z.object({
  id: Uuid,
  ref: z.string(),
  customerId: Uuid.nullable(),
  customerName: z.string().nullable(),
  containerId: Uuid,
  service: z.enum(['rent', 'move', 'repair']),
  fromDate: ISODate,
  toDate: ISODate.nullable(),
  fromPlace: z.string().nullable(),
  toPlace: z.string().nullable(),
  status: z.enum(['draft', 'confirmed', 'running', 'done', 'cancelled']),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
  createdAt: ISOTimestamp,
})

export const ContCatalogDto = z.object({
  id: Uuid,
  kind: z.enum(['place', 'cont_type', 'service_item', 'material', 'status_label']),
  code: z.string(),
  name: z.string(),
  sortOrder: z.number(),
  active: z.boolean(),
  note: z.string().nullable(),
  color: z.string().nullable(),
  version: z.number(),
})

/* ══ VỎ CONTAINER: DẦU · SỬA CHỮA · KHOÁ KỲ (F30) ════════════════════════════════════════════ */

export const FuelLotDto = z.object({
  id: Uuid,
  ref: z.string(),
  recvDate: ISODate,
  vendorName: z.string().nullable(),
  place: z.string().nullable(),
  /** Lít — số, KHÔNG phải `Money`. */
  qtyIn: z.number(),
  qtyRemain: z.number(),
  unitPrice: MoneyOut,
})

export const FuelStockDto = z.object({
  lots: z.array(FuelLotDto),
  onHandQty: z.number(),
  stockValue: MoneyOut,
  lockedTo: ISODate.nullable(),
})

export const FuelMoveDto = z.object({
  id: Uuid,
  ref: z.string(),
  moveDate: ISODate,
  kind: z.enum(['issue', 'transfer', 'adjust']),
  containerId: Uuid.nullable(),
  jobId: Uuid.nullable(),
  fromPlace: z.string().nullable(),
  toPlace: z.string().nullable(),
  qty: z.number(),
  /** Giá vốn của phiếu — tổng tiền các lô FIFO đã ăn vào. */
  costAmount: MoneyOut,
  reason: z.string().nullable(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  version: z.number(),
})

/** ⭐ BẢNG GIẢI THÍCH: phiếu đã ăn vào lô nào, mỗi lô bao nhiêu lít, bao nhiêu đồng. */
export const FuelMoveLotDto = z.object({
  lotRef: z.string(),
  qty: z.number(),
  unitPrice: MoneyOut,
  amount: MoneyOut,
})

export const FuelMoveResultDto = z.object({
  id: Uuid,
  ref: z.string(),
  costAmount: MoneyOut,
  lines: z.array(FuelMoveLotDto),
})

const RequestLinkDto = z.object({
  id: Uuid, ref: z.string(), status: z.string(), submitted: z.boolean(),
}).nullable()

export const RepairItemDto = z.object({
  id: z.string(),
  name: z.string(),
  desc: z.string().optional(),
  vendorPlan: z.string().optional(),
  vendorActual: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high']).optional(),
  costEst: MoneyOut.optional(),
  quotes: z.array(z.object({ vendorName: z.string(), amount: MoneyOut })).optional(),
  qty: blankAsAbsent(z.number()),
  unitPrice: MoneyOut.optional(),
  costSettle: MoneyOut.optional(),
  doneDate: blankAsAbsent(ISODate),
  accepted: z.boolean().optional(),
  supplyMode: z.enum(['', 'to_stock', 'from_stock']).optional(),
  supplyId: blankAsAbsent(Uuid),
})

export const ContRepairDto = z.object({
  id: Uuid,
  ref: z.string(),
  repairDate: ISODate,
  containerId: Uuid,
  containerCode: z.string().nullable(),
  containerType: z.string().nullable(),
  kind: z.enum(['repair', 'material']),
  repClass: z.enum(['periodic', 'major']),
  planDate: ISODate.nullable(),
  payDue: ISODate.nullable(),
  garage: z.string().nullable(),
  description: z.string(),
  costEst: MoneyOut,
  costSettle: MoneyOut.nullable(),
  advanceAmount: MoneyOut.nullable(),
  opsApprover: z.string().nullable(),
  directorApprover: z.string().nullable(),
  items: z.array(RepairItemDto),
  status: z.enum([
    'draft', 'plan_review', 'plan_review_ops', 'plan_review_director', 'executing',
    'settle_review', 'settle_review_ops', 'settle_review_director', 'done', 'rejected', 'cancelled',
  ]),
  settledAt: ISODate.nullable(),
  advanceRequest: RequestLinkDto,
  settleRequest: RequestLinkDto,
  rejectReason: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
})

export const RelatedCostDto = z.object({
  id: Uuid,
  ref: z.string(),
  description: z.string(),
  amount: MoneyOut,
  settleAmount: MoneyOut.nullable(),
  containerIds: z.array(Uuid),
  status: z.enum(['draft', 'director_review', 'approved', 'paid', 'rejected']),
  bankName: z.string().nullable(),
  bankAccount: z.string().nullable(),
  accountName: z.string().nullable(),
  paymentRequest: RequestLinkDto,
  rejectReason: z.string().nullable(),
  paidAt: ISODate.nullable(),
  history: z.array(z.unknown()).nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  version: z.number(),
})

export const ContMaterialDto = z.object({
  id: Uuid,
  name: z.string(),
  unit: z.string(),
  qty: z.number(),
  qtyUsed: z.number(),
  unitPrice: MoneyOut.nullable(),
  buyDate: ISODate.nullable(),
  containerId: Uuid.nullable(),
  supplier: z.string().nullable(),
  source: z.enum(['qt', 'mua']),
  settled: z.boolean(),
  note: z.string().nullable(),
  version: z.number(),
})

export const ContReportDto = z.object({
  from: ISODate,
  to: ISODate,
  openingQty: z.number(),
  inQty: z.number(),
  outQty: z.number(),
  adjustQty: z.number(),
  closingQty: z.number(),
  closingValue: MoneyOut,
  issuedCost: MoneyOut,
  /**
   * ⭐ LỆCH giữa hai đường tính tồn cuối: cộng phiếu, và tồn thật của lô. KHÁC 0 nghĩa là có phiếu
   * ghi mà không trừ lô — FIFO đã thủng, và phải có người nhìn.
   */
  mismatch: z.number(),
  repairCost: MoneyOut,
  materialCost: MoneyOut,
  lockedTo: ISODate.nullable(),
})

/* ══ BÁO CÁO LỜI LỖ VỎ LẠNH ═══════════════════════════════════════════════════════════════════ */

const ProfitCells = {
  orders: z.number(),
  revenue: MoneyOut,
  fuelCost: MoneyOut,
  repairCost: MoneyOut,
  depreciation: MoneyOut,
  otherCost: MoneyOut,
  commonCost: MoneyOut,
  salary: MoneyOut,
  cost: MoneyOut,
  profit: MoneyOut,
}

/** Số gõ tay. `null` = CHƯA GÕ, khác 0 — ở bảng theo vỏ, chưa gõ là nhận phần chia đều. */
const TypedCells = z.object({
  depreciation: MoneyOut.nullable(),
  otherCost: MoneyOut.nullable(),
  commonCost: MoneyOut.nullable(),
})

export const ProfitMonthDto = z.object({
  month: z.string(),
  ...ProfitCells,
  override: TypedCells,
  suggestedDepreciation: MoneyOut,
  relatedCost: MoneyOut,
})

export const ProfitContainerDto = z.object({
  containerId: Uuid,
  code: z.string(),
  ...ProfitCells,
  override: TypedCells,
})

export const ProfitSalaryLineDto = z.object({
  containerId: Uuid,
  code: z.string(),
  orders: z.number(),
  amount: MoneyOut,
})

export const ProfitReportDto = z.object({
  year: z.number(),
  basis: z.enum(['confirmed', 'delivered', 'invoiced']),
  salaryRate: MoneyOut,
  months: z.array(ProfitMonthDto),
  total: z.object(ProfitCells),
  routes: z.array(z.object({ route: z.string().nullable(), orders: z.number() })),
  byContainer: z.object({
    month: z.string(),
    rows: z.array(ProfitContainerDto),
    exceeded: z.object({
      depreciation: z.boolean(), otherCost: z.boolean(), commonCost: z.boolean(),
    }),
  }),
  salary: z.array(z.object({ month: z.string(), lines: z.array(ProfitSalaryLineDto) })),
})

/** Một dòng bảng Điều hành vỏ lạnh (đơn RF của nhóm RATRACO) — `vendorCost` rỗng khi không có quyền giá vốn. */
export const ReeferBoardRowDto = z.object({
  id: Uuid,
  ref: z.string(),
  version: z.number(),
  customerName: z.string().nullable(),
  cargo: z.string().nullable(),
  contType: z.string().nullable(),
  routeName: z.string().nullable(),
  planDate: ISODate,
  dueAt: ISODate.nullable(),
  createdOn: ISODate,
  pickupAddr: z.string().nullable(),
  dropAddr: z.string().nullable(),
  orderStatus: z.string(),
  billStatus: z.string(),
  invoicedOn: ISODate.nullable(),
  revenue: MoneyOut,
  vendorCost: MoneyOut.nullable(),
  /** Phân bổ dầu của đơn — `null` khi chưa phân bổ hoặc không có quyền giá vốn. */
  estOffBook: MoneyOut.nullable(),
  actualDeliverOn: ISODate.nullable(),
  hasDrops: z.boolean(),
  drops: z.array(z.unknown()),
  fuelUsedL: z.number().nullable(),
  contReceivedOn: ISODate.nullable(),
  contReceivedBy: z.string().nullable(),
  reeferOpsConfirmedOn: ISODate.nullable(),
  reeferOpsConfirmedBy: z.string().nullable(),
  createdByName: z.string().nullable(),
  partners: z.string().nullable(),
  liveLegs: z.number(),
  undeliveredLegs: z.number(),
  hasPod: z.boolean(),
  container: z.object({
    id: Uuid, code: z.string(), contType: z.string().nullable(), plugged: z.boolean(),
    opsStatus: z.enum(['at_station', 'operating', 'picking_up', 'empty_move', 'other', 'repairing']),
    locationId: Uuid.nullable(), locationName: z.string().nullable(), version: z.number(),
  }).nullable(),
})


/* ══ 💸 CHI PHÍ — NHẬT KÝ DẦU THEO VỎ (chép `container-ops/Fuel.tsx`) ══════════════════════════════ */

export const FuelLogDto = z.object({
  id: Uuid,
  ref: z.string(),
  logDate: ISODate,
  containerId: Uuid,
  containerCode: z.string(),
  containerType: z.string().nullable(),
  jobId: Uuid.nullable(),
  jobRef: z.string().nullable(),
  liters: z.number(),
  cost: MoneyOut,
  unitPrice: MoneyOut.nullable(),
  station: z.string().nullable(),
  staff: z.string().nullable(),
  vendorId: Uuid.nullable(),
  vendorName: z.string().nullable(),
  vendorCode: z.string().nullable(),
  vendorTax: z.string().nullable(),
  vendorBankAccount: z.string().nullable(),
  vendorBankName: z.string().nullable(),
  payeeAccount: z.string().nullable(),
  payeeBank: z.string().nullable(),
  payeeHolder: z.string().nullable(),
  expenseItem: z.string().nullable(),
  mgmtOnly: z.boolean(),
  invoiceNo: z.string().nullable(),
  invoiceDate: ISODate.nullable(),
  vatRate: z.number().nullable(),
  acctExpense: z.string().nullable(),
  acctPayable: z.string().nullable(),
  acctVat: z.string().nullable(),
  transferId: Uuid.nullable(),
  note: z.string().nullable(),
  purchaseDocId: Uuid.nullable(),
  purchaseDocNo: z.string().nullable(),
  purchaseDocStatus: z.string().nullable(),
  bankTxnId: Uuid.nullable(),
  bankNoteNo: z.string().nullable(),
  bankNoteExported: z.boolean(),
  locked: z.boolean(),
  history: z.array(z.unknown()),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  version: z.number(),
})

export const FuelTransferDto = z.object({
  id: Uuid,
  transferDate: ISODate,
  fromContainerId: Uuid,
  fromCode: z.string(),
  toContainerId: Uuid,
  toCode: z.string(),
  liters: z.number(),
  reason: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  staffId: Uuid.nullable(),
  staffName: z.string().nullable(),
  approverId: Uuid.nullable(),
  approverName: z.string().nullable(),
  approvedOn: ISODate.nullable(),
  unitPrice: z.number().nullable(),
  history: z.array(z.unknown()),
  createdOn: ISODate,
  canEdit: z.boolean(),
  overdue: z.boolean(),
  version: z.number(),
})

export const FuelStockRowDto = z.object({
  containerId: Uuid,
  containerCode: z.string(),
  avgPrice: MoneyOut.nullable(),
  price: MoneyOut,
  opening: z.number(),
  doL: z.number(),
  doVal: MoneyOut,
  sanNet: z.number(),
  sanVal: MoneyOut,
  closing: z.number(),
  pbVal: MoneyOut,
  measuredL: z.number(),
  measuredVal: MoneyOut,
  measuredEntered: z.number(),
  measuredTotal: z.number(),
  afterConsume: z.number(),
  afterConsumeVal: MoneyOut,
  realL: z.number().nullable(),
  realVal: MoneyOut.nullable(),
  actualConsumeL: z.number().nullable(),
  actualConsumeVal: MoneyOut.nullable(),
  variance: MoneyOut.nullable(),
  dmOpen: MoneyOut,
  dmOpenOverridden: z.boolean(),
  dmDoVal: MoneyOut,
  dmClose: MoneyOut,
  adjCl: MoneyOut,
  alerts: z.array(z.enum(['belowNorm', 'overNorm', 'overMeasured'])),
})

export const FuelStockReportDto = z.object({
  month: z.string(),
  companyAvgPrice: MoneyOut,
  canEditAdjCl: z.boolean(),
  rows: z.array(FuelStockRowDto),
})

export const FuelYearRowDto = z.object({
  month: z.number(),
  opening: z.number(),
  doL: z.number(),
  sanNet: z.number(),
  sanVal: MoneyOut,
  cost: MoneyOut,
  closing: z.number(),
  price: MoneyOut,
  pbVal: MoneyOut,
  openingVal: MoneyOut,
  doVal: MoneyOut,
  closingVal: MoneyOut,
})

export const FuelYearReportDto = z.object({ year: z.number(), rows: z.array(FuelYearRowDto) })

export const FuelByJobRowDto = z.object({
  jobId: Uuid,
  ref: z.string(),
  planDate: ISODate,
  packName: z.string().nullable(),
  routeName: z.string().nullable(),
  containerCode: z.string().nullable(),
  containerType: z.string().nullable(),
  pbVal: MoneyOut,
  measuredL: z.number().nullable(),
  measuredVal: MoneyOut.nullable(),
  variance: MoneyOut.nullable(),
  allocDocNo: z.string().nullable(),
  version: z.number(),
})

export const DeprAllocJobDto = z.object({
  jobId: Uuid,
  ref: z.string(),
  planDate: ISODate,
  containerCode: z.string().nullable(),
  containerId: Uuid.nullable(),
  amount: MoneyOut,
  allocDocNo: z.string().nullable(),
  version: z.number(),
})

export const FuelStockReminderDto = z.object({
  month: z.string(),
  missing: z.array(z.object({ id: Uuid, code: z.string() })),
}).nullable()

/** Đơn lạnh để gắn vào phiếu đổ dầu. */
export const ReeferJobDto = z.object({
  id: Uuid,
  ref: z.string(),
  contNo: z.string().nullable(),
  routeName: z.string().nullable(),
  planDate: ISODate,
  orderStatus: z.string(),
  delivered: z.boolean(),
  customerName: z.string().nullable(),
  pickupAddr: z.string().nullable(),
  dropAddr: z.string().nullable(),
})

/* ══ KẾ HOẠCH & MỤC TIÊU (F31) ═══════════════════════════════════════════════════════════════ */

export const PlanPeriodDto = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  fromDate: ISODate,
  toDate: ISODate,
  status: z.enum(['open', 'closed']),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  closedBy: z.string().nullable(),
  closedAt: ISOTimestamp.nullable(),
  history: z.array(z.unknown()).nullable(),
  version: z.number(),
})

export const PlanTargetDto = z.object({
  id: Uuid,
  periodId: Uuid,
  scope: z.enum(['company', 'sale', 'vendor']),
  scopeId: Uuid.nullable(),
  scopeName: z.string().nullable(),
  metric: z.enum(['revenue', 'margin', 'trips']),
  targetValue: MoneyOut,
  note: z.string().nullable(),
  version: z.number(),
})

export const AchievementDto = z.object({
  scope: z.enum(['company', 'sale', 'vendor']),
  scopeId: Uuid.nullable(),
  scopeName: z.string(),
  metric: z.enum(['revenue', 'margin', 'trips']),
  targetValue: MoneyOut,
  actualValue: MoneyOut,
  /** Âm nghĩa là đã VƯỢT mục tiêu. */
  gap: MoneyOut,
  /** `null` khi mục tiêu bằng 0 — "chưa giao chỉ tiêu" khác "đạt 100%". */
  achievedPct: z.number().nullable(),
})

export const VendorProgressDto = z.object({
  vendorId: Uuid,
  vendorName: z.string(),
  routeName: z.string().nullable(),
  plannedTrips: z.number(),
  actualTrips: z.number(),
  progressPct: z.number().nullable(),
})

export const PeriodReportDto = z.object({
  period: PlanPeriodDto,
  targets: z.array(PlanTargetDto),
  achievements: z.array(AchievementDto),
  vendorPlans: z.array(VendorProgressDto),
})

/**
 * ⭐ CỔNG THẦU PHỤ — CHỈ kế hoạch của chính họ.
 * ⚠ KHÔNG có mục tiêu công ty, KHÔNG có thứ hạng, KHÔNG có kế hoạch của nhà thầu khác.
 */
export const MyPlanDto = z.object({
  periodCode: z.string(),
  periodFrom: ISODate,
  periodTo: ISODate,
  routeName: z.string().nullable(),
  plannedTrips: z.number(),
  actualTrips: z.number(),
  progressPct: z.number().nullable(),
  note: z.string().nullable(),
})

export type TaxLookupDto = z.infer<typeof TaxLookupDto>
export type RailPriceExDto = z.infer<typeof RailPriceExDto>
export type RailLegCustomerExDto = z.infer<typeof RailLegCustomerExDto>
export type CustomerDto = z.infer<typeof CustomerDto>
export type VendorDto = z.infer<typeof VendorDto>
export type PartnerBoardFactsDto = z.infer<typeof PartnerBoardFactsDto>
export type CatalogCustomerDto = z.infer<typeof CatalogCustomerDto>
export type CatalogVendorDto = z.infer<typeof CatalogVendorDto>
export type PartnerBoardSummaryDto = z.infer<typeof PartnerBoardSummaryDto>
export type ArRegisterSummaryDto = z.infer<typeof ArRegisterSummaryDto>
export type ContractDto = z.infer<typeof ContractDto>
export type ContractPriceDto = z.infer<typeof ContractPriceDto>
export type ContractRenewalDto = z.infer<typeof ContractRenewalDto>
export type ContractAppendixDto = z.infer<typeof ContractAppendixDto>
export type ContractCommentDto = z.infer<typeof ContractCommentDto>
export type QuoteDto = z.infer<typeof QuoteDto>
export type DocTemplateDto = z.infer<typeof DocTemplateDto>
export type OrderDto = z.infer<typeof OrderDto>
export type LegDto = z.infer<typeof LegDto>
export type AttachmentDto = z.infer<typeof AttachmentDto>
export type CostDto = z.infer<typeof CostDto>
export type CostSummaryDto = z.infer<typeof CostSummaryDto>
export type ArInvoiceDto = z.infer<typeof ArInvoiceDto>
export type ArInvoiceLineDto = z.infer<typeof ArInvoiceLineDto>
export type ArPaymentDto = z.infer<typeof ArPaymentDto>
export type ArAdvanceDto = z.infer<typeof ArAdvanceDto>
export type ArAllocTargetDto = z.infer<typeof ArAllocTargetDto>
export type ArAllocDto = z.infer<typeof ArAllocDto>
export type ArAdvanceListRowDto = z.infer<typeof ArAdvanceListRowDto>
export type ArAllocViewDto = z.infer<typeof ArAllocViewDto>
export type BillQueueDto = z.infer<typeof BillQueueDto>
export type ArAgingDto = z.infer<typeof ArAgingDto>
export type ArDebtRowDto = z.infer<typeof ArDebtRowDto>
export type ArDebtGroupDto = z.infer<typeof ArDebtGroupDto>
export type ArDebtReportDto = z.infer<typeof ArDebtReportDto>
export type ArStatementDto = z.infer<typeof ArStatementDto>
export type StatementPrintRowDto = z.infer<typeof StatementPrintRowDto>
export type StatementPrintDto = z.infer<typeof StatementPrintDto>
export type StatementRequestPreviewDto = z.infer<typeof StatementRequestPreviewDto>
export type LegEditStateDto = z.infer<typeof LegEditStateDto>
export type VendorPnlDto = z.infer<typeof VendorPnlDto>
export type BulkOrderImportDto = z.infer<typeof BulkOrderImportDto>
export type BulkCostImportResultDto = z.infer<typeof BulkCostImportResultDto>
export type BulkRevenueResultDto = z.infer<typeof BulkRevenueResultDto>
export type StatusFlowCheckDto = z.infer<typeof StatusFlowCheckDto>
export type OrderDetailDto = z.infer<typeof OrderDetailDto>
export type MyCommissionDto = z.infer<typeof MyCommissionDto>
export type OpsBoardDto = z.infer<typeof OpsBoardDto>
export type OpsReportDataDto = z.infer<typeof OpsReportDataDto>
export type OrderExportDataDto = z.infer<typeof OrderExportDataDto>
export type OnBehalfRowDto = z.infer<typeof OnBehalfRowDto>
export type ApStatementDto = z.infer<typeof ApStatementDto>
export type ApStatementRefLookupDto = z.infer<typeof ApStatementRefLookupDto>
export type JobFeePendingDto = z.infer<typeof JobFeePendingDto>
export type JobFeeStatementDto = z.infer<typeof JobFeeStatementDto>
export type ApStatementPrintRowDto = z.infer<typeof ApStatementPrintRowDto>
export type ApStatementPrintDto = z.infer<typeof ApStatementPrintDto>
export type ApInvoiceDto = z.infer<typeof ApInvoiceDto>
export type ApInvoiceLineDto = z.infer<typeof ApInvoiceLineDto>
export type ApCostDto = z.infer<typeof ApCostDto>
export type ApDebtRowDto = z.infer<typeof ApDebtRowDto>
export type ApDebtGroupDto = z.infer<typeof ApDebtGroupDto>
export type ApDebtReportDto = z.infer<typeof ApDebtReportDto>
export type PaymentRequestDto = z.infer<typeof PaymentRequestDto>
export type PayStepsDto = z.infer<typeof PayStepsDto>
export type PostingInputDto = z.infer<typeof PostingInputDto>
export type RequestDocDto = z.infer<typeof RequestDocDto>
export type PaymentDocDto = z.infer<typeof PaymentDocDto>
export type PaymentDocLineDto = z.infer<typeof PaymentDocLineDto>
export type RequestLoanPayDto = z.infer<typeof RequestLoanPayDto>
export type RequestStatementDto = z.infer<typeof RequestStatementDto>
export type QuoteTemplateDto = z.infer<typeof QuoteTemplateDto>
export type CreditExtensionDto = z.infer<typeof CreditExtensionDto>
export type VendorPostingDto = z.infer<typeof VendorPostingDto>
export type OutboxLetterDto = z.infer<typeof OutboxLetterDto>
export type OnBehalfRecvDto = z.infer<typeof OnBehalfRecvDto>
export type SyncLogDto = z.infer<typeof SyncLogDto>
export type CashPlanDto = z.infer<typeof CashPlanDto>
export type PayableRequestDto = z.infer<typeof PayableRequestDto>
export type VendorLegDto = z.infer<typeof VendorLegDto>
export type VendorPlannedJobDto = z.infer<typeof VendorPlannedJobDto>
export type VendorMeDto = z.infer<typeof VendorMeDto>
export type VendorApInvoiceDto = z.infer<typeof VendorApInvoiceDto>
export type CompanySettingsDto = z.infer<typeof CompanySettingsDto>
export type AppUserDto = z.infer<typeof AppUserDto>
export type PasswordStateDto = z.infer<typeof PasswordStateDto>
export type UserTempPasswordDto = z.infer<typeof UserTempPasswordDto>
export type VendorUserDto = z.infer<typeof VendorUserDto>
export type VendorTempPasswordDto = z.infer<typeof VendorTempPasswordDto>
export type VendorUserIssuedDto = z.infer<typeof VendorUserIssuedDto>
export type UserRefDto = z.infer<typeof UserRefDto>
export type ApprovalRuleDto = z.infer<typeof ApprovalRuleDto>
export type OpsItemDto = z.infer<typeof OpsItemDto>
export type PriceVersionDto = z.infer<typeof PriceVersionDto>
export type DecidePriceVersionResultDto = z.infer<typeof DecidePriceVersionResultDto>
export type ManagedPriceVersionDto = z.infer<typeof ManagedPriceVersionDto>
export type PriceVersionAuditDto = z.infer<typeof PriceVersionAuditDto>
export type DeletePriceVersionsResultDto = z.infer<typeof DeletePriceVersionsResultDto>
export type PricePeriodRowDto = z.infer<typeof PricePeriodRowDto>
export type PriceStatsDto = z.infer<typeof PriceStatsDto>
export type LedgerLockDto = z.infer<typeof LedgerLockDto>
export type LedgerLockHistoryDto = z.infer<typeof LedgerLockHistoryDto>
export type ReconItemDto = z.infer<typeof ReconItemDto>
export type ReconRequestDto = z.infer<typeof ReconRequestDto>
export type ReconSummaryDto = z.infer<typeof ReconSummaryDto>
export type AmisDocDto = z.infer<typeof AmisDocDto>
export type ConfirmLoadedResultDto = z.infer<typeof ConfirmLoadedResultDto>
export type AmisDocLineDto = z.infer<typeof AmisDocLineDto>
export type AmisDocDetailDto = z.infer<typeof AmisDocDetailDto>
export type DebtOffsetDto = z.infer<typeof DebtOffsetDto>
export type ApInvoiceRefDto = z.infer<typeof ApInvoiceRefDto>
export type ClosedLoopRowDto = z.infer<typeof ClosedLoopRowDto>
export type ClosedLoopReportDto = z.infer<typeof ClosedLoopReportDto>
export type DunningLineDto = z.infer<typeof DunningLineDto>
export type DunningLetterDto = z.infer<typeof DunningLetterDto>
export type SaleMailDto = z.infer<typeof SaleMailDto>
export type Ledger131Dto = z.infer<typeof Ledger131Dto>
export type OpeningImportResultDto = z.infer<typeof OpeningImportResultDto>
export type FillDueResultDto = z.infer<typeof FillDueResultDto>
export type MoneyAccountDto = z.infer<typeof MoneyAccountDto>
export type BankTxnDto = z.infer<typeof BankTxnDto>
export type AccountSummaryDto = z.infer<typeof AccountSummaryDto>
export type OpeningIssueDto = z.infer<typeof OpeningIssueDto>
export type MisaSheetDto = z.infer<typeof MisaSheetDto>
export type ExcelMarkResultDto = z.infer<typeof ExcelMarkResultDto>
export type MisaObjectItemDto = z.infer<typeof MisaObjectItemDto>
export type MisaObjectsDto = z.infer<typeof MisaObjectsDto>
export type AuditIssueDto = z.infer<typeof AuditIssueDto>
export type LedgerReportDto = z.infer<typeof LedgerReportDto>
export type PairIssueDto = z.infer<typeof PairIssueDto>
export type PairReportDto = z.infer<typeof PairReportDto>
export type HealAppliedDto = z.infer<typeof HealAppliedDto>
export type LedgerSummaryDto = z.infer<typeof LedgerSummaryDto>
export type CashFlowReportDto = z.infer<typeof CashFlowReportDto>
export type ForecastLineDto = z.infer<typeof ForecastLineDto>
export type ForecastDto = z.infer<typeof ForecastDto>
export type TrackPodDto = z.infer<typeof TrackPodDto>
export type TrackViewDto = z.infer<typeof TrackViewDto>
export type AllocPriceDto = z.infer<typeof AllocPriceDto>
export type AllocBoardDto = z.infer<typeof AllocBoardDto>
export type AllocUsedOrderDto = z.infer<typeof AllocUsedOrderDto>
export type AllocClashDto = z.infer<typeof AllocClashDto>
export type FuelPriceDto = z.infer<typeof FuelPriceDto>
export type BctcPeriodDto = z.infer<typeof BctcPeriodDto>
export type BctcTargetDto = z.infer<typeof BctcTargetDto>
export type BctcYearDto = z.infer<typeof BctcYearDto>
export type DimTargetDto = z.infer<typeof DimTargetDto>
export type PrevQtyDto = z.infer<typeof PrevQtyDto>
export type ActualOverrideDto = z.infer<typeof ActualOverrideDto>
export type KhthGridDto = z.infer<typeof KhthGridDto>
export type KhthReportDto = z.infer<typeof KhthReportDto>
export type PriceCatDto = z.infer<typeof PriceCatDto>
export type PriceCatRowDto = z.infer<typeof PriceCatRowDto>
export type RateBatchDto = z.infer<typeof RateBatchDto>
export type RateHitDto = z.infer<typeof RateHitDto>
export type CostRateDto = z.infer<typeof CostRateDto>
export type RailPriceBoardDto = z.infer<typeof RailPriceBoardDto>
export const RateSheetResultDto = z.object({ updated: z.number(), added: z.number(), duplicates: z.number() })
export const NewCostPeriodResultDto = z.object({
  added: z.number(), duplicates: z.number(),
  exActive: z.number(), exAdded: z.number(), exFallback: z.number(),
  exSkipped: z.array(z.object({ station: z.string(), ward: z.string().nullable(), addr: z.string() })),
})
export type NewCostPeriodResultDto = z.infer<typeof NewCostPeriodResultDto>
export type RateSheetResultDto = z.infer<typeof RateSheetResultDto>
export type ImportRateResultDto = z.infer<typeof ImportRateResultDto>
export type RailStatementDto = z.infer<typeof RailStatementDto>
export type RailReconRowDto = z.infer<typeof RailReconRowDto>
export type RailReconSummaryDto = z.infer<typeof RailReconSummaryDto>
export type RailReconResultDto = z.infer<typeof RailReconResultDto>
export type RailApplyResultDto = z.infer<typeof RailApplyResultDto>
export type StatsOverviewDto = z.infer<typeof StatsOverviewDto>
export type StatsMonthDto = z.infer<typeof StatsMonthDto>
export type StatsSaleDto = z.infer<typeof StatsSaleDto>
export type StatsVendorDto = z.infer<typeof StatsVendorDto>
export type StatsRouteDto = z.infer<typeof StatsRouteDto>
export type StatsPackDto = z.infer<typeof StatsPackDto>
export type StatsCustomerDto = z.infer<typeof StatsCustomerDto>
export type StatsOrderDto = z.infer<typeof StatsOrderDto>
export type StatsUnbilledDto = z.infer<typeof StatsUnbilledDto>
export type PriceAuditRowDto = z.infer<typeof PriceAuditRowDto>
export type PriceAuditDto = z.infer<typeof PriceAuditDto>
export type ListPriceRowDto = z.infer<typeof ListPriceRowDto>
export type StatsReportDto = z.infer<typeof StatsReportDto>
export type DashOverviewDto = z.infer<typeof DashOverviewDto>
export type DashYearChartDto = z.infer<typeof DashYearChartDto>
export type CommissionOrderRowDto = z.infer<typeof CommissionOrderRowDto>
export type CommissionBranchRowDto = z.infer<typeof CommissionBranchRowDto>
export type CommissionSaleRowDto = z.infer<typeof CommissionSaleRowDto>
export type CommissionPayoutBasisDto = z.infer<typeof CommissionPayoutBasisDto>
export type CommissionMonthCalcDto = z.infer<typeof CommissionMonthCalcDto>
export type CommissionMonthPayoutDto = z.infer<typeof CommissionMonthPayoutDto>
export type CommissionSaleBriefDto = z.infer<typeof CommissionSaleBriefDto>
export type CommissionMonthStateDto = z.infer<typeof CommissionMonthStateDto>
export type CommissionOrderOverrideDto = z.infer<typeof CommissionOrderOverrideDto>
export type CommissionDryRunDto = z.infer<typeof CommissionDryRunDto>
export type CommissionPolicyVersionDto = z.infer<typeof CommissionPolicyVersionDto>
export type CommissionPolicyStateDto = z.infer<typeof CommissionPolicyStateDto>
export type CommissionPhatBoardDto = z.infer<typeof CommissionPhatBoardDto>
export type CommissionPhatResultDto = z.infer<typeof CommissionPhatResultDto>
export type CommissionDiscrepancyDto = z.infer<typeof CommissionDiscrepancyDto>
export type CommissionReportDto = z.infer<typeof CommissionReportDto>
export type CommissionSnapshotFileDto = z.infer<typeof CommissionSnapshotFileDto>
export type CustPolicyRowDto = z.infer<typeof CustPolicyRowDto>
export type CustPolicyDto = z.infer<typeof CustPolicyDto>
export type CustPolicyMatchDto = z.infer<typeof CustPolicyMatchDto>
export type BidRequestDto = z.infer<typeof BidRequestDto>
export type BidOfferDto = z.infer<typeof BidOfferDto>
export type RankedOfferDto = z.infer<typeof RankedOfferDto>
export type BidSummaryDto = z.infer<typeof BidSummaryDto>
export type BidDetailDto = z.infer<typeof BidDetailDto>
export type MyBidDto = z.infer<typeof MyBidDto>
export type BiddingBoardDto = z.infer<typeof BiddingBoardDto>
export type OpenBiddingResultDto = z.infer<typeof OpenBiddingResultDto>
export type BidPickResultDto = z.infer<typeof BidPickResultDto>
export type ContainerDto = z.infer<typeof ContainerDto>
export type FleetRowDto = z.infer<typeof FleetRowDto>
export type FleetSummaryDto = z.infer<typeof FleetSummaryDto>
export type FleetDto = z.infer<typeof FleetDto>
export type ContOrderDto = z.infer<typeof ContOrderDto>
export type ContCatalogDto = z.infer<typeof ContCatalogDto>
export type FuelLotDto = z.infer<typeof FuelLotDto>
export type FuelStockDto = z.infer<typeof FuelStockDto>
export type FuelMoveDto = z.infer<typeof FuelMoveDto>
export type FuelMoveLotDto = z.infer<typeof FuelMoveLotDto>
export type FuelMoveResultDto = z.infer<typeof FuelMoveResultDto>
export type ContRepairDto = z.infer<typeof ContRepairDto>
export type RepairItemDto = z.infer<typeof RepairItemDto>
export type RelatedCostDto = z.infer<typeof RelatedCostDto>
export type ContMaterialDto = z.infer<typeof ContMaterialDto>
export type ContReportDto = z.infer<typeof ContReportDto>
export type ProfitMonthDto = z.infer<typeof ProfitMonthDto>
export type ProfitContainerDto = z.infer<typeof ProfitContainerDto>
export type ProfitSalaryLineDto = z.infer<typeof ProfitSalaryLineDto>
export type ProfitReportDto = z.infer<typeof ProfitReportDto>
export type ReeferJobDto = z.infer<typeof ReeferJobDto>
export type ReeferBoardRowDto = z.infer<typeof ReeferBoardRowDto>
export type FuelLogDto = z.infer<typeof FuelLogDto>
export type OrderPriceRowDto = z.infer<typeof OrderPriceRowDto>
export type OrderPriceLookupDto = z.infer<typeof OrderPriceLookupDto>
export type JobPayRequestDto = z.infer<typeof JobPayRequestDto>
export type OrderAllocLookupDto = z.infer<typeof OrderAllocLookupDto>
export type FuelTransferDto = z.infer<typeof FuelTransferDto>
export type FuelStockRowDto = z.infer<typeof FuelStockRowDto>
export type FuelStockReportDto = z.infer<typeof FuelStockReportDto>
export type FuelYearRowDto = z.infer<typeof FuelYearRowDto>
export type FuelYearReportDto = z.infer<typeof FuelYearReportDto>
export type FuelByJobRowDto = z.infer<typeof FuelByJobRowDto>
export type DeprAllocJobDto = z.infer<typeof DeprAllocJobDto>
export type FuelStockReminderDto = z.infer<typeof FuelStockReminderDto>
export type PlanPeriodDto = z.infer<typeof PlanPeriodDto>
export type PlanTargetDto = z.infer<typeof PlanTargetDto>
export type AchievementDto = z.infer<typeof AchievementDto>
export type VendorProgressDto = z.infer<typeof VendorProgressDto>
export type PeriodReportDto = z.infer<typeof PeriodReportDto>
export type MyPlanDto = z.infer<typeof MyPlanDto>
export type LegacyImportResultDto = z.infer<typeof LegacyImportResultDto>
export type ReserveRefsResultDto = z.infer<typeof ReserveRefsResultDto>
export type SetLegacyStatusResultDto = z.infer<typeof SetLegacyStatusResultDto>
export type OrderBoardRowDto = z.infer<typeof OrderBoardRowDto>
export type OrderBoardPageDto = z.infer<typeof OrderBoardPageDto>
export type OrderBoardSummaryDto = z.infer<typeof OrderBoardSummaryDto>
export type OrderBoardSelectAllDto = z.infer<typeof OrderBoardSelectAllDto>
export type OrderBoardExcelDto = z.infer<typeof OrderBoardExcelDto>
export type LatestOrdersDto = z.infer<typeof LatestOrdersDto>
export type OrderProvincesDto = z.infer<typeof OrderProvincesDto>
